// ───── consent.js ─────
/**
 * Consent banner controller (4.0.79).
 *
 * The markup ships hidden on every (cached) page; this decides whether to
 * show it, and writes the visitor's answer to the `orem_consent` cookie.
 * PHP reads that cookie on every dispatch — see Utilities::consent_signals().
 *
 * Cookie: `v1.<analytics>.<ads>.<unix-ts>`, 180 days.
 *
 * Region is decided in the browser (4.1.0) — timezone + language heuristic,
 * see regionHeuristic(). The 4.0.79 `/consent/state` REST call cost a full
 * WordPress bootstrap once per session for every visitor without a cookie,
 * and visitors outside the consent regions never get a cookie, so it fired
 * for essentially every Mexican session; that class of beacon is what took
 * production down on 2026-09-17. Over-inclusion (Turkey, Russia, Canary
 * Islands...) only shows a banner and defaults to denied — the safe direction.
 *
 * This class also owns the Consent Mode v2 wire signals (signals()), which
 * used to live in Utilities::consent_signals(): the dispatcher reads them
 * per event, and PHP keeps its own copy only for the server-side purchase.
 */
const OREM_CONSENT_CSS = `
/* Brandbook OREM26, paleta WEB (pp. 29-31) y tipografia digital (p. 26).
   Subtono Pearl White #FAF8F5 de fondo, Ink #2B2B2B para el cuerpo en
   Prompt, Playfair Display en Burgundy #5A1420 para el titulo, y el
   boton principal en Subtono Burgundy #6B1D33 -> hover #862B45, 6px,
   .2s, que nunca invierte a fondo claro. */
.orem-consent{position:fixed;left:0;right:0;bottom:0;z-index:2147483000;
    background:#FAF8F5;border-top:1px solid #E8D5C4;
    box-shadow:0 -6px 24px rgba(43,43,43,.10);
    font-family:"Prompt",Helvetica,Arial,sans-serif;color:#2B2B2B}
.orem-consent[hidden]{display:none!important}
.orem-consent__panel{max-width:1100px;margin:0 auto;padding:22px 24px}
.orem-consent__title{margin:0 0 8px;font-family:"Playfair Display",Georgia,"Times New Roman",serif;
    font-weight:500;font-size:1.25rem;line-height:1.3;color:#5A1420}
.orem-consent__text{margin:0;font-weight:300;font-size:.9375rem;line-height:1.55;max-width:70ch}
.orem-consent__link{color:#6B1D33;text-decoration:underline}
.orem-consent__prefs{margin:16px 0 0;display:grid;gap:10px}
/* Cualquier regla que fije display tiene que traer su guardia [hidden]:
   display:grid le gana al [hidden]{display:none} del navegador y el panel
   se quedaba SIEMPRE abierto (visto en produccion en 4.0.81). */
.orem-consent__prefs[hidden]{display:none}
.orem-consent__row{display:flex;gap:10px;align-items:flex-start;font-size:.9rem;
    font-weight:300;line-height:1.5;cursor:pointer}
.orem-consent__row--locked{cursor:default;opacity:.7}
.orem-consent__row[hidden]{display:none}
.orem-consent__row strong{font-weight:500}
.orem-consent__row input{margin-top:.25rem;flex:0 0 auto;accent-color:#6B1D33}
.orem-consent__actions{margin-top:18px;display:flex;flex-wrap:wrap;gap:10px;align-items:center;
    justify-content:flex-end}
.orem-consent__actions[hidden]{display:none}
/* Rechazar y Aceptar comparten tamano, tipografia y peso visual a
   proposito: el EDPB trata un "rechazar" mas discreto como patron
   oscuro. Es requisito legal, no gusto. */
.orem-consent__btn{font-family:inherit;font-weight:500;font-size:.9rem;line-height:1;
    padding:12px 20px;border-radius:6px;cursor:pointer;transition:background-color .2s,color .2s;
    border:1px solid #6B1D33}
/* El tema (Shoptimizer) pinta todo <button> con su propio color de texto
   (visto en produccion: texto casi negro sobre #6B1D33, contraste ~1.6:1).
   Por eso el selector lleva .orem-consent delante y color/fondo van con
   !important, tambien en :hover/:focus/:active/:visited. Contraste real:
   #FFFFFF sobre #6B1D33 = 11.6:1, sobre #862B45 = 9.2:1. */
.orem-consent .orem-consent__btn--primary,
.orem-consent .orem-consent__btn--primary:focus,
.orem-consent .orem-consent__btn--primary:active{background:#6B1D33!important;color:#FFFFFF!important;
    border-color:#6B1D33!important;text-shadow:none}
.orem-consent .orem-consent__btn--primary:hover{background:#862B45!important;color:#FFFFFF!important;
    border-color:#862B45!important}
.orem-consent .orem-consent__btn--secondary,
.orem-consent .orem-consent__btn--secondary:focus,
.orem-consent .orem-consent__btn--secondary:active{background:transparent!important;color:#6B1D33!important;
    border-color:#6B1D33!important}
.orem-consent .orem-consent__btn--secondary:hover{background:#F3E3D9!important;color:#5A1420!important}
.orem-consent__btn[hidden]{display:none}
.orem-consent__linkbtn{font-family:inherit;font-weight:400;font-size:.875rem;
    background:none;border:0;padding:6px 4px;color:#6B1D33;text-decoration:underline;cursor:pointer}
.orem-consent__linkbtn[hidden]{display:none}
.orem-consent__btn:focus-visible,.orem-consent__linkbtn:focus-visible{
    outline:2px solid #6B1D33;outline-offset:2px}
@media (max-width:640px){
    .orem-consent__panel{padding:18px 16px}
    .orem-consent__actions{justify-content:stretch}
    .orem-consent__btn{flex:1 1 45%;text-align:center}
    .orem-consent__linkbtn{flex:1 1 100%;text-align:center}
}
@media (prefers-reduced-motion:no-preference){
    .orem-consent{animation:orem-consent-in .2s ease-out}
    @keyframes orem-consent-in{from{transform:translateY(100%)}to{transform:none}}
}
`;

class OremConsent {
    COOKIE = 'orem_consent';
    TTL_DAYS = 180;

    // EU-27 + EEA + UK + CH — mirror of Utilities::CONSENT_REQUIRED_COUNTRIES.
    // Deliberately NOT a US list (CPRA & co. are opt-out regimes).
    static CONSENT_REQUIRED_COUNTRIES = [
        'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
        'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
        'IS', 'LI', 'NO',
        'GB', 'CH',
    ];

    constructor() {
        const init = () => this.init();
        if (document.readyState === 'complete' || document.readyState === 'interactive') init();
        else document.addEventListener('DOMContentLoaded', init);
    }


    /**
     * Inject the banner stylesheet (4.0.81).
     *
     * This used to be a `<style>` block printed by Consent.php. WP Rocket's
     * Remove-Unused-CSS gutted it in production: the banner ships `hidden`, so
     * RUCSS decided every rule was unused and served a zero-length tag
     * (verified live — styleTagLen 0 with #wpr-usedcss active). The banner
     * still appeared for a consent-required visitor, just completely unstyled
     * and sitting in the page flow instead of pinned to the viewport.
     *
     * Injecting from JS is immune to CSS optimizers, and costs nothing in
     * coverage: the banner is never shown without JS in the first place.
     * Keep this as the ONLY definition of the styles — a copy back in PHP
     * would drift and RUCSS would eat it again anyway.
     */
    injectStyles() {
        if (document.getElementById('orem-consent-css-js')) return;
        const el = document.createElement('style');
        el.id = 'orem-consent-css-js';
        el.textContent = OREM_CONSENT_CSS;
        (document.head || document.documentElement).appendChild(el);
    }

    init() {
        this.el = document.getElementById('orem-consent');
        if (!this.el) return;
        this.injectStyles();
        this.wireButtons();
        this.ensurePrefsLink();

        // Already answered → never show it again until the cookie expires.
        if (this.read()) return;

        if (this.region().region === 'consent_required') this.show();
    }

    /**
     * Where is this visitor, legally speaking — without asking a server.
     *
     * Timezone first, language only when there is no timezone (4.1.15):
     *   tz   — IANA timezone starts with `Europe/` or `Atlantic/` (Canaries,
     *          Azores, Madeira, Faroe, Reykjavik are all in scope anyway).
     *          Any other timezone → `row`, whatever the language says.
     *   lang — fallback when Intl gives no timezone: the region subtag of
     *          navigator.language (`es-ES`, `de-DE`, `en-GB`) is in the
     *          consent list. `es-MX`, `es-419` are not.
     * Until 4.1.15 either signal was enough, and phones in Mexico set to
     * "Español (España)" (`es-ES`, common on Android/iOS) got the banner — and
     * until they answered, ads consent was denied, so Meta/TikTok were off
     * for real Mexican shoppers. The language says what the device speaks,
     * not where it is; the timezone is where it is.
     * Nothing usable at all → `unknown`, which
     * behaves like `row` (granted) exactly as the server did, and is labelled
     * so its share stays measurable in GA4 (`consent_region_source`).
     *
     * A VPN or a mis-set clock can misplace someone; the failure mode is a
     * banner that was not required, never a missing one where it was.
     */
    region() {
        if (this._region) return this._region;
        let region = 'unknown';
        let source = 'none';
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
            if (tz) {
                region = 'row';
                source = 'tz';
                if (tz.indexOf('Europe/') === 0 || tz.indexOf('Atlantic/') === 0) {
                    this._region = { region: 'consent_required', source: 'tz' };
                    return this._region;
                }
            }
        } catch (e) {}
        if (source === 'tz') {
            this._region = { region: region, source: source };
            return this._region;
        }
        try {
            const langs = [].concat(navigator.languages || [], navigator.language || []);
            for (let i = 0; i < langs.length; i++) {
                const m = String(langs[i] || '').match(/^[a-z]{2,3}-([A-Za-z]{2})(?:-|$)/);
                if (!m) continue;
                if (region === 'unknown') { region = 'row'; source = 'lang'; }
                if (OremConsent.CONSENT_REQUIRED_COUNTRIES.indexOf(m[1].toUpperCase()) !== -1) {
                    this._region = { region: 'consent_required', source: 'lang' };
                    return this._region;
                }
            }
        } catch (e) {}
        this._region = { region: region, source: source };
        return this._region;
    }

    /**
     * Consent Mode v2 wire signals for the current visitor — port of
     * Utilities::consent_signals()/consent_wire() (4.0.77/4.0.79).
     *
     * An explicit banner answer wins EVERYWHERE; region only decides the
     * default for someone who has not answered. `dma` tracks the REGION, not
     * the answer. `gcd` letter semantics are inherited (see the PHP docblock):
     * `t` = granted, `r` = denied, shape preserved.
     */
    signals() {
        const r = this.region();
        const requiresConsent = r.region === 'consent_required';
        const choice = this.read();

        let analytics, ads, basis;
        if (choice) {
            analytics = !!choice.analytics; ads = !!choice.ads; basis = 'explicit';
        } else if (requiresConsent) {
            analytics = false; ads = false; basis = 'default_denied';
        } else {
            analytics = true; ads = true; basis = 'default_granted';
        }

        const a = ads ? 't' : 'r';
        const n = analytics ? 't' : 'r';
        return {
            gcs: 'G1' + (ads ? '1' : '0') + (analytics ? '1' : '0'),
            gcd: '13' + a + '3' + a + '3' + a + '3' + n + '5l1',
            npa: ads ? '0' : '1',
            dma: requiresConsent ? '1' : '0',
            region: r.region,
            region_source: r.source,
            basis: basis,
            ads: ads,
            analytics: analytics,
        };
    }

    /**
     * Guarantee the withdrawal link exists (third and last route).
     *
     * PHP offers two: auto-append to a footer nav menu, and the
     * `[orem_consent_prefs]` shortcode. Neither fires on orem.com.mx — the
     * footer there is `<footer class="copyright">` built from Gutenberg block
     * widgets, so there is no nav menu to filter and nobody placed the
     * shortcode. Verified against the live page after the 4.0.79 deploy.
     *
     * Rather than hard-code a Shoptimizer selector into a plugin that is meant
     * to be reusable, fall back to the DOM: if no link rendered, append one to
     * the last <footer> on the page.
     *
     * A JS-injected link is invisible with JS disabled, and that is coherent
     * rather than a gap: with JS off the banner cannot show either, so nobody
     * can have granted anything, and the region default (denied in the EU)
     * already applies. There is no consent to withdraw.
     */
    ensurePrefsLink() {
        if (document.querySelector('.orem-consent-prefs-link')) return;

        const footers = document.querySelectorAll('footer');
        const target = footers.length ? footers[footers.length - 1] : null;
        if (!target) return;

        const wrap = document.createElement('p');
        wrap.className = 'orem-consent-prefs-fallback';
        wrap.style.cssText = 'margin:.75em 0 0;text-align:center;font-size:.8125rem';

        const a = document.createElement('a');
        a.href = '#';
        a.className = 'orem-consent-prefs-link';
        a.textContent = 'Preferencias de cookies';
        a.style.color = 'inherit';
        a.addEventListener('click', (e) => { e.preventDefault(); this.reopen(); });

        wrap.appendChild(a);
        target.appendChild(wrap);
    }

    wireButtons() {
        this.el.addEventListener('click', (evt) => {
            const btn = evt.target.closest('[data-orem-consent]');
            if (!btn) return;
            const action = btn.getAttribute('data-orem-consent');

            if (action === 'accept') return this.save(true, true);
            if (action === 'reject') return this.save(false, false);
            if (action === 'prefs')  return this.openPrefs(btn);
            if (action === 'save') {
                const a = this.el.querySelector('#orem-consent-analytics');
                const m = this.el.querySelector('#orem-consent-ads');
                return this.save(!!(a && a.checked), !!(m && m.checked));
            }
        });
    }

    openPrefs(prefsBtn) {
        const prefs = this.el.querySelector('#orem-consent-prefs');
        const save  = this.el.querySelector('[data-orem-consent="save"]');
        if (prefs) prefs.hidden = false;
        if (save)  save.hidden = false;
        if (prefsBtn) prefsBtn.hidden = true;
    }

    show() {
        this.el.hidden = false;
    }

    /**
     * Persist the answer and hide the banner.
     *
     * Nothing is re-dispatched for the current pageview: events already sent
     * under the pre-answer default stay as they were. Under a denied default
     * those carried no PII and no Meta/TikTok call, so accepting later only
     * ever widens what the NEXT event may carry — never retroactively leaks
     * something that was already withheld.
     */
    save(analytics, ads) {
        const value = 'v1.' + (analytics ? '1' : '0') + '.' + (ads ? '1' : '0') + '.' + Math.floor(Date.now() / 1000);
        this.write(value);
        this.el.hidden = true;

        try {
            document.dispatchEvent(new CustomEvent('orem_consent_change', {
                detail: { analytics: !!analytics, ads: !!ads },
            }));
        } catch (e) {}
    }

    read() {
        const m = document.cookie.match(/(?:^|;\s*)orem_consent=([^;]*)/);
        if (!m) return null;
        const parts = decodeURIComponent(m[1]).split('.');
        if (parts.length < 3 || parts[0] !== 'v1') return null;
        return { analytics: parts[1] === '1', ads: parts[2] === '1' };
    }

    write(value) {
        const exp = new Date(Date.now() + this.TTL_DAYS * 864e5).toUTCString();
        const secure = location.protocol === 'https:' ? '; Secure' : '';
        // Root path + Lax so the beacon and the normal page load both send it.
        document.cookie = this.COOKIE + '=' + encodeURIComponent(value) +
            '; expires=' + exp + '; path=/; SameSite=Lax' + secure;
    }

    /** Re-open the banner — for a "cookie settings" link in the footer. */
    reopen() {
        if (!this.el) return;
        const current = this.read();
        const a = this.el.querySelector('#orem-consent-analytics');
        const m = this.el.querySelector('#orem-consent-ads');
        if (a) a.checked = !!(current && current.analytics);
        if (m) m.checked = !!(current && current.ads);
        this.openPrefs(this.el.querySelector('[data-orem-consent="prefs"]'));
        this.show();
    }
}

const oremConsent = new OremConsent();

// Public handle so a footer link can call `oremConsent.reopen()` — required
// in the EU: consent has to be as easy to withdraw as it was to give.
window.oremConsent = oremConsent;

;
// ───── utm-mapping.js ─────
/**
 * UTM source/medium canonicalization (4.0.39).
 *
 * JS mirror of includes/UtmMapping.php — keep both in sync when adding
 * new sources or aliases.
 *
 * Exposes globals consumed by app.js / app.min.js:
 *   deriveSourceMediumFromReferer(refHost) → { s, m }
 *   oremNormalizeUtmPair(src, med)         → { s, m }
 *
 * Loaded BEFORE app.min.js via wp_enqueue_script dependency chain.
 */

// Source aliases — collapse drift to canonical names.
var OREM_SOURCE_ALIASES = {
    'fb': 'meta',
    'facebook': 'meta',
    'meta-sitelink': 'meta',
    'th': 'meta',                 // Threads
    'an': 'meta',                 // Audience Network
    'msg': 'meta',                // Messenger
    'igshopping': 'ig',
    'instagram': 'ig',
    'sendinblue': 'brevo',
    'marcoantonioregil': 'influencer_175',
    // 4.0.48 — Kueski Pay "Search Merchant" → kueski (default medium=merchant_listing)
    'kueski_search_merchant': 'kueski',
    // 4.0.45 — AI search hostnames collapse to short canonical names so
    // OREM_SOURCE_DEFAULT_MEDIUM kicks in with ai_search.
    'chatgpt.com': 'chatgpt',
    'claude.ai': 'claude',
    'perplexity.ai': 'perplexity',
    'gemini.google.com': 'gemini',
    'copilot.microsoft.com': 'copilot'
};

// Medium aliases — paid variants → 'paid'; ai variants → 'ai_search'.
var OREM_MEDIUM_ALIASES = {
    'paid_social': 'paid',
    'paid_search': 'paid',
    'cpc': 'paid',
    'ppc': 'paid',
    'banner': 'paid',
    'display': 'paid',
    'social_paid': 'paid',
    'ai': 'ai_search',
    'llm': 'ai_search',
    'messagebird': 'whatsapp'
};

// Source → default medium when medium absent/(not set)/referral and source
// has a strong default (search engines, AI, payment portals, email/whatsapp).
var OREM_SOURCE_DEFAULT_MEDIUM = {
    'google': 'organic',
    'bing': 'organic',
    'yahoo': 'organic',
    'duckduckgo': 'organic',
    'ecosia': 'organic',
    'brave': 'organic',
    'chatgpt': 'ai_search',
    'claude': 'ai_search',
    'perplexity': 'ai_search',
    'gemini': 'ai_search',
    'copilot': 'ai_search',
    'brevo': 'email',
    'messagebird': 'whatsapp',
    'kueski': 'merchant_listing',
    'mercadopago': 'merchant_listing'
};

// Sources that get medium=`social` when medium is empty.
var OREM_SOCIAL_SOURCES = ['ig', 'meta', 'tiktok', 'youtube', 'pinterest', 'telegram', 'twitter'];

// Sources that override an explicit `referral` medium with their default
// (e.g. google/referral should still become google/organic).
var OREM_REFERRAL_OVERRIDE_SOURCES = [
    'google', 'bing', 'yahoo', 'duckduckgo', 'ecosia', 'brave',
    'chatgpt', 'claude', 'perplexity', 'gemini', 'copilot',
    'brevo', 'messagebird', 'kueski', 'mercadopago'
];

// Referrer hostname → [source, medium]. Mirrors REFERER_HOST_MAP in PHP.
var OREM_REFERER_HOST_MAP = {
    'instagram.com':       { s: 'ig',     m: 'social' },
    'l.instagram.com':     { s: 'ig',     m: 'social' },
    'm.instagram.com':     { s: 'ig',     m: 'social' },
    'facebook.com':        { s: 'meta',   m: 'social' },
    'm.facebook.com':      { s: 'meta',   m: 'social' },
    'l.facebook.com':      { s: 'meta',   m: 'social' },
    'lm.facebook.com':     { s: 'meta',   m: 'social' },
    'fb.com':              { s: 'meta',   m: 'social' },
    'youtube.com':         { s: 'youtube',m: 'social' },
    'm.youtube.com':       { s: 'youtube',m: 'social' },
    'tiktok.com':          { s: 'tiktok', m: 'social' },
    'pinterest.com':       { s: 'pinterest', m: 'social' },
    'mx.pinterest.com':    { s: 'pinterest', m: 'social' },
    't.co':                { s: 'twitter',m: 'social' },
    'twitter.com':         { s: 'twitter',m: 'social' },
    'x.com':               { s: 'twitter',m: 'social' },
    'web.telegram.org':    { s: 'telegram', m: 'social' },
    'google.com':          { s: 'google', m: 'organic' },
    'google.com.mx':       { s: 'google', m: 'organic' },
    'bing.com':            { s: 'bing',   m: 'organic' },
    'ntp.msn.com':         { s: 'bing',   m: 'organic' },
    'yahoo.com':           { s: 'yahoo',  m: 'organic' },
    'mx.search.yahoo.com': { s: 'yahoo',  m: 'organic' },
    'duckduckgo.com':      { s: 'duckduckgo', m: 'organic' },
    'search.brave.com':    { s: 'brave',  m: 'organic' },
    'ecosia.org':          { s: 'ecosia', m: 'organic' },
    'chatgpt.com':         { s: 'chatgpt',    m: 'ai_search' },
    'claude.ai':           { s: 'claude',     m: 'ai_search' },
    'perplexity.ai':       { s: 'perplexity', m: 'ai_search' },
    'mail.google.com':     { s: 'brevo',  m: 'email' },
    'outlook.live.com':    { s: 'brevo',  m: 'email' },
    'marcoantonioregil.com': { s: 'influencer_175', m: 'influencer' }
};

/**
 * Map a referrer hostname to canonical { source, medium }. Returns
 * { s: '(direct)', m: '(none)' } when no referrer; falls through to raw
 * host with medium=referral when no rule matches.
 */
function deriveSourceMediumFromReferer(refHost) {
    if (!refHost) return { s: '(direct)', m: '(none)' };
    var h = String(refHost).toLowerCase().replace(/^www\./, '');
    if (OREM_REFERER_HOST_MAP[h]) return OREM_REFERER_HOST_MAP[h];
    // Wildcard: any *.google.* → google/organic (e.g. google.de, google.es).
    if (/^(?:.+\.)?google\.[a-z.]+$/.test(h)) return { s: 'google', m: 'organic' };
    // Wildcard: Brevo link tracker subdomains.
    if (/\.sendib[mt]\d*\.com$/.test(h)) return { s: 'brevo', m: 'email' };
    return { s: h, m: 'referral' };
}

/**
 * Canonicalize utm_source/utm_medium captured from URL params. Returns
 * { s, m } both lowercased + capped at 50 chars. Defensive against
 * malformed URLs that put '&' or '=' inside utm_source.
 */
function oremNormalizeUtmPair(src, med) {
    src = String(src || '').toLowerCase().trim();
    med = String(med || '').toLowerCase().trim();
    src = src.replace(/[&=].*/, '');
    med = med.replace(/[&=].*/, '');

    if (OREM_SOURCE_ALIASES[src]) src = OREM_SOURCE_ALIASES[src];
    if (OREM_MEDIUM_ALIASES[med]) med = OREM_MEDIUM_ALIASES[med];

    if (med === '' || med === '(not set)' || med === 'referral') {
        if (OREM_SOURCE_DEFAULT_MEDIUM[src]) {
            if (med !== 'referral'
                || OREM_REFERRAL_OVERRIDE_SOURCES.indexOf(src) !== -1) {
                med = OREM_SOURCE_DEFAULT_MEDIUM[src];
            }
        }
    }

    if (med === '' || med === '(not set)') {
        if (OREM_SOCIAL_SOURCES.indexOf(src) !== -1) {
            med = 'social';
        } else if (/^influencer_/.test(src) || src === 'shopmy') {
            med = 'influencer';
        }
    }

    if (src.length > 50) src = src.substring(0, 50);
    if (med.length > 50) med = med.substring(0, 50);
    return { s: src, m: med };
}

;
// ───── app.js ─────
async function sha256(value) {
    const encoded = new TextEncoder().encode(value.trim().toLowerCase());
    const buffer = await crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(buffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

EventTypes = {
    PAGE_VIEW: "PageView",
    VIEW_CONTENT: "ViewContent",
    VIEW_ITEM_LIST: "ViewItemList",
    LEAD: "Lead",
    ADD_TO_CART: "AddToCart",
    INITIATE_CHECKOUT: "InitiateCheckout",
    PURCHASE: "Purchase",
    ADD_PAYMENT_INFO: "AddPaymentInfo",
    // 4.0.64 — GA4 standard checkout funnel step (was never instrumented;
    // begin_checkout → add_shipping_info → add_payment_info → purchase).
    ADD_SHIPPING_INFO: "AddShippingInfo",
    CONTACT: "Contact",
    SEARCH: "Search",
    CHECKOUT_PROGRESS: "CheckoutProgress",
    ENGAGEMENT: "Engagement",
    // 4.0.49 — Blocks checkout funnel observability.
    // High-volume per-session events kept separate from CHECKOUT_PROGRESS
    // so milestone funnels don't get diluted by field-level noise.
    CHECKOUT_FIELD_BLUR: "CheckoutFieldBlur",
    CHECKOUT_VALIDATION_ERROR: "CheckoutValidationError",
    // 4.0.61 — Google Places address-autocomplete signals (emitted by the
    // addons autocomplete via document.body CustomEvents; see checkout.js
    // attachAddressAutocompleteEvents). USED = adoption; CORRECTED = the
    // customer edited a field the autocomplete had filled (quality signal —
    // surfaces where Google's MX data is wrong, at scale). Params are coarse
    // only (field/cp/estado/source) — NEVER the street address (PII).
    ADDRESS_AUTOCOMPLETE_USED: "AddressAutocompleteUsed",
    ADDRESS_AUTOCOMPLETE_CORRECTED: "AddressAutocompleteCorrected",
};
// WEB-72 (2026-09) — menu reestructura; WEB-7x (2026-09-20) fix. GA4-only,
// no ad-platform value; measures whether the nav overhaul actually moves
// traffic into the categories that sell (see Tools/menus/README.md).
// menu_click, menu_open y linkbar_click NO viven en EventTypes/eventMapping
// a propósito — WEB-72 los puso ahí originalmente
// (`pixelManager.sendEvent(EventTypes.MENU_CLICK, ...)`) y el evento NUNCA
// llegó a GA4 desde que se desplegó: `sendEvent` resuelve el nombre real
// vía `getEventName(eventName, GOOGLE)`, que busca la entrada en
// `eventMapping`; sin ella devuelve `null` y `sendEvent` corta ANTES de
// despachar (con solo un `console.error` silencioso). Cero eventos en GA4
// desde 2026-08-22, verificado — 0 filas en 30 días, mientras
// `us_mode_switch` (mismo pipeline, misma semana) sí llegó porque usa esta
// misma vía. `pixelManager.sendCustom(name, params)` es el camino correcto
// para un evento sin equivalente en Meta/TikTok: salta el mapa por
// completo (mismo mecanismo que expone `window.oremTrack`, y el que ya usa
// `scroll_depth` desde 4.0.69) y aun así pasa por todos los gates de
// `sendEvent` (bot UA, admin, `/wp-admin/`, dedupe).

class TrackingPixel {


    constructor() {
        if (this.constructor === TrackingPixel) {
            throw new Error("Abstract class 'TrackingPixel' cannot be instantiated directly.");
        }

        this.Platforms = {
            FACEBOOK: "facebook",
            TIKTOK: "tiktok",
            GOOGLE: "google",
        };

        // Event mapping table
        this.eventMapping = {
            [EventTypes.PAGE_VIEW]: {
                //[this.Platforms.FACEBOOK]: "PageView",
                //[this.Platforms.TIKTOK]: "Pageview",
                [this.Platforms.GOOGLE]: "page_view" //Tracked automatically by Gtag
            },
            [EventTypes.VIEW_CONTENT]: {
                //[this.Platforms.FACEBOOK]: "ViewContent",
                //[this.Platforms.TIKTOK]: "ViewContent",
                [this.Platforms.GOOGLE]: "view_item"
            },
            // view_item_list is GA4-only on purpose. Meta/TikTok have no
            // standard equivalent and would treat it as an unrecognized
            // custom event. Per-platform routing for events lives in sGTM
            // Trigger Exceptions, NOT in `ep.activate_tag_*` (those are
            // reserved for owner/QA test traffic gating).
            [EventTypes.VIEW_ITEM_LIST]: {
                [this.Platforms.GOOGLE]: "view_item_list"
            },
            [EventTypes.LEAD]: {
                //[this.Platforms.FACEBOOK]: "Lead",
                //[this.Platforms.TIKTOK]: "Submit Form",
                [this.Platforms.GOOGLE]: "generate_lead"
            },
            [EventTypes.ADD_TO_CART]: {
                //[this.Platforms.FACEBOOK]: "AddToCart",
                //[this.Platforms.TIKTOK]: "AddToCart",
                [this.Platforms.GOOGLE]: "add_to_cart"
            },
            [EventTypes.INITIATE_CHECKOUT]: {
                //[this.Platforms.FACEBOOK]: "InitiateCheckout",
                //[this.Platforms.TIKTOK]: "InitiateCheckout",
                [this.Platforms.GOOGLE]: "begin_checkout"
            },
            [EventTypes.PURCHASE]: {
                //[this.Platforms.FACEBOOK]: "Purchase",
                //[this.Platforms.TIKTOK]: "CompletePayment",
                [this.Platforms.GOOGLE]: "purchase"
            },
            [EventTypes.ADD_PAYMENT_INFO]: {
                //[this.Platforms.FACEBOOK]: "AddPaymentInfo",
                //[this.Platforms.TIKTOK]: "AddPaymentInfo",
                [this.Platforms.GOOGLE]: "add_payment_info"
            },
            // 4.0.64 — GA4-only (Meta/TikTok have no standard shipping-info
            // event worth routing; per-platform routing lives in sGTM anyway).
            [EventTypes.ADD_SHIPPING_INFO]: {
                [this.Platforms.GOOGLE]: "add_shipping_info"
            },
            [EventTypes.CONTACT]: {
                //[this.Platforms.FACEBOOK]: "Contact",
                //[this.Platforms.TIKTOK]: "Contact",
                // 4.0.71 — was "working_lead": not a lead, it's a click on the
                // customer-service WhatsApp button (trackContact() below);
                // the misleading name caused a real misdiagnosis in the
                // log-46 audit. sGTM Trigger Exceptions match on this literal
                // event name to fire Meta "Contact" / TikTok's tag — the
                // container rename must land together with this deploy (see
                // plan B2) or those tags silently stop firing for this event
                // until it does.
                [this.Platforms.GOOGLE]: "whatsapp_support_click"
            },
            [EventTypes.SEARCH]: {
                //[this.Platforms.FACEBOOK]: "Search",
                //[this.Platforms.TIKTOK]: "Search",
                [this.Platforms.GOOGLE]: "search"
            },
            [EventTypes.CHECKOUT_PROGRESS]: {
                //[this.Platforms.FACEBOOK]: "NA",
                //[this.Platforms.TIKTOK]: "NA",
                [this.Platforms.GOOGLE]: "checkout_progress"
            },
            [EventTypes.ENGAGEMENT]: {
                [this.Platforms.GOOGLE]: "user_engagement"
            },
            // 4.0.49 — Blocks checkout funnel additions.
            [EventTypes.CHECKOUT_FIELD_BLUR]: {
                [this.Platforms.GOOGLE]: "checkout_field_blur"
            },
            [EventTypes.CHECKOUT_VALIDATION_ERROR]: {
                [this.Platforms.GOOGLE]: "checkout_validation_error"
            },
            // 4.0.61 — address autocomplete signals (GA4-only).
            [EventTypes.ADDRESS_AUTOCOMPLETE_USED]: {
                [this.Platforms.GOOGLE]: "address_autocomplete_used"
            },
            [EventTypes.ADDRESS_AUTOCOMPLETE_CORRECTED]: {
                [this.Platforms.GOOGLE]: "address_autocomplete_corrected"
            }
        };

    }

    // Abstract method to inject the tracking pixel
    injectPixel() {
        //throw new Error("Method 'injectPixel()' must be implemented.");
    }

    // Helper method to normalize data (can be overridden if needed)
    normalizeData(data) {
        return data;
    }

    // Helper method to convert the 
    getEventName(eventName, platform) {
        // Check if the eventName exists in the mapping table
        if (this.eventMapping[eventName] && this.eventMapping[eventName][platform]) {
            return this.eventMapping[eventName][platform];
        } else {
            console.error(`Invalid event name "${eventName}" or platform "${platform}"`);
            return null; // Return null if event name or platform is invalid
        }
    }

    getExternalId() {
        let userId = readCookie('orem_logged_in_user_id');
        return typeof userId != 'undefined' && userId != '' ? userId : null;
    }

    isDataEmpty(data) {
        return Object.keys(data).length === 0;
    }
}

// ServerSideDispatcher (4.1.0): the browser posts sGTM *event data* straight
// to the Stape Data Client on our own subdomain — no WordPress hop.
//
//   sendBeacon → https://miseventos.orem.com.mx/api/v2/s (Data Client, exact
//   path) → runContainer(eventData) → GA4 / Meta CAPI / TikTok / Google Ads.
//
// Why the WP REST hop died: every /wp-json/orem/v1/track/* beacon paid the
// full WP + WooCommerce + 30-plugin bootstrap (~90 queries, ~300 MB) and a
// pageview fires 3-5 of them; on 2026-09-17 that alone saturated Kinsta's 8
// PHP-FPM workers (18-24 s per request, 502 on add-to-cart). The path is
// deliberately NOT /g/collect and the host is first-party, so adblock lists
// have nothing to match — that was the only property of the REST hop worth
// keeping, and this keeps it.
//
// The Data Client copies JSON keys 1:1 into event data, so this class emits
// the keys the GA4 client used to derive (event_name, client_id,
// ga_session_id, x-ga-*, orem_user_data, activate_tag_*, ...). Every tag,
// variable and transformation in container GTM-P2TFK2VN reads those names —
// nothing there had to change except the Client Name filter on 5 triggers.
// Keep GA4_KEYS in sync with what sGTM Preview shows for a /g/collect hit.
class ServerSideDispatcher extends TrackingPixel {
    constructor(config) {
        super();
        const pv = (typeof orem_pixel_vars !== 'undefined' && orem_pixel_vars) ? orem_pixel_vars : {};
        this.pv = pv;
        this.endpoint = pv.collectEndpoint
            || ((pv.serverSideContainerUrl || 'https://miseventos.orem.com.mx') + '/api/v2/s');
        this.userData = null;
        this.userDataReady = this.getGoogleUserData().then(data => {
            this.userData = data;
        });
    }

    async getGoogleUserData() {
        let userData = readCookie('orem_logged_in_user_data');
        if (userData && userData != null && userData != '') {
            return JSON.parse(userData);
        }

        let email = oremUserData.getMarketingEmail();
        let phone = oremUserData.getMarketingPhone();
        let firstName = oremUserData.getMarketingFirstName();
        let lastName = oremUserData.getMarketingLastName();

        // 4.0.55 — cache the sha256 hashes keyed on the raw email/phone so
        // refreshUserData() (called per-event) doesn't re-hash identical
        // values. Guests enter email/phone DURING checkout (after page
        // load), so we must re-read each event — but hashing is async and
        // we don't want to pay it on every dispatch when nothing changed.
        userData = {};
        if (email && email != '') {
            if (this._emCache && this._emCache.raw === email) {
                userData.sha256_email_address = this._emCache.hash;
            } else {
                const h = await sha256(email);
                this._emCache = { raw: email, hash: h };
                userData.sha256_email_address = h;
            }
        }
        if (phone && phone != '') {
            if (this._phCache && this._phCache.raw === phone) {
                userData.sha256_phone_number = this._phCache.hash;
            } else {
                const h = await sha256(phone);
                this._phCache = { raw: phone, hash: h };
                userData.sha256_phone_number = h;
            }
            // 4.0.57 — also expose the RAW phone so the Meta CAPI tag's
            // "Orem - x-fb-ud-ph" sGTM variable (which reads
            // orem_user_data.phone_number, strips the '+', and re-hashes
            // per Meta's digits-only spec) gets a CORRECTLY-formatted hash.
            // Our sha256_phone_number is hashed WITH the '+' (right for
            // Google/TikTok E.164, wrong for Meta which wants no '+'), and
            // the sGTM variable can't un-hash. Consistent with the raw
            // first_name/last_name already in user_data. Stays out of GA4
            // via the existing "Exclude orem_user_data" transformation.
            userData.phone_number = phone;
        }

        if ((firstName && firstName != '') || (lastName && lastName != '')) {
            userData.address = {};
            if (firstName && firstName != '') userData.address.first_name = firstName;
            if (lastName && lastName != '') userData.address.last_name = lastName;
        }

        return this.isDataEmpty(userData) ? null : userData;
    }

    /**
     * 4.0.55 — refresh this.userData from the current marketing cookies.
     * The constructor caches userData once at page load, but on the WC
     * Blocks checkout guests type their email/phone AFTER load (captured
     * into mkt_email/mkt_phone by BlocksCheckoutEventTracker's customer-
     * data subscription). Without this refresh, every client-side
     * checkout event (add_payment_info, begin_checkout, etc.) shipped
     * with the page-load snapshot (empty for guests) → FB/TikTok never
     * got phone/email for the guest majority. Re-reads cookies + hashes
     * (cached by raw value, so it's cheap when nothing changed).
     */
    async refreshUserData() {
        // Logged-in users have a stable orem_logged_in_user_data cookie —
        // no need to re-derive; keep the cached snapshot.
        const loggedIn = readCookie('orem_logged_in_user_data');
        if (loggedIn && loggedIn != '') return;
        try {
            const fresh = await this.getGoogleUserData();
            if (fresh && fresh != null) this.userData = fresh;
        } catch (e) { /* never break tracking on user-data refresh */ }
    }

    // Cookie read that tolerates both raw and percent-encoded values (PHP
    // wrote some of these with rawurlencode, the JS bootstrap wrote others
    // plain).
    cookieValue(name) {
        const raw = readCookie(name);
        if (!raw) return '';
        try { return decodeURIComponent(raw); } catch (e) { return String(raw); }
    }

    firstCookie(names) {
        for (let i = 0; i < names.length; i++) {
            const v = this.cookieValue(names[i]);
            if (v) return v;
        }
        return '';
    }

    isTestMode() {
        return this.pv.testMode === '1' || this.pv.isNonProductionHost === '1';
    }

    /**
     * Acquisition params from the first-party cookies (mirror of the wire
     * fallbacks in the retired Core::sendEventToGTM, 4.0.41/43/45): canonical
     * source/medium, and the whole UTM set dropped when it is admin noise
     * (Customizer template library) or an unresolved Meta {{macro}}.
     */
    attributionParams() {
        const out = {};
        const utm = {};
        ['source', 'medium', 'campaign', 'content', 'term', 'id'].forEach((k) => {
            const v = this.cookieValue('orem_utm_' + k);
            if (v) utm[k] = v;
        });
        if (utm.source || utm.medium) {
            const n = oremNormalizeUtmPair(utm.source || '', utm.medium || '');
            if (n.s) utm.source = n.s;
            if (n.m) utm.medium = n.m;
        }
        const src = String(utm.source || '').toLowerCase();
        const med = String(utm.medium || '').toLowerCase();
        const adminUtm = (src === 'template-library' || med === 'wp-dash');
        const macroUtm = ['source', 'medium', 'campaign', 'content', 'term'].some((k) => {
            const v = String(utm[k] || '');
            return v.indexOf('{{') !== -1 || v.indexOf('}}') !== -1;
        });
        if (!adminUtm && !macroUtm) {
            Object.keys(utm).forEach((k) => { out['utm_' + k] = utm[k]; });
        }

        const clickIds = {
            gclid:   ['orem-gclid', 'orem_gclid'],
            msclkid: ['orem-msclkid', 'orem_msclkid'],
            fbclid:  ['orem_fbclid'],
            ttclid:  ['orem_ttclid', 'ttclid'],
            wbraid:  ['orem_wbraid'],
            gbraid:  ['orem_gbraid'],
        };
        Object.keys(clickIds).forEach((k) => {
            const v = this.firstCookie(clickIds[k]);
            if (v) out[k] = v;
        });

        // Host-only cookies do NOT travel to miseventos.orem.com.mx (the
        // retired PHP hop forwarded the whole Cookie header, which is how the
        // Meta CAPI tag used to see _fbp/_fbc). The official Meta tag reads
        // x-fb-ck-fbp / x-fb-ck-fbc from event data before falling back to
        // cookies; TikTok's reads ttp/ttclid.
        const fbp = this.cookieValue('_fbp');
        const fbc = this.cookieValue('_fbc');
        const ttp = this.cookieValue('_ttp');
        if (fbp) out['x-fb-ck-fbp'] = fbp;
        if (fbc) out['x-fb-ck-fbc'] = fbc;
        if (ttp) out.ttp = ttp;
        return out;
    }

    /**
     * Consent Mode v2 signals + the platform kill-switch (4.0.79 rules,
     * moved from PHP). consent.js owns the region heuristic and the cookie;
     * without it we fall open to granted exactly like the server did on
     * `unknown`, and label it so the rate stays measurable.
     */
    consentSignals() {
        try {
            if (window.oremConsent && typeof window.oremConsent.signals === 'function') {
                return window.oremConsent.signals();
            }
        } catch (e) { /* fall through */ }
        return {
            gcs: 'G111', gcd: '13t3t3t3t5l1', npa: '0', dma: '0',
            region: 'unknown', region_source: 'none', basis: 'default_granted',
            ads: true, analytics: true,
        };
    }

    /**
     * ep.activate_tag_<platform> — test-mode / consent kill-switch only.
     * Per-event platform ROUTING lives in the sGTM Trigger Exceptions, not
     * here (the 4.0.x rule stands). Production: all '1'. Marketing denied:
     * Meta + TikTok '0' (Consent Mode cannot cover server-to-server PII
     * POSTs). Test mode: Meta/TikTok '1' iff a test inbox code exists,
     * Google Ads '0' (no test inbox), GA4 '1' + _dbg on the dev stream.
     */
    activationParams(consent) {
        const out = {};
        const codes = (this.pv.testEventCodes && typeof this.pv.testEventCodes === 'object') ? this.pv.testEventCodes : {};
        const activate = { meta: '1', tiktok: '1', gads: '1', ga4: '1' };
        if (!consent.ads) {
            activate.meta = '0';
            activate.tiktok = '0';
        }
        if (this.isTestMode()) {
            activate.meta   = codes.meta   ? '1' : '0';
            activate.tiktok = codes.tiktok ? '1' : '0';
            activate.gads   = '0';
            activate.ga4    = '1';
            out['x-ga-mp2-_dbg'] = '1';
            out.test_mode   = '1';
            out.test_reason = this.pv.testMode === '1' ? 'orem_test_cookie' : 'non_production_host';
            if (codes.meta)   out.test_event_code_meta   = String(codes.meta);
            if (codes.tiktok) out.test_event_code_tiktok = String(codes.tiktok);
        }
        Object.keys(activate).forEach((p) => { out['activate_tag_' + p] = activate[p]; });
        return out;
    }

    /**
     * GA4 wire-protocol session counters (4.0.11/4.0.42/4.0.44) — unchanged
     * math, new key names: what used to ride as _s/_ss/sct/seg/tfd on the
     * /g/collect query string is emitted as the event-data keys the GA4
     * client derives from them.
     */
    sessionParams() {
        const out = {};
        try {
            ensureFreshSession();
            const cid = this.cookieValue('orem-ga-cid');
            const sid = this.cookieValue('orem-ga-sid');
            if (cid) out.client_id = cid;
            else out.cid_source = 'absent';
            // 4.1.19 — cid minted here, not by the head bootstrap (measurable
            // once cid_source is a GA4 dimension).
            if (cid && oremCidReminted) out.cid_source = 'js_remint';
            if (sid) {
                out.ga_session_id = sid;

                // ga_session_number — increments when sid changes; lives in
                // localStorage so it survives across sessions.
                let sct = parseInt(window.localStorage.getItem('orem-ga-sct') || '0', 10);
                if (isNaN(sct) || sct < 0) sct = 0;
                const lastSid = window.localStorage.getItem('orem-ga-last-sid');
                if (sid !== lastSid) {
                    sct += 1;
                    window.localStorage.setItem('orem-ga-sct', String(sct));
                    window.localStorage.setItem('orem-ga-last-sid', sid);
                }
                out.ga_session_number = sct;

                // Hit sequence within the session — cross-tab (localStorage,
                // 4.0.44) so _ss=1 fires exactly once per real session.
                const OREM_SEQ_PREFIX = 'orem-ga-hit-seq:';
                const seqKey = OREM_SEQ_PREFIX + sid;
                for (let i = window.localStorage.length - 1; i >= 0; i--) {
                    const k = window.localStorage.key(i);
                    if (k && k.indexOf(OREM_SEQ_PREFIX) === 0 && k !== seqKey) {
                        window.localStorage.removeItem(k);
                    }
                }
                let seq = parseInt(window.localStorage.getItem(seqKey) || '0', 10);
                if (isNaN(seq) || seq < 0) seq = 0;
                seq += 1;
                window.localStorage.setItem(seqKey, String(seq));
                out['x-ga-request_count'] = seq;
                if (seq === 1) out['x-ga-mp2-_ss'] = '1';
            }

            // 4.1.13 — first_visit. Nothing sent `_fv` since 4.0.0 dropped
            // gtag, so GA4 never generated first_visit and "New users" sat at
            // ~0. The bootstrap flags a freshly MINTED cid; the first hit that
            // actually goes out carries `_fv=1` and spends the flag. Keyed on
            // the cid mint on purpose, NOT on orem-ga-sct==1: localStorage can
            // outlive a dead cookie and would hide genuinely new cids.
            if (this.takeFirstVisitFlag()) out['x-ga-mp2-_fv'] = '1';

            // Engaged-session sticky bit (threshold from OREM_ENGAGED_SESSION_MS).
            out['x-ga-mp2-seg'] = (oremTotalEngagementSnapshot() >= OREM_SEG_THRESHOLD_MS) ? '1' : '0';

            if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
                out['x-ga-tfd'] = Math.max(0, Math.round(performance.now()));
            }
        } catch (e) { /* never break tracking on counter math */ }
        return out;
    }

    // Reads and clears the one-shot flag set by print_browser_id_bootstrap
    // (sessionStorage, or a host-only cookie where sessionStorage throws).
    takeFirstVisitFlag() {
        let pending = false;
        try {
            if (window.sessionStorage.getItem('orem-ga-fv-pending') === '1') {
                pending = true;
                window.sessionStorage.removeItem('orem-ga-fv-pending');
            }
        } catch (e) { /* sessionStorage unavailable — cookie path below */ }
        if (this.cookieValue('orem-ga-fv-pending') === '1') {
            pending = true;
            const secure = location.protocol === 'https:' ? '; Secure' : '';
            document.cookie = 'orem-ga-fv-pending=; Max-Age=0; Path=/; SameSite=Lax' + secure;
        }
        return pending;
    }

    async sendEvent(eventName, data, eventId, opts = null) {
        await this.userDataReady;
        // 4.0.55 — refresh user data so late-entered guest email/phone
        // (captured into mkt_* cookies during the Blocks checkout) is
        // included on this event. No-op for logged-in users.
        await this.refreshUserData();
        // 4.0.63 — opts.rawName lets window.oremTrack push a custom event name
        // straight through (skips the EventTypes→GA4 mapping AND the
        // item-required gate, which only apply to the predefined ecommerce
        // events).
        const googleEventName = (opts && opts.rawName)
            ? opts.rawName
            : this.getEventName(eventName, this.Platforms.GOOGLE);
        if (!googleEventName || googleEventName === 'NA') return;

        // 4.0.11 — gate item-required events when items[] is empty/missing.
        // GA4 silently accepts these, but they pollute reports with
        // 0-value transactions and corrupt the conversion funnel.
        const itemRequired = {
            view_item: true,
            view_item_list: true,
            add_to_cart: true,
            begin_checkout: true,
            add_shipping_info: true,
            add_payment_info: true,
            purchase: true,
        };
        if (itemRequired[googleEventName]) {
            const items = data && Array.isArray(data.items) ? data.items : null;
            if (!items || items.length === 0) {
                console.warn('orem-tracker: skipping ' + googleEventName + ' (no items)');
                return;
            }
        }

        // 4.1.20 — put wiped ids back BEFORE anything reads them (consent,
        // session counters, attribution). See oremIdGuard().
        const idRestored = oremIdGuard();
        const consent = this.consentSignals();
        const isTest = this.isTestMode();
        const tid = (isTest && this.pv.ga4MeasurementIdDev) ? this.pv.ga4MeasurementIdDev : this.pv.ga4MeasurementId;

        // ───── event data (sGTM common schema + GA4-client derived keys) ─────
        const ev = {
            event_name: googleEventName,
            event_id: String(eventId),
            'x-ga-measurement_id': tid,
            'x-ga-protocol_version': '2',
            'x-ga-mp2-_ee': '1',
            page_location: location.href,
            page_referrer: document.referrer || '',
            page_title: (document.title || '').substring(0, 200),
            language: (navigator.language || '').substring(0, 16),
        };
        if (typeof screen !== 'undefined' && screen.width && screen.height) {
            ev.screen_resolution = screen.width + 'x' + screen.height;
        }

        Object.assign(ev, this.sessionParams());
        Object.assign(ev, this.attributionParams());
        Object.assign(ev, this.activationParams(consent));
        // Diagnostic (4.1.20): how many keys this dispatch restored after a
        // storage wipe. Absent on normal hits. Remove once the IG rate is known.
        if (idRestored) ev.id_restored = idRestored;

        ev['x-ga-gcs'] = consent.gcs;
        ev['x-ga-gcd'] = consent.gcd;
        ev['x-ga-npa'] = consent.npa;
        ev['x-ga-dma'] = consent.dma;
        ev['x-ga-dma_cps'] = 'syphamo';
        ev.consent_region = consent.region;
        ev.consent_region_source = consent.region_source || 'none';
        ev.consent_basis = consent.basis;

        const externalId = this.getExternalId();
        if (externalId && externalId != null) ev.user_id = externalId;

        // User properties (logged-in only — localized once per page, the
        // page cache is bypassed for logged-in users anyway). Replaces the
        // WC_Customer lookup the PHP hop did per event.
        if (this.pv.customerStatus) {
            ev['x-ga-mp2-user_properties'] = {
                customer_status: String(this.pv.customerStatus),
                lifetime_orders: String(this.pv.lifetimeOrders || '0'),
            };
        }

        // 4.0.79 — no marketing consent, no PII on the wire. orem_user_data
        // feeds the sGTM x-fb-ud-* variables and the "Add user_data"
        // transformation (which is what builds GA4 Enhanced Conversions
        // server-side); with ad_storage denied there is no lawful basis to
        // ship it and Consent Mode does not cover a payload we hand over.
        //
        // 4.1.4 — orem_user_data MUST be a JSON string (4.1.0 regression,
        // log 59/60: 0/136 Meta events with em/ph, 0/135 TikTok with email
        // since 2026-09-18; only the GA4-client purchase kept them). Through
        // the GA4 client it was always the string `ep.orem_user_data=<json>`,
        // and container GTM-P2TFK2VN is built on that: every "Add x-fb-ud-*"
        // and "Add user_data" transformation is conditioned on
        // `orem_user_data contiene {` (a STRING match), and the Orem - x-fb-ud-*
        // / Orem - Get Google User Data templates JSON.parse() it. Handed an
        // object, none of them run and Meta/TikTok/Google Ads get no match data.
        if (consent.ads) {
            const ud = (opts && opts.userData) ? opts.userData : this.userData;
            if (ud) ev.orem_user_data = JSON.stringify(ud);
        }

        // Event params: items[] as-is (already GA4 item shape), scalars 1:1
        // (the GA4 client stripped the ep. prefix; there is none to strip).
        const params = data || {};
        Object.keys(params).forEach((k) => {
            const v = params[k];
            if (k === 'items') {
                if (Array.isArray(v)) ev.items = v;
                return;
            }
            if (k === 'engagement_time_msec' && v !== '' && !isNaN(Number(v))) {
                ev.engagement_time_msec = Math.round(Number(v));
                return;
            }
            if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
                if (typeof ev[k] === 'undefined') ev[k] = v;
            }
        });

        if (isTest) {
            console.log('[orem-test] dispatch ' + googleEventName, { endpoint: this.endpoint, eventData: ev });
        }

        this.transmit(JSON.stringify(ev));
        console.log('send event server-side: ' + googleEventName);
    }

    /**
     * text/plain on purpose: it is a CORS-simple request, so a cross-origin
     * beacon to miseventos.orem.com.mx needs no preflight. The Data Client
     * JSON.parses any body that is not form-urlencoded. sendBeacon survives
     * navigation; keepalive fetch is the fallback.
     */
    transmit(body) {
        let sent = false;
        try {
            if (navigator.sendBeacon) {
                sent = navigator.sendBeacon(this.endpoint, new Blob([body], { type: 'text/plain' }));
            }
        } catch (e) { /* fall through to fetch */ }
        if (sent) return;
        try {
            fetch(this.endpoint, {
                method: 'POST',
                mode: 'cors',
                credentials: 'include',
                headers: { 'Content-Type': 'text/plain' },
                body: body,
                keepalive: true
            }).catch(function () { /* swallow — never break UX on tracking */ });
        } catch (e) { /* swallow */ }
    }
}



// 4.0.76 (WEB-28) — JS-side filter registry. Mirrors the PHP contract core
// already uses to stay out of business decisions: Core.php does
// `apply_filters('orem_core/track/identify/should_block', ...)` and addons is
// who decides (woocommerce-orem-addons/includes/lead-gate.php). Same idea here:
// core owns WHEN a tracking event would fire; anything that needs domain
// knowledge to VETO it (is this person already a subscriber? is this campaign
// excluded?) is registered by addons and core never learns why.
//
// Contract (addons side):
//   window.oremCoreFilters['track/lead/should_block'] = function (blocked, ctx) {
//       // ctx = { formName, leadData, meta }   → return true to suppress
//   };
//
// Synchronous on purpose: the verdict must already be in hand (it rides along
// in the form's own AJAX response), because making trackLead async would
// reorder the setMarketingData cookie write that sendEvent's dispatch depends
// on. No filter registered ⇒ behaviour identical to before this version.
try {
    if (typeof window !== 'undefined') {
        window.oremCoreFilters = window.oremCoreFilters || {};
    }
} catch (e) { /* non-browser context */ }

function applyOremFilter(name, initial, ctx) {
    try {
        const fn = (typeof window !== 'undefined' && window.oremCoreFilters)
            ? window.oremCoreFilters[name]
            : null;
        if (typeof fn !== 'function') return initial;
        return !!fn(initial, ctx);
    } catch (e) {
        return initial; // a broken filter must never suppress tracking
    }
}

// Same registry, object-valued: lets addons ENRICH an event's params with
// something only it can know, without core having to understand it. Used for
// `lead_check`, whose values come from a Brevo lookup core must stay ignorant
// of. A filter that returns anything but an object is ignored.
function applyOremValueFilter(name, initial, ctx) {
    try {
        const fn = (typeof window !== 'undefined' && window.oremCoreFilters)
            ? window.oremCoreFilters[name]
            : null;
        if (typeof fn !== 'function') return initial;
        const out = fn(initial, ctx);
        return (out && typeof out === 'object') ? out : initial;
    } catch (e) {
        return initial;
    }
}

// 4.0.71 — custom-event names (window.oremTrack) that should ALSO fire the
// standard multi-platform lead event, so a new lead-capture surface (quiz,
// and whatever comes next) reaches Meta/TikTok/Ads through the already-wired
// EventTypes.LEAD route without opening sGTM again. Value = the lead_source
// prefix. See PixelManager.sendCustom.
const CUSTOM_LEAD_MIRROR = {
    quiz_lead: 'quiz',
};

// Master Pixel Manager
class PixelManager {
    constructor(pixlelsConfig, googleConfig) {
        this.pixels = [];
        this.googlePixel = null;
        this.initPixels(pixlelsConfig);
        if (googleConfig) this.initGooglePixel(googleConfig);

        //console.log('pixels config')
        //console.log(this.pixels);
    }

    initPixels(config) {
        if (config.meta) this.pixels.push(new MetaPixel(config.meta));
        if (config.tiktok) this.pixels.push(new TikTokPixel(config.tiktok));
    }

    initGooglePixel(config) {
        // 4.0.0: Google class replaced with ServerSideDispatcher.
        // Config object kept for forward compat (in case we want to thread
        // measurementId or other settings through later).
        // 4.0.63 — keep a named ref so sendCustom() (the window.oremTrack
        // public API) can dispatch custom events straight to the GA4
        // server-side pipeline.
        this.googlePixel = new ServerSideDispatcher(config);
        this.pixels.push(this.googlePixel);
    }

    sendEvent(eventType, data, eventId = null, opts = null) {
        // 4.0.41 — bot UA gate. Stape's X-Device-Bot detection runs in
        // the sGTM container and a Trigger Exception drops the GA4
        // forward, but the request still consumes a Stape ingestion
        // slot. Filter at JS level so meta-externalads / HeadlessChrome /
        // search bot crawls never hit the wire. In log (5).csv these
        // accounted for ~10% of dispatch volume (47 from
        // meta-externalads alone — Meta's ad-checker IS executing JS
        // since 2024 and walks our pixelManager just like a real user).
        try {
            if (typeof navigator !== 'undefined' && navigator.userAgent) {
                if (/meta-externalads|facebookexternalhit|HeadlessChrome|PhantomJS|Googlebot|bingbot|YandexBot|DuckDuckBot|Twitterbot|LinkedInBot|Slackbot|TelegramBot|WhatsApp|Discordbot|Applebot|SemrushBot|AhrefsBot|MJ12bot|SEBot|DotBot|BLEXBot|PetalBot|MojeekBot|GPTBot|ClaudeBot|Bytespider|YisouSpider|crawler|spider|scraper|-Bot\/[\d\.]+/i.test(navigator.userAgent)) {
                    return;
                }
            }
        } catch (e) { /* never break tracking on UA inspection */ }

        // 4.1.20 — automation-driven browsers (Puppeteer/Playwright/Selenium
        // set navigator.webdriver). Log 65: an AWS crawler with a plain
        // "X11; Linux x86_64 Chrome" UA passed the regex above and Stape's
        // X-Device-Bot, and reached Meta and TikTok as a PageView. No real
        // visitor's browser sets this flag. Test mode is exempt so automated
        // QA with ?orem_test=1 keeps dispatching to the dev stream.
        try {
            if (typeof navigator !== 'undefined' && navigator.webdriver === true
                && !(typeof orem_pixel_vars !== 'undefined' && (orem_pixel_vars.testMode === '1' || orem_pixel_vars.isNonProductionHost === '1'))) {
                return;
            }
        } catch (e) { /* never break tracking */ }

        // 4.0.41 — admin context skip (path-based). Customizer's Template
        // Library opens preview iframes that fire page_view per nav with
        // `?utm_source=template-library&utm_medium=wp-dash`. These go to
        // the dev tid via skipDispatch but still hit Stape. Block the
        // class of /wp-admin/* contexts at the source. Logged-in admins
        // browsing the front-end (no template-library marker) keep going
        // through skipDispatch below so the existing console-only
        // behavior is preserved.
        try {
            if (typeof location !== 'undefined') {
                if (location.pathname && location.pathname.indexOf('/wp-admin/') === 0) return;
                if (location.search && location.search.indexOf('utm_source=template-library') !== -1) return;
            }
        } catch (e) { /* never break tracking */ }

        // 4.0.37 — admin / QA-tagged traffic bypasses dispatch entirely. Log
        // to console so the owner can see WHAT would have fired without
        // polluting analytics. orem-test=1 overrides skip (handled PHP-side
        // in skipDispatch flag, not here).
        if (typeof orem_pixel_vars !== 'undefined' && orem_pixel_vars.skipDispatch === '1') {
            if (typeof console !== 'undefined' && console.log) {
                console.log('[orem] %s NOT dispatched (admin/QA skip — set ?orem_test=1 to enable)', eventType, data);
            }
            return;
        }
        if (eventId === null) eventId = this.generateEventID();
        if (!data) data = {};

        // 4.0.41 — event_id dedup. bfcache restores, double-bound
        // listeners, and rapid back-forward navigation can dispatch the
        // same eventId twice within a single page lifetime (observed in
        // log (5).csv: page_view 1778271747419-79402-73516 fired twice
        // 1s apart, identical cid/sid). Set is page-lifetime — survives
        // long enough to swallow retries without growing unbounded.
        // Cap at 200 entries with LRU-ish trim (drop oldest 50) — a
        // typical session emits ~10 events, so 200 covers heavy QA
        // walkthroughs without leaking memory.
        if (!this._dispatchedIds) this._dispatchedIds = new Set();
        const idKey = String(eventId);
        if (this._dispatchedIds.has(idKey)) {
            if (typeof console !== 'undefined' && console.log) {
                console.log('[orem] %s dedup skip (eventId=%s)', eventType, idKey);
            }
            return;
        }
        this._dispatchedIds.add(idKey);
        if (this._dispatchedIds.size > 200) {
            this._dispatchedIds = new Set(Array.from(this._dispatchedIds).slice(50));
        }

        // 4.0.63 — custom events (window.oremTrack) dispatch ONLY through the
        // GA4 server pipeline with the raw event name; standard events keep
        // fanning out to every configured pixel.
        if (opts && opts.rawName) {
            if (this.googlePixel) this.googlePixel.sendEvent(eventType, data, eventId, opts);
        } else {
            this.pixels.forEach(pixel => pixel.sendEvent(eventType, data, eventId));
        }
    }

    generateEventID() {
        const eventId = Date.now() + '-' + Math.floor(Math.random() * 100000) + '-' + Math.floor(Math.random() * 100000);
        return eventId;
    }

    // 4.0.63 — public custom-event API (exposed as window.oremTrack). Lets
    // embedded widgets (e.g. the Elementor quiz) fire ARBITRARY GA4 event
    // names through the same server-side pipeline (bot/admin/test gating,
    // cid/session attribution, Enhanced Conversions) WITHOUT depending on the
    // bundle internals or the EventTypes enum. `lead` (optional {email,
    // firstName}) is hashed here with the same sha256 the rest of user_data
    // uses, so the caller never handles raw-PII hashing. Best-effort: never
    // throws to the caller.
    async sendCustom(name, params, lead) {
        if (!name) return;
        let userData = null;
        try {
            if (lead && lead.email) {
                userData = {};
                const h = await sha256(lead.email);
                if (h) userData.sha256_email_address = h;
                if (lead.firstName) {
                    userData.address = { first_name: String(lead.firstName).trim().toLowerCase() };
                }
            } else if (lead && (lead.sha256_email_address || lead.address)) {
                userData = lead; // already-shaped user_data object
            }
        } catch (e) { /* never break tracking on hashing */ }
        this.sendEvent(String(name), params || {}, null, { rawName: String(name), userData: userData });

        // 4.0.71 — mirror to the standard Lead event (Meta "Lead" / TikTok
        // "Submit Form" / GA4 generate_lead) for custom sources registered in
        // CUSTOM_LEAD_MIRROR. Fires only with an email present — no email,
        // no platform match value, not worth a second event. Fire-and-forget
        // BEFORE the synchronous setMarketingData call, same ordering
        // trackLead() in forms.js already relies on: sendEvent's async body
        // yields at its own first `await`, so the cookie write below lands
        // before refreshUserData() re-reads mkt_* cookies on its next tick.
        const mirrorPrefix = CUSTOM_LEAD_MIRROR[name];
        if (mirrorPrefix && lead && lead.email) {
            const sourceDetail = (params && params.quiz_name) ? params.quiz_name : name;
            // 4.0.76 (WEB-28) — the mirrored Lead event goes through the same
            // extension point as forms.js trackLead, so a future veto reaches
            // both. Today nothing registers it, so this is a pass-through.
            // NOTE: unlike the Elementor path, this one is NOT in the local
            // per-form ledger — the quiz HTML lives in Elementor, outside this
            // repo, and calls oremTrack in parallel with its own POST.
            // setMarketingData below runs either way.
            const suppressed = applyOremFilter('track/lead/should_block', false, {
                formName: mirrorPrefix + ':' + sourceDetail,
                leadData: lead,
                meta: { custom: name, params: params || {} },
            });
            try {
                if (!suppressed) {
                    // WEB-38 — the caller (quiz widget) already resolved
                    // `lead_check` server-side (Orem_Lead_Novelty, same
                    // predicate as the Elementor/quiz-lead doors) and put it
                    // straight in `params`; core just forwards the scalar
                    // through the generic ep.* passthrough, same as any other
                    // custom param. No new filter needed — the widget calls
                    // oremTrack itself, so it controls its own payload.
                    const mirroredParams = { lead_source: mirrorPrefix + ':' + sourceDetail };
                    if (params && typeof params.lead_check === 'string') {
                        mirroredParams.lead_check = params.lead_check;
                    }
                    this.sendEvent(EventTypes.LEAD, mirroredParams);
                }
            } catch (e) { /* never break tracking */ }
            try {
                oremUserData.setMarketingData(lead.email, null, lead.firstName || null, null);
            } catch (e) { /* never break tracking */ }
        }
    }
}

//###########################    Orem UserData    ###########################
//###########################################################################
class OremUserData {

    //#############################    Marketing    #############################
    //###########################################################################
    getMarketingFirstName() {
        let marketingFirstName = '';
        let loggedInFirstName = readCookie('mkt_first_name_logged_in');
        if (loggedInFirstName && loggedInFirstName != null && loggedInFirstName != '') {
            marketingFirstName = loggedInFirstName;
        } else {
            marketingFirstName = readCookie('mkt_firstName');
        }

        return marketingFirstName && marketingFirstName != '' ? marketingFirstName : '';
    }

    getMarketingLastName() {
        let marketingLastName = '';
        let loggedInLastName = readCookie('mkt_last_name_logged_in');
        if (loggedInLastName && loggedInLastName != null && loggedInLastName != '') {
            marketingLastName = loggedInLastName;
        } else {
            marketingLastName = readCookie('mkt_lastName');
        }

        return marketingLastName && marketingLastName != '' ? marketingLastName : '';
    }

    getMarketingEmail() {
        let marketingEmail = null;
        let loggedInEmail = readCookie('mkt_email_logged_in');
        if (loggedInEmail && loggedInEmail != null && loggedInEmail != '') {
            marketingEmail = loggedInEmail;

        } else {
            //No logged in user
            marketingEmail = readCookie('mkt_email');
            if (marketingEmail == null || !isValidEmail(marketingEmail)) {
                //No email in cookie
                const localStorageEmail = window.localStorage.getItem('mkt_email');
                if (localStorageEmail !== null && isValidEmail(localStorageEmail))
                    marketingEmail = localStorageEmail;
            }
        }


        logger.log('getMarketingEmail', marketingEmail);
        return marketingEmail;
    }

    getMarketingPhone() {
        let marketingPhone = null;

        //Check from cookies
        let loggedInPhone = readCookie('mkt_phone_number_logged_in');
        if (loggedInPhone && loggedInPhone != null && loggedInPhone != '') {
            marketingPhone = loggedInPhone;
        } else {
            marketingPhone = readCookie('mkt_phone');
            //Check from local storage
            if (marketingPhone !== null && marketingPhone.length <= 8) {
                const localStoragePhone = window.localStorage.getItem('mkt_phone');
                if (localStoragePhone !== null && localStoragePhone.length > 8)
                    marketingPhone = localStoragePhone;
            }
        }




        if (marketingPhone != null) {
            if (marketingPhone.length === 10) {
                marketingPhone = "+52" + marketingPhone;
            }
            if (!marketingPhone.startsWith("+")) marketingPhone = "+" + marketingPhone;

            logger.log('getMarketingPhone', marketingPhone);
        }

        if (marketingPhone !== null && marketingPhone.length < 11) marketingPhone = null;

        return marketingPhone;
    }

    setMarketingData(email, phone, firstName, lastName) {
        let phoneUpdated = false;
        let emailUpdated = false;

        if (!email && !phone && !firstName && !lastName) {
            logger.error('setMarketingData', 'all params are empty');
            return;
        }


        if (firstName != null && firstName != '' && firstName != this.getMarketingFirstName()) {
            setCookieOrem('mkt_firstName', firstName, 360);
        }

        if (lastName != null && lastName != '' && lastName != this.getMarketingLastName()) {
            setCookieOrem('mkt_lastName', lastName, 360);
        }

        if (phone != null && phone.length > 8 && phone != this.getMarketingPhone()) {
            setCookieOrem('mkt_phone', phone, 360);
            phoneUpdated = true;
        }

        if (email != null && email != '' && email != this.getMarketingEmail()) {
            logger.log('setMarketingData', 'email: ' + email + ', phone: ' + phone + ', firstName: ' + firstName + ', lastName: ' + lastName);

            //Email has changed
            setCookieOrem('mkt_email', email, 360);
            emailUpdated = true;
        }

        if (phoneUpdated || emailUpdated) {

            // ───── Server-side identify to Brevo (REST API) ─────
            //
            // Sends contact data to /wp-json/orem/v1/track/identify which
            // dispatches to Brevo POST /v3/contacts (updateEnabled=true) via
            // OREM_BREVO_API_KEY. Replaces the previous in-browser Brevo.identify()
            // call as the primary path — guarantees the contact is created/updated
            // even if the SDK never loads.
            //
            // Also sends the localStorage page_view buffer (history[]) so Brevo
            // backfills behavior history against the contact, replicating what
            // Brevo's SDK does internally when it merges anonymous browsing into
            // an identified contact via brevo_visitor_id.
            try {
                if (typeof window.fetch === 'function' && email && email !== '') {
                    var pvHistory = [];
                    try {
                        pvHistory = JSON.parse(window.localStorage.getItem('orem_pv_buf') || '[]');
                    } catch (e) { /* corrupt buffer: ignore */ }

                    // 4.0.37 — admin/test flags + skipDispatch local gate.
                    // Admin filling the form must not contaminate Brevo.
                    if (typeof orem_pixel_vars !== 'undefined' && orem_pixel_vars.skipDispatch === '1') {
                        if (typeof console !== 'undefined' && console.log) {
                            console.log('[orem] identify NOT dispatched (admin/QA skip — set ?orem_test=1 to enable)', { email: email });
                        }
                        return;
                    }
                    fetch('/wp-json/orem/v1/track/identify', {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            email: email || '',
                            phone: phone || '',
                            firstName: firstName || '',
                            lastName: lastName || '',
                            history: pvHistory,
                            _is_admin: (typeof orem_pixel_vars !== 'undefined' && orem_pixel_vars.isAdmin === '1') ? 1 : 0,
                            _test_mode: (typeof orem_pixel_vars !== 'undefined' && orem_pixel_vars.testMode === '1') ? 1 : 0
                        }),
                        keepalive: true
                    }).then(function () {
                        // Buffer flushed server-side — clear local copy so the
                        // next anonymous session starts fresh and we don't
                        // re-send the same events.
                        try { window.localStorage.removeItem('orem_pv_buf'); } catch (e) {}
                    }).catch(function () { /* swallow — never break UX on tracking */ });
                }
            } catch (error) {
                console.error(error);
            }

            // (Brevo.identify call removed — the Brevo SDK is no longer
            // loaded in the browser. All identify + product_viewed history
            // is dispatched server-side via the fetch above to
            // /wp-json/orem/v1/track/identify, which forwards to Brevo's
            // Contacts and Track APIs.)

            //Cookie required by Woocommerce plugin
            setCookieOrem('email_id', email, 360);

            if (typeof window.clarity == 'function' && email != '') window.clarity("identify", email);

        }
    }
}
//#########################    End Orem UserData    #########################
//###########################################################################

//#############################    Orem Log    ##############################
//###########################################################################

class OremLogger {

    displayInfo = false;
    displayWarning = true;
    displayError = true;

    log(callerName, text) {
        if (this.displayInfo) {
            if (typeof text === 'object') {
                console.log(callerName + ' - object:');
                console.log(text);
            } else {
                console.log(callerName + ' - ' + text);
            }
        }

    };

    warn(callerName, text) {
        if (this.displayWarning) {
            if (typeof text === 'object') {
                console.warn(callerName + ' - object:');
                console.warn(text);
            } else {
                console.warn(callerName + ' - ' + text);
            }
        }

    };

    error(callerName, text) {
        if (this.displayError) {
            if (typeof text === 'object') {
                console.error(callerName + ' - object:');
                console.error(text);
            } else {
                console.error(callerName + ' - ' + text);
            }
        }
    };
}

//###########################    End Orem Log    ############################
//###########################################################################
const oremUserData = new OremUserData();
const logger = new OremLogger();



function isValidEmail(email) {
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

function readCookie(name) {
    var nameEQ = name + "=";
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) == ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) == 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
    return null;
}

// 4.1.20 (written as 4.1.9 in PR #109, which never shipped) — identity guard
// (log 65). Instagram's in-app browser on iPhone
// wiped every cookie and localStorage key OUR SCRIPTS wrote, 15-45 s after
// landing, while the page kept running (4 of 8 IG visitors; IG = 31% of
// PageViews). Server-set cookies (_fbc from PHP, Stape's own) survived. Each
// wipe minted a new orem-ga-cid/sid/_fbp/_ttp on the next dispatch or page —
// one visitor counted as two users and two sessions, Meta/TikTok lost the
// browser-id thread, `cid_source=absent` on the hits in between, and the
// UTM/click-id/consent cookies went with them (a rejected-consent choice
// included).
//
// We keep the last known values in memory (the JS runtime survives the
// wipe) and put them back the moment we notice. Restoring is gated on a
// WIPE, never on one missing key: nothing in this codebase deletes
// orem-ga-cid (400 d, sliding since 4.1.12) or the localStorage `orem-ga-sct` counter, so either
// one vanishing mid-page means storage was cleared under us. Without that
// gate we would resurrect keys that the session/engagement code prunes on
// purpose. Not covered: IG sometimes loads the landing TWICE in separate
// storage (preload + the real open, 14 s apart in log 65) — two documents,
// two runtimes, nothing to carry the id across.
const OREM_ID_GUARD_COOKIES = (function () {
    // D400 = the bootstrap's ID_MAX_AGE (4.1.12 sliding expiry).
    var D90 = 90 * 86400, D1 = 86400, Y5 = 5 * 365 * 86400, D180 = 180 * 86400, D360 = 360 * 86400, D400 = 400 * 86400;
    var list = [
        // [name, scope, max-age seconds] — scope mirrors the writer:
        // 'domain' = Domain=.orem.com.mx (bootstrap setCookieWithDomain /
        // setOremCookieWithDomain), 'host' = host-only.
        ['orem-ga-cid', 'domain', D400],
        ['orem-ga-sid', 'domain', D1],
        ['orem-ga-last-activity', 'domain', D1],
        ['_fbp', 'host', D400],
        ['_ttp', 'domain', D400],
        ['_orem_id_v', 'host', D400],   // without it the bootstrap re-mints _fbp/_ttp
        ['_orem_ttp_d', 'domain', Y5],
        ['orem_consent', 'host', D180],
        ['orem_first_touch_ts', 'domain', D90],
        ['orem_journey', 'domain', D90],
        ['orem-gclid', 'domain', D90],
        ['orem-msclkid', 'domain', D90],
        ['orem_fbclid', 'domain', D90],
        ['orem_ttclid', 'domain', D90],
        ['orem_wbraid', 'domain', D90],
        ['orem_gbraid', 'domain', D90],
        ['mkt_email', 'host', D360],
        ['mkt_firstName', 'host', D360],
        ['mkt_lastName', 'host', D360],
        ['mkt_phone', 'host', D360],
    ];
    ['source', 'medium', 'campaign', 'content', 'term', 'id'].forEach(function (k) {
        list.push(['orem_utm_' + k, 'domain', D90]);
    });
    return list;
})();
const OREM_ID_GUARD_LS_PREFIXES = ['orem-ga-', 'mkt_', 'orem_lead_counted', 'orem_pv_buf'];
const oremIdMemo = { cookies: {}, ls: {} };

// Raw (still-encoded) value, so a restore writes back byte-identical text
// whatever encoding the original writer used.
function oremRawCookie(name) {
    var parts = document.cookie.split(';');
    for (var i = 0; i < parts.length; i++) {
        var p = parts[i].replace(/^\s+/, '');
        if (p.indexOf(name + '=') === 0) return p.substring(name.length + 1);
    }
    return null;
}

function oremWriteRawCookie(name, raw, scope, maxAge) {
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    var base = name + '=' + raw + '; Max-Age=' + maxAge + '; Path=/';
    if (scope === 'domain') {
        var h = location.hostname.split('.');
        var domain = location.hostname;
        if (h.length > 2) {
            var second = h[h.length - 2];
            var keep = (second === 'com' || second === 'co' || second === 'org' || second === 'net' || second === 'gob' || second === 'edu') ? 3 : 2;
            if (h.length > keep) domain = h.slice(-keep).join('.');
        }
        document.cookie = base + '; Domain=.' + domain + '; SameSite=Lax' + secure;
        if (oremRawCookie(name) === raw) return;
        // Public-suffix host (e.g. *.kinsta.cloud staging) refuses Domain= —
        // same fallback the bootstrap's setTtp() uses.
    }
    document.cookie = base + '; SameSite=Lax' + secure;
}

function oremLsKeys() {
    var out = {};
    try {
        for (var i = 0; i < window.localStorage.length; i++) {
            var k = window.localStorage.key(i);
            if (!k) continue;
            for (var j = 0; j < OREM_ID_GUARD_LS_PREFIXES.length; j++) {
                if (k.indexOf(OREM_ID_GUARD_LS_PREFIXES[j]) === 0) {
                    out[k] = window.localStorage.getItem(k);
                    break;
                }
            }
        }
    } catch (e) { /* localStorage blocked */ }
    return out;
}

// Returns how many keys it put back (0 = nothing was wiped). Cheap enough to
// run before every dispatch: one document.cookie split + a localStorage scan.
function oremIdGuard() {
    var restored = 0;
    try {
        var ls = oremLsKeys();
        var cookieWiped = !!oremIdMemo.cookies['orem-ga-cid'] && !oremRawCookie('orem-ga-cid');
        var lsWiped = !!oremIdMemo.ls['orem-ga-sct'] && ls['orem-ga-sct'] == null;
        if (cookieWiped || lsWiped) {
            OREM_ID_GUARD_COOKIES.forEach(function (c) {
                var v = oremIdMemo.cookies[c[0]];
                if (v && !oremRawCookie(c[0])) { oremWriteRawCookie(c[0], v, c[1], c[2]); restored++; }
            });
            Object.keys(oremIdMemo.ls).forEach(function (k) {
                if (ls[k] == null) {
                    try { window.localStorage.setItem(k, oremIdMemo.ls[k]); restored++; } catch (e) { /* blocked */ }
                }
            });
            ls = oremLsKeys();
        }
        // Not wiped (or just repaired): the memo becomes an exact snapshot, so
        // keys the app removed on purpose drop out of it.
        var cookies = {};
        OREM_ID_GUARD_COOKIES.forEach(function (c) {
            var v = oremRawCookie(c[0]);
            if (v) cookies[c[0]] = v;
        });
        oremIdMemo.cookies = cookies;
        oremIdMemo.ls = ls;
    } catch (e) { /* never break tracking */ }
    return restored;
}
// Snapshot at bundle eval (the wp_head bootstrap has already written the
// ids), then repair before the page goes away: a wipe followed by a tap that
// navigates without dispatching anything would otherwise reach the next
// page's bootstrap with empty storage and mint new ids there.
oremIdGuard();
try {
    window.addEventListener('pagehide', oremIdGuard);
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') oremIdGuard();
    });
} catch (e) { /* never break tracking */ }

// 4.0.71 — session rotation at DISPATCH time, not just pageload.
//
// print_browser_id_bootstrap (Core.php wp_head) only rotates orem-ga-sid
// on page load. A tab left open past the 30-min timeout keeps reading the
// stale sid on every user_engagement heartbeat, because this dispatcher
// (called on every event, not just pageload) never re-checked expiry.
// Log-46 audit: 7 sessions with 47-376 min internal gaps under the SAME
// sid, _ss staying 0 and sct never incrementing — GA4 collapsed what were
// really multiple sessions (one had 54 hits over 8 hours) into one.
// Mirrors the bootstrap's own 30-min/1-day logic exactly so both paths
// agree on when a session ends. Once the sid rotates here, sct/_s/_ss
// self-correct below — they're already keyed off the cookie value.
//
// Also re-records the journey entry (recordJourneyEntry, defined further
// down): it's idempotent per sid (early-returns updating just the exit
// path when the sid is unchanged), so calling it here is a safe no-op on
// every dispatch and the only way a session that rotates mid-tab gets its
// own orem_journey entry instead of being silently absorbed into the one
// recorded at page load under the old sid.
function ensureFreshSession() {
    oremEnsureIdentityCookies();
    try {
        var nowSec = Math.floor(Date.now() / 1000);
        var last = parseInt(readCookie('orem-ga-last-activity') || '0', 10);
        if (!readCookie('orem-ga-sid') || (nowSec - last) > 1800) {
            setOremCookieWithDomain('orem-ga-sid', String(nowSec), 1);
        }
        setOremCookieWithDomain('orem-ga-last-activity', String(nowSec), 1);
        var freshSid = readCookie('orem-ga-sid');
        if (freshSid) recordJourneyEntry(freshSid);
    } catch (e) { /* never break tracking */ }
}


function deleteCookie(name) {
    setCookieOrem(name, "", null);
}

function setCookieOrem(cname, cvalue, exdays) {
    let expires = "";
    if (exdays !== null) {
        const d = new Date();
        d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
        expires = "expires=" + d.toUTCString() + ";";
    } else {
        expires = "expires=Thu, 01 Jan 1970 00:00:00 UTC;"; // Set to a past date
    }
    document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}


// 4.0.0: removed gtag('consent', 'default', ...) — gtag.js no longer
// loads in the browser, so the consent API is not available. Stape
// sGTM applies the equivalent consent state server-side via the GA4
// server tag's consent overrides config.

const pixlelsConfig = {
    //meta: '586929266201445',
    //tiktok: 'TIKTOK_PIXEL_ID',
};

const googleConfig = {
    measurementId: orem_pixel_vars.ga4MeasurementId,
};

const pixelManager = new PixelManager(pixlelsConfig, googleConfig);

// 4.0.63 — PUBLIC tracking API for embedded widgets (Elementor quiz, etc.).
// STABLE CONTRACT — internal refactors MUST preserve this signature:
//   window.oremTrack(eventName, params, lead?)
//     eventName : string  → GA4 event name (custom names allowed)
//     params    : object  → scalars arrive in GA4 as ep.<key>
//     lead      : {email, firstName} (optional) → hashed here for Enhanced
//                 Conversions (or pass an already-shaped user_data object)
// Routes through the server-side pipeline (bot/admin/test gating, cid/session
// attribution from cookies). Guarded + fire-and-forget: never throws, never
// blocks the host page. Callers use `window.oremTrack && window.oremTrack(...)`
// so a missing/older bundle silently no-ops instead of breaking the page.
try {
    if (typeof window !== 'undefined') {
        window.oremTrack = function (eventName, params, lead) {
            try { pixelManager.sendCustom(eventName, params, lead); } catch (e) { /* never break host page */ }
        };
    }
} catch (e) { /* non-browser context */ }

// ───── 4.0.69: Scroll-depth tracker (blog posts only) ─────
//
// GA4's native `scroll` event is an Enhanced-Measurement feature of gtag.js
// (which this stack intentionally dropped) and only fires once at 90%.
// This emits `scroll_depth` at 25/50/75/90% — once per threshold per
// pageload — through the same server-side pipeline as every other event
// (bot/admin/test gating, cid/session attribution all apply).
//
// Scope: WP blog posts only (`single-post` body class) to keep event volume
// negligible (max 4 events per article read). Routing: custom-name path
// (opts.rawName) → GA4 only, like the quiz events — Meta/TikTok never see it.
// Reporting requires the event-scoped custom dimension `percent_scrolled`.
class ScrollDepthTracker {
    constructor() {
        this.thresholds = [25, 50, 75, 90];
        this.fired = {};
        this._ticking = false;
        this._onScroll = this._onScroll.bind(this);
        window.addEventListener('scroll', this._onScroll, { passive: true });
        // Initial check: a short article can start past a threshold (native
        // GA4 scroll also fires on load for short pages).
        this._check();
    }

    _depth() {
        const doc = document.documentElement;
        const total = Math.max(
            doc.scrollHeight || 0,
            document.body ? (document.body.scrollHeight || 0) : 0
        );
        if (!total) return 0;
        const seen = (window.pageYOffset || doc.scrollTop || 0) + window.innerHeight;
        return Math.min(100, Math.round((seen / total) * 100));
    }

    _onScroll() {
        if (this._ticking) return;
        this._ticking = true;
        const raf = window.requestAnimationFrame || function (cb) { setTimeout(cb, 100); };
        raf(() => {
            this._ticking = false;
            this._check();
        });
    }

    _check() {
        const d = this._depth();
        this.thresholds.forEach(t => {
            if (d >= t && !this.fired[t]) {
                this.fired[t] = true;
                try {
                    pixelManager.sendCustom('scroll_depth', { percent_scrolled: t });
                } catch (e) { /* never break the page */ }
            }
        });
        if (this.fired[90]) window.removeEventListener('scroll', this._onScroll);
    }
}

try {
    if (typeof document !== 'undefined' && document.body && document.body.classList.contains('single-post')) {
        new ScrollDepthTracker();
    }
} catch (e) { /* never break the page */ }

//Load
//
// Bug fix (2026-05-03): same window.load race fix as event-tracker.js — when
// WP Rocket Delay JS releases pixels.min.js after the load event has already
// fired (e.g. user finally interacts after sitting on the page), the
// addEventListener callback never runs and we lose the gtag PAGE_VIEW /
// VIEW_CONTENT / INITIATE_CHECKOUT dispatches entirely.
// Helper: write a first-party cookie scoped to the registrable domain
// (.orem.com.mx) so it travels with checkout POSTs no matter which
// subdomain the visitor is on. Mirrors print_browser_id_bootstrap's
// setCookieWithDomain — kept local to pixels.js so we don't depend on
// IIFE-scoped functions from the head bootstrap.
// allowEmpty (4.1.19): write '' on purpose, like the bootstrap's UTM overwrite.
function setOremCookieWithDomain(name, value, days, allowEmpty) {
    if (!value && !(allowEmpty && value === '')) return;
    var h = location.hostname.split('.');
    var domain = location.hostname;
    if (h.length > 2) {
        var second = h[h.length - 2];
        var twoLevelTLD = (second === 'com' || second === 'co' || second === 'org' || second === 'net' || second === 'gob' || second === 'edu');
        var keep = twoLevelTLD ? 3 : 2;
        if (h.length > keep) domain = h.slice(-keep).join('.');
    }
    var expires = '';
    if (days) {
        var d = new Date();
        d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + d.toUTCString();
    }
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = name + '=' + encodeURIComponent(value)
        + expires
        + '; Path=/; Domain=.' + domain
        + '; SameSite=Lax' + secure;
}

// ───── Customer journey recorder ─────
//
// Records one entry per GA4 session into the `orem_journey` cookie. Triggered
// from inside persistGoogleSessionIdsToOremCookies once the canonical sid is
// known. We compare the incoming sid against the most recent entry's sid;
// if they match this is a continuation page-load of an already-recorded
// session and we no-op. If they differ (or the cookie is empty) we prepend
// a fresh entry.
//
// Per-entry shape (compact keys to keep the cookie under 4KB):
//   t   — epoch seconds (when this session started)
//   sid — GA4 session_id, correlatable with the orem-ga-sid cookie
//   s   — utm_source if URL had one, else derived from referer host
//   m   — utm_medium (same fallback rules as s)
//   c   — utm_campaign (truncated 40)
//   ct  — utm_content (truncated 30, optional)
//   tm  — utm_term (truncated 30, optional)
//   lp  — landing path within this session (truncated 60, no domain)
//   ref — external referer host (null if internal/empty/self)
//   cid — ["fbclid", "gclid", ...] which click-ID URL params were present
//
// Cap: 15 entries (FIFO). With ~150-200 bytes per entry plus URL-encoding
// overhead this lands around 3-3.5KB — safely below the 4KB browser limit.
//
// Why session-level (not page-level): we want a multi-touch journey, not
// a session replay. Page-by-page tracking would explode the cookie and
// duplicates what GA4 already does well in aggregate. The per-order use
// case is "how did this specific customer arrive across N visits" — for
// which one entry per session is the right resolution.
// ───── Device + browser detection (4.0.5) ─────
// Cached at module-load so the regex tests run once per page, not per
// event. Used by recordJourneyEntry to stamp every session with its
// device class + browser. Buckets match what GA4 / Stape categorize
// against, so the journey timeline rolls up cleanly with the rest of
// the analytics pipeline.
var oremDeviceCache = null;
var oremBrowserCache = null;

function oremDetectDevice() {
    if (oremDeviceCache !== null) return oremDeviceCache;
    var ua = (navigator && navigator.userAgent) || '';
    if (/iPhone/i.test(ua))            oremDeviceCache = 'iPhone';
    else if (/iPad/i.test(ua))         oremDeviceCache = 'iPad';
    else if (/Android/i.test(ua))      oremDeviceCache = 'Android';
    else if (/Macintosh|Mac OS X/i.test(ua)) oremDeviceCache = 'Mac';
    else if (/Windows/i.test(ua))      oremDeviceCache = 'Windows';
    else if (/Linux/i.test(ua))        oremDeviceCache = 'Linux';
    else                                oremDeviceCache = 'Other';
    return oremDeviceCache;
}

function oremDetectBrowser() {
    if (oremBrowserCache !== null) return oremBrowserCache;
    var ua = (navigator && navigator.userAgent) || '';
    if (/Edg\//.test(ua))              oremBrowserCache = 'Edge';
    else if (/SamsungBrowser/.test(ua)) oremBrowserCache = 'Samsung';
    else if (/OPR\/|Opera/.test(ua))    oremBrowserCache = 'Opera';
    else if (/Firefox/.test(ua))        oremBrowserCache = 'Firefox';
    else if (/Chrome\//.test(ua))       oremBrowserCache = 'Chrome';
    else if (/Safari/.test(ua))         oremBrowserCache = 'Safari';
    else                                 oremBrowserCache = 'Other';
    return oremBrowserCache;
}

// deriveSourceMediumFromReferer + oremNormalizeUtmPair are defined in
// utm-mapping.js (loaded before this file via the build order in build.js).

// Manual param extractor that preserves raw bytes — URLSearchParams.get()
// silently corrupts latin-1 escapes (Brevo email links). decodeFlexible
// tries strict UTF-8 first, then a latin-1 fallback (4.0.34). Returns null
// when the key is absent and '' when it is present without a value (same
// presence semantics as URLSearchParams.has()).
function oremQueryParam(name) {
    var search = location.search || '';
    if (!search) return null;
    var s = search.charAt(0) === '?' ? search.slice(1) : search;
    var pairs = s.split('&');
    var enc = encodeURIComponent(name);
    for (var i = 0; i < pairs.length; i++) {
        var idx = pairs[i].indexOf('=');
        var k = idx === -1 ? pairs[i] : pairs[i].slice(0, idx);
        if (k === name || k === enc) {
            var raw = idx === -1 ? '' : pairs[i].slice(idx + 1);
            var spaced = raw.replace(/\+/g, ' ');
            try { return decodeURIComponent(spaced); }
            catch (e) {
                try {
                    var fixed = spaced.replace(/%[0-9A-Fa-f]{2}/g, function(m) {
                        var b = parseInt(m.slice(1), 16);
                        return b < 0x80 ? m : encodeURIComponent(String.fromCharCode(b));
                    });
                    return decodeURIComponent(fixed);
                } catch (e2) { return spaced; }
            }
        }
    }
    return null;
}

// ───── 4.1.19: self-heal for the head bootstrap's cookies ─────
//
// On part of the iPhone visits inside the Instagram/Facebook app, the cookies
// print_browser_id_bootstrap writes on the landing page (orem-ga-cid, _fbp,
// orem_utm_*, orem_first_touch_ts) are not there afterwards, while the ones
// this bundle writes (orem_journey, orem-ga-sid) are: orders 327070, 327104,
// 327135, 327159, 327185 had cid/_fbp minted 5-139 s after the journey entry
// and no UTMs, although the journey shows the ig/paid or link_in_bio landing.
// GA4 then split the visit (a 1-3 hit ig session + a "(not set)" one holding
// the cart and the purchase). This re-creates whatever is missing, once per
// pageload, at the first dispatch. It only creates — never rotates.
//
// 4.1.20 — the cause is the mid-page wipe that oremIdGuard() (above) repairs,
// and the guard runs FIRST in every dispatch, putting the SAME ids back. So
// this only acts when there is nothing to restore: the ids were already gone
// when the bundle took its first snapshot (bootstrap did not run / its
// cookies never stuck). cid_source=js_remint marks that case, ep.id_restored
// the wipe-and-restore one. The order snapshot keeps its own fallback
// (journey UTMs, Core::stamp_utm_from_journey) for whatever both miss.
var oremIdentityHealed = false;
var oremCidReminted = false;

function oremSetHostCookie(name, value, maxAgeSeconds) {
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = name + '=' + value
        + '; Max-Age=' + maxAgeSeconds
        + '; Path=/; SameSite=Lax' + secure;
}

function oremEnsureIdentityCookies() {
    if (oremIdentityHealed) return;
    oremIdentityHealed = true;
    try {
        var nowMs = Date.now();
        var ID_DAYS = 400; // = the bootstrap's ID_MAX_AGE

        if (!readCookie('orem-ga-cid')) {
            var rnd;
            try {
                var buf = new Uint32Array(1);
                crypto.getRandomValues(buf);
                rnd = buf[0] >>> 1; // 31-bit, like the bootstrap
            } catch (e) { rnd = Math.floor(Math.random() * 2147483647); }
            setOremCookieWithDomain('orem-ga-cid', rnd + '.' + Math.floor(nowMs / 1000), ID_DAYS);
            // Flags only when the cookie stuck: with cookies blocked every
            // dispatch would otherwise mint a new "first visit".
            if (readCookie('orem-ga-cid')) {
                oremCidReminted = true;
                try { sessionStorage.setItem('orem-ga-fv-pending', '1'); }
                catch (e) { oremSetHostCookie('orem-ga-fv-pending', '1', 1800); }
            }
        }

        if (!readCookie('_fbp')) {
            var parts = location.hostname.split('.');
            var level = parts.length === 2 ? 1 : 2;
            oremSetHostCookie('_fbp', 'fb.' + level + '.' + nowMs + '.' + (Math.floor(Math.random() * 9e9) + 1e9), ID_DAYS * 86400);
            // Fresh value, not a poisoned one: mark it current, or the
            // bootstrap on the next page reads versionStale and rotates it.
            // Keep in sync with COOKIE_VERSION in print_browser_id_bootstrap.
            oremSetHostCookie('_orem_id_v', '2', ID_DAYS * 86400);
        }

        // UTMs: same presence test and full overwrite as the bootstrap, but
        // only if the bootstrap did not process THIS landing (it stamps
        // orem_first_touch_ts with Date.now() while the head parses, i.e.
        // after performance.timeOrigin).
        var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
        var anyUtm = false;
        for (var i = 0; i < keys.length; i++) {
            if (oremQueryParam(keys[i]) !== null) { anyUtm = true; break; }
        }
        if (!anyUtm) return;
        var rs = String(oremQueryParam('utm_source') || '').toLowerCase();
        var rm = String(oremQueryParam('utm_medium') || '').toLowerCase();
        if (rs === 'template-library' || rm === 'wp-dash') return;
        var ts = parseInt(readCookie('orem_first_touch_ts') || '0', 10);
        var origin = (typeof performance !== 'undefined' && performance.timeOrigin) ? performance.timeOrigin : 0;
        if (ts && (!origin || ts >= origin)) return;
        keys.forEach(function (k) {
            var v = oremQueryParam(k);
            setOremCookieWithDomain('orem_' + k, v == null ? '' : v, 90, true);
        });
        setOremCookieWithDomain('orem_first_touch_ts', String(nowMs), 90);
    } catch (e) { /* never break tracking */ }
    finally {
        // Re-snapshot so a later wipe on this page restores what was just
        // minted instead of minting again (no wipe here: cid is present).
        oremIdGuard();
    }
}

function recordJourneyEntry(sid) {
    if (!sid) return;
    sid = String(sid);

    var raw = readCookie('orem_journey');
    var journey = [];
    if (raw) {
        try {
            var parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) journey = parsed;
        } catch (e) { /* corrupt — start fresh */ }
    }

    var currentPath = (location.pathname || '/').substring(0, 60);

    // Same session as last recorded entry → update exit path (xp) so the
    // journey reflects the LAST page visited in this session, not just
    // the landing. Only writes the cookie when xp actually changed —
    // avoids re-serializing on every gtag.get callback during a single
    // page load.
    //
    // 4.1.15 — EXCEPT when this pageload is a new UTM landing inside the same
    // session. The bootstrap overwrites the orem_utm_* cookies on every URL
    // that carries a UTM (last-acquisition-touch), so the order is credited
    // to that later touch — but the journey kept only the session's FIRST
    // landing and the two disagreed. Order 326875: a Meta ad landing at
    // 12:12, a link_in_bio landing 6 min later in the same session, checkout
    // at 12:27 → order UTMs = link_in_bio, journey showed only the ad. Now
    // that second landing gets its own entry (same sid, newer t).
    if (journey.length > 0 && journey[0] && journey[0].sid === sid && !isNewUtmTouch(journey[0])) {
        if (journey[0].xp !== currentPath) {
            journey[0].xp = currentPath;
            setOremCookieWithDomain('orem_journey', JSON.stringify(journey), 90);
        }
        return;
    }

    var entry = { t: Math.floor(Date.now() / 1000), sid: sid };

    // A declaration, not `var qparam = …`: isNewUtmTouch() above runs before
    // this line and needs the hoisted function.
    function qparam(name) { return oremQueryParam(name); }

    // Does the URL carry UTMs (same presence test as the bootstrap's cookie
    // overwrite) that differ from the given entry? Admin-context UTMs never
    // count. Compared with the same truncation the entry was stored with.
    function isNewUtmTouch(prev) {
        var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
        var any = false;
        for (var k = 0; k < keys.length; k++) {
            if (qparam(keys[k]) !== null) { any = true; break; }
        }
        if (!any) return false;
        var rs = qparam('utm_source') || '', rm = qparam('utm_medium') || '';
        if (rs.toLowerCase() === 'template-library' || rm.toLowerCase() === 'wp-dash') return false;
        var n = oremNormalizeUtmPair(rs, rm);
        var cut = function (v, len) { return String(v || '').substring(0, len); };
        return cut(n.s, 30) !== cut(prev.s, 30)
            || cut(n.m, 30) !== cut(prev.m, 30)
            || cut(qparam('utm_campaign'), 40) !== cut(prev.c, 40)
            || cut(qparam('utm_content'), 30) !== cut(prev.ct, 30)
            || cut(qparam('utm_term'), 30) !== cut(prev.tm, 30);
    }

    // UTMs first; fall back to referer-derived source/medium.
    var s = qparam('utm_source'), m = qparam('utm_medium');
    var c = qparam('utm_campaign'), ct = qparam('utm_content'), tm = qparam('utm_term');

    var siteHost = location.hostname.replace(/^www\./, '');
    var refHostExternal = null;
    try {
        if (document.referrer) {
            var rh = new URL(document.referrer).hostname.toLowerCase();
            if (rh.replace(/^www\./, '') !== siteHost) refHostExternal = rh;
        }
    } catch (e) { /* malformed referrer — leave null */ }

    if (!s && !m) {
        var derived = deriveSourceMediumFromReferer(refHostExternal);
        s = s || derived.s;
        m = m || derived.m;
    } else {
        // 4.0.39 — URL had UTMs; canonicalize to drop drift (fb→meta,
        // cpc→paid, IGShopping→ig, etc.).
        var norm = oremNormalizeUtmPair(s || '', m || '');
        s = norm.s;
        m = norm.m;
        // 4.1.19 — marks s/m as coming from a URL UTM, not from the referer
        // (an Instagram referer and a utm_source=ig link both give ig/social).
        // The order snapshot only falls back to an entry carrying it.
        entry.u = 1;
    }

    if (s)  entry.s  = String(s).substring(0, 30);
    if (m)  entry.m  = String(m).substring(0, 30);
    if (c)  entry.c  = String(c).substring(0, 40);
    if (ct) entry.ct = String(ct).substring(0, 30);
    if (tm) entry.tm = String(tm).substring(0, 30);

    entry.lp = currentPath; // landing path
    entry.xp = currentPath; // exit path (initialized; updated on next pageload within this session)
    if (refHostExternal) entry.ref = refHostExternal.substring(0, 40);

    // 4.0.5: device + browser per session entry. Useful to spot
    // device-switch journeys (browsed on mobile, bought on desktop)
    // which is a common ecommerce attribution signal we couldn't
    // surface before. Detection is regex on navigator.userAgent
    // — coarse but matches the same buckets analytics tools use.
    entry.dv = oremDetectDevice();
    entry.br = oremDetectBrowser();

    // Track which click-ID URL params triggered this session (presence-only,
    // not values — the values are already in _orem_<clid> cookies).
    var clickParams = ['gclid', 'msclkid', 'fbclid', 'ttclid', 'wbraid', 'gbraid'];
    var present = [];
    for (var i = 0; i < clickParams.length; i++) {
        if (qparam(clickParams[i])) present.push(clickParams[i]);
    }
    if (present.length) entry.cid = present;

    // Prepend, cap at 10 entries (FIFO drop oldest).
    // 4.0.7: was 15. Cookie hard limit is 4KB per browser. With xp +
    // dv + br fields added in 4.0.5/4.0.6 a worst-case entry can hit
    // ~700 bytes URL-encoded; 15 entries would exceed 4KB. 10 entries
    // covers >95% of real ecommerce journeys (people who took 11+
    // distinct sessions to convert are extremely rare) while leaving
    // headroom for the URL-encoding overhead.
    journey.unshift(entry);
    if (journey.length > 10) journey = journey.slice(0, 10);

    // Soft size guard: even at 10 entries, an unlucky combination of
    // long campaign names + long paths + multiple click IDs could push
    // the cookie above the browser limit. Trim oldest entries until the
    // raw JSON is under 3000 bytes (URL-encoded ≈ 4000 bytes, safely
    // under the 4096 byte per-cookie cap on every modern browser).
    var serialized = JSON.stringify(journey);
    while (serialized.length > 3000 && journey.length > 1) {
        journey.pop(); // tail = oldest
        serialized = JSON.stringify(journey);
    }

    setOremCookieWithDomain('orem_journey', serialized, 90);

    // Diagnostic: verify the cookie actually got written. Browser policies
    // (incognito with strict cookie blocking, content-blocking extensions,
    // quota exceeded) can silently reject document.cookie writes. Surface
    // it so we can spot why an order is missing _orem_journey in production.
    try {
        if (!readCookie('orem_journey')) {
            console.warn('[orem_journey] write failed silently — browser blocked or quota exceeded');
        }
    } catch (e) { /* never break tracking on diagnostic */ }
}




// 4.0.0: persistGoogleSessionIdsToOremCookies removed. cid/sid are now
// generated synchronously in print_browser_id_bootstrap (wp_head priority
// 0) using crypto.getRandomValues — completely independent of gtag.js
// since gtag is no longer loaded in 4.0.0. Cookies set there
// (`orem-ga-cid`, `orem-ga-sid`, `orem-ga-last-activity`) drive both the
// browser-side payload and PHP's sendEventToGTM cid/sid resolution.

// ───── Engagement tracker (4.0.0, seg fix 4.0.25) ─────
//
// GA4's engaged_session metric requires a `user_engagement` event with
// an `engagement_time_msec` parameter. Without it, every session shows
// as a bounce in GA4 reporting. gtag.js handles this automatically via
// the Page Visibility API + 10s flush; since we no longer load gtag,
// we replicate the behavior here:
//
//   - Active engagement = page is visible AND has focus
//   - Accumulate active milliseconds while engaged (oremEngagementMs)
//   - Flush every 30s, on blur, visibilitychange→hidden, pagehide
//
// Two distinct counters:
//
//   oremEngagementMs       per-flush delta (resets on each flush; sent as
//                          engagement_time_msec on the user_engagement event)
//   localStorage cum       cumulative session-wide engagement, sid-scoped
//                          (4.0.40 — migrated from sessionStorage so multi-tab
//                          users accumulate seg across all tabs of one sid).
//                          Drives the sticky `seg` flag — once cumulative
//                          crosses OREM_SEG_THRESHOLD_MS, every subsequent
//                          event carries seg=1.
//
// The user_engagement event itself dispatches through PixelManager so it
// inherits cid/sid/event_id/user_data automatically.
var OREM_SEG_THRESHOLD_MS = (typeof orem_pixel_vars !== 'undefined'
    && orem_pixel_vars.engagedSessionMs)
    ? parseInt(orem_pixel_vars.engagedSessionMs, 10) || 10000
    : 10000;

var oremEngagementMs = 0;
var oremEngagementLastTickAt = null;

function oremEngagementIsEngaged() {
    return document.visibilityState === 'visible' && document.hasFocus();
}

function oremEngagementStart() {
    if (oremEngagementIsEngaged() && oremEngagementLastTickAt === null) {
        oremEngagementLastTickAt = Date.now();
    }
}

function oremEngagementPause() {
    if (oremEngagementLastTickAt !== null) {
        oremEngagementMs += Date.now() - oremEngagementLastTickAt;
        oremEngagementLastTickAt = null;
    }
}

// 4.0.40 — Cumulative session engagement migrated sessionStorage → localStorage.
// sessionStorage is per-tab; cross-tab navigation (e.g. open product in new tab)
// reset the counter, undercounting `seg=1` engagement signals (~22% of sessions
// crossing 10s threshold across multiple tabs were misreported as seg=0).
//
// Now stored in localStorage, sid-scoped key. Stale keys from previous sessions
// are pruned on bump (cheap iteration, localStorage usually has <50 entries).
const OREM_ENG_STORAGE_PREFIX = 'orem-ga-session-eng:';
function oremTotalEngagementKey() {
    return OREM_ENG_STORAGE_PREFIX + (readCookie('orem-ga-sid') || '0');
}
function oremTotalEngagementGet() {
    try {
        var v = parseInt(window.localStorage.getItem(oremTotalEngagementKey()) || '0', 10);
        return isNaN(v) ? 0 : v;
    } catch (e) { return 0; }
}
function oremTotalEngagementBump(deltaMs) {
    try {
        var current_key = oremTotalEngagementKey();
        // Prune stale keys (previous sessions' sids) — keeps localStorage tidy.
        // Race-safe: even if two tabs prune simultaneously, idempotent removeItem.
        for (var i = window.localStorage.length - 1; i >= 0; i--) {
            var k = window.localStorage.key(i);
            if (k && k.indexOf(OREM_ENG_STORAGE_PREFIX) === 0 && k !== current_key) {
                window.localStorage.removeItem(k);
            }
        }
        var v = oremTotalEngagementGet() + deltaMs;
        window.localStorage.setItem(current_key, String(v));
    } catch (e) { /* localStorage disabled — seg will stay 0, acceptable */ }
}
// Snapshot total = stored cumulative + unflushed current + live tick.
// Used at sendEvent time so seg=1 fires on the very event that crosses
// the threshold, not waiting for the next flush.
function oremTotalEngagementSnapshot() {
    var live = oremEngagementMs;
    if (oremEngagementLastTickAt !== null) {
        live += Date.now() - oremEngagementLastTickAt;
    }
    return oremTotalEngagementGet() + live;
}

function oremEngagementFlush() {
    oremEngagementPause();
    if (oremEngagementMs < 1000) {
        // Tiny accumulations get held until next flush — avoids spamming
        // GA4 with sub-second engagement events.
        oremEngagementStart();
        return;
    }
    var ms = oremEngagementMs;
    oremTotalEngagementBump(ms);   // bump cumulative BEFORE reset
    oremEngagementMs = 0;
    pixelManager.sendEvent(EventTypes.ENGAGEMENT, { engagement_time_msec: ms });
    oremEngagementStart();
}

function oremEngagementInit() {
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
            oremEngagementStart();
        } else {
            oremEngagementFlush();
        }
    });
    window.addEventListener('focus', oremEngagementStart);
    window.addEventListener('blur', oremEngagementFlush);
    window.addEventListener('pagehide', oremEngagementFlush);
    setInterval(oremEngagementFlush, 30000);
    oremEngagementStart();
}

// WEB-72 (2026-09) — clic en el menú de navegación (header + cajón móvil).
// `.orem-nav-primary` / `.orem-nav-mobile` son clases ESTABLES puestas por
// PHP en el propio <ul> del menú vía wp_nav_menu_args (ver
// orem_nav_stable_menu_class en class-wp-menu-css-js-manager.php) —
// deliberadamente NO se usa el slug del menú (ej. "principal-2026-09"):
// ese slug cambia con cada 'version'/'season' de Tools/menus/menus.spec.php
// (abril agrega "-mama", enero cambia el año) y un selector atado a él se
// habría roto en el primer swap estacional. Delegado en `document` con
// captura porque el <a> vive dentro de markup del tema padre (no hay gancho
// PHP propio para el click) y algunos ítems de mega-menú son tarjetas de
// producto que un listener normal en bubble podría perder si otro handler
// detiene la propagación primero.
const OREM_MENU_CLICK_SELECTOR = '.orem-nav-primary a, .orem-nav-mobile a';

function oremMenuClickLocation(a) {
    if (a.closest('.orem-nav-mobile')) return 'mobile';
    if (a.closest('.orem-nav-primary')) return 'primary';
    return 'unknown';
}

function oremMenuClickDepth(a) {
    // Depth = number of ancestor <li> above this link's own <li>. Top-level
    // items (AGENDAS, DIARIOS Y REGALOS, …) are depth 0.
    var li = a.closest('li');
    var depth = 0;
    while (li && li.parentElement) {
        var parentLi = li.parentElement.closest('li');
        if (!parentLi) break;
        depth++;
        li = parentLi;
    }
    return depth;
}

function oremMenuClickInit() {
    document.addEventListener('click', function (e) {
        var a = e.target && e.target.closest ? e.target.closest(OREM_MENU_CLICK_SELECTOR) : null;
        if (!a) return;
        var label = (a.textContent || '').trim().slice(0, 80);
        var location = oremMenuClickLocation(a);
        var depth = oremMenuClickDepth(a);
        // 4.1.4 — repeated taps on the same item while the next page loads
        // are one choice, not three (log 60: "Ver todos los diarios" x3 in
        // 2 s, distinct event ids).
        // 4.1.20 — sliding window: every suppressed tap extends it, so a burst
        // counts once however long it lasts (log 65: "Stickers" x3 and
        // "Tazas y botellas" x2 at exactly 2 s intervals slipped past the
        // fixed 2 s window).
        var now = Date.now();
        var key = location + '|' + (a.href || label);
        var last = oremMenuClickInit._last;
        oremMenuClickInit._last = { key: key, ts: now };
        if (last && last.key === key && now - last.ts < 3000) return;
        pixelManager.sendCustom('menu_click', {
            // Nombres semánticos (para cuando se registren como custom
            // dimensions de GA4 — ver Documentation/MENU.md §Medición).
            menu_label: label,
            menu_location: location,
            menu_depth: depth,
            // Espejo en dimensiones YA registradas y reportables hoy
            // (ga4_custom_dimensions: text/event_action/link_url/
            // event_label), para que esto sea medible desde el primer
            // clic sin depender de que alguien entre a GA4 Admin.
            text: label,
            event_action: location,
            link_url: a.href || '',
            event_label: String(depth),
        });
    }, true);
}

// WEB-7x (2026-09-20) — apertura del cajón móvil. Sin esto no hay forma de
// saber si la tasa de APERTURA del menú cambió con el rediseño (solo se
// medían los clics de dentro) — necesario para separar "se abre menos" de
// "se abre igual pero se usa distinto", la pregunta que de verdad importa
// para juzgar si el acordeón cerrado por defecto ayudó o estorbó.
// `.menu-toggle` es el botón hamburguesa del theme padre
// (`aria-controls="site-navigation"`); `.close-drawer` es el botón de
// cerrar (X) — deliberadamente NO instrumentado, un cierre no es un evento
// de descubrimiento.
const OREM_MENU_OPEN_SELECTOR = '.menu-toggle';

function oremMenuOpenInit() {
    document.addEventListener('click', function (e) {
        var t = e.target && e.target.closest ? e.target.closest(OREM_MENU_OPEN_SELECTOR) : null;
        if (!t) return;
        // 4.1.4 — the hamburger stays clickable on top of the open drawer
        // and a second tap does NOT close it (Shoptimizer keeps
        // body.mobile-toggled; aria-expanded never changes), so every extra
        // tap was another menu_open (log 60: pairs 0-1 s apart). Capture
        // phase runs before the theme's handler: the class is only already
        // there when the drawer was open before this click.
        if (document.body && document.body.classList.contains('mobile-toggled')) return;
        pixelManager.sendCustom('menu_open', {
            menu_location: 'mobile',
            event_action: 'mobile',
        });
    }, true);
}


// 4.0.0: customer journey recording. orem-ga-sid is set synchronously by
// print_browser_id_bootstrap before this script runs, so we can read it
// from the cookie directly and record without depending on any async gtag
// callback. recordJourneyEntry is idempotent per sid — same session = no-op.
var oremJourneySid = readCookie('orem-ga-sid');
if (oremJourneySid) {
    try { recordJourneyEntry(oremJourneySid); } catch (e) { /* never break tracking */ }
}

const oremPixelsOnLoad = () => {
    console.log('init v' + orem_pixel_vars.version);

    // 4.1.0: page_view is dispatched from the bundle again. The 4.0.x inline
    // /track/page-view script had no timing advantage — it also waited for
    // `load` + requestIdleCallback — and every one of those beacons cost a
    // full WordPress bootstrap. window.oremPageViewEventId is still minted
    // by body_open_cookie_keeper so the id stays stable across the page.
    pixelManager.sendEvent(EventTypes.PAGE_VIEW, {}, window.oremPageViewEventId || null);

    // 4.0.10: stop sharing window.oremPageViewEventId with view_item. The
    // shared id was a legacy of the gtag.js coexistence era (3.0.31) where
    // both server-side /track/page-view and browser-side gtag fired
    // page_view, and GA4 needed the matching event_id to dedupe. Since
    // 4.0.0 removed gtag entirely, page_view fires once and view_item
    // is a different event name — the shared id provides no dedupe value
    // and breaks BigQuery joins / future server-side view_item retries.
    // Letting sendEvent default eventId to null forces generateEventID()
    // to mint a fresh one per event.
    if (typeof oremViewContentData !== 'undefined' && oremViewContentData !== null) {
        pixelManager.sendEvent(EventTypes.VIEW_CONTENT, oremViewContentData);
    }

    // view_item_list fires once on archive pages (shop, category, tag, search)
    // when the PHP loop populates `oremViewItemListData` with items + list
    // metadata. GA4-only — Meta/TikTok blocked in sGTM Trigger Exceptions.
    if (typeof oremViewItemListData !== 'undefined' && oremViewItemListData !== null) {
        pixelManager.sendEvent(EventTypes.VIEW_ITEM_LIST, oremViewItemListData);
    }

    // begin_checkout fires once per checkout page load.
    if (typeof oremInitCheckoutData !== 'undefined' && oremInitCheckoutData != null) {
        pixelManager.sendEvent(EventTypes.INITIATE_CHECKOUT, oremInitCheckoutData);
    }

    // Engagement tracker — installs visibility/focus/pagehide listeners
    // and starts accumulating active ms. Required for GA4 engaged_session
    // metric. Safe to install on every page (no duplicates, idempotent).
    try { oremEngagementInit(); } catch (e) { /* never break tracking */ }
    try { oremMenuClickInit(); } catch (e) { /* never break tracking */ }
    try { oremMenuOpenInit(); } catch (e) { /* never break tracking */ }
};
// 4.1.4 — a prerendered document runs its scripts before (and sometimes
// without) the visitor ever seeing it: Chrome prerenders from the omnibox,
// so page_view / view_item fired for pages nobody opened (log 60: two
// page_views 133 ms apart, different event ids, one pageload). Hold the whole
// bootstrap until the page is actually shown — what gtag.js does natively.
const oremPixelsWhenShown = () => {
    if (document.prerendering) {
        document.addEventListener('prerenderingchange', oremPixelsOnLoad, { once: true });
    } else {
        oremPixelsOnLoad();
    }
};
if (document.readyState === 'complete') {
    oremPixelsWhenShown();
} else {
    window.addEventListener('load', oremPixelsWhenShown);
}

//});



//#############################    Orem Log    ##############################
//###########################################################################


/*
// Meta Pixel (Facebook)
class MetaPixel extends TrackingPixel {
    constructor(pixelId) {
        super();
        this.pixelId = pixelId;
        this.injectPixel();
    }

    injectPixel() {
        if (!window.fbq) {
            !(function (f, b, e, v, n, t, s) {
                if (f.fbq) return;
                n = f.fbq = function () {
                    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
                };
                if (!f._fbq) f._fbq = n;
                n.push = n;
                n.loaded = !0;
                n.version = '2.0';
                n.queue = [];
                t = b.createElement(e);
                t.async = !0;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t, s);
            })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

            const userData = this.getMetaUserData();
            if (this.isDataEmpty(userData)) {
                if (typeof fbq === 'function') fbq('init', this.pixelId);
            } else {
                if (typeof fbq === 'function') {
                    //console.log('init fbq with user data');
                    //console.log(userData);
                    fbq('init', this.pixelId, userData);
                }
            }
        }
    }

    getMetaObject(eventId) {

        let metaObject = {
            eventID: eventId,
        }
        return metaObject;
    }

    getMetaUserData() {
        let userData = {};
        let loggedInUserData = readCookie('orem_logged_in_user_data');
        if (loggedInUserData && loggedInUserData != null && loggedInUserData != '') {
            // Parse the JSON string back into an object
            loggedInUserData = JSON.parse(loggedInUserData);
            if (typeof loggedInUserData.address.city !== 'undefined') userData.ct = loggedInUserData.address.city;
            if (typeof loggedInUserData.address.region !== 'undefined') userData.st = loggedInUserData.address.region;
            if (typeof loggedInUserData.address.postal_code !== 'undefined') userData.zp = loggedInUserData.address.postal_code;
            if (typeof loggedInUserData.address.country !== 'undefined') userData.country = loggedInUserData.address.country;

        }

        //email
        let email = oremUserData.getMarketingEmail();
        if (email && email !== null && email != '') userData.em = email;

        //phone
        let phone = oremUserData.getMarketingPhone();
        if (phone && phone !== null && phone != '') userData.ph = phone.replace('+', '');

        //first name
        let firstName = oremUserData.getMarketingFirstName();
        if (firstName && firstName !== null && firstName != '') userData.fn = firstName;

        //first name
        let lastName = oremUserData.getMarketingLastName();
        if (lastName && lastName !== null && lastName != '') userData.ln = lastName;

        //external ID
        const externalId = this.getExternalId();
        if (typeof externalId != 'undefined' && externalId !== null && externalId != '') userData.external_id = externalId;

        return userData;
    }

    sendEvent(eventName, data, eventId) {
        let metaEventName = this.getEventName(eventName, this.Platforms.FACEBOOK);
        if (metaEventName != 'NA') {
            let normalizedData = this.normalizeData(eventName, data);

            const metaObject = this.getMetaObject(eventId);

            //console.log('META Send event: ' + metaEventName + ' - ' + JSON.stringify(normalizedData) + ' - metaObject - ' + JSON.stringify(metaObject));
            //console.log('META Send event: ' + metaEventName + ' - ' + JSON.stringify(normalizedData));
            if (typeof fbq === 'function') fbq('track', metaEventName, normalizedData, metaObject);
        }

    }

    contentsGAtoMeta(data, incudeNumItems) {
        // Extract the "items" array, value, and currency
        const {
            items,
            value,
            currency
        } = data;
        // Prepare content_ids by extracting item_id from each item
        const content_ids = items.map(item => item.item_id);

        // Si no se proporciona ningún content_type, Meta hará que el evento coincida con todos 
        // los elementos que tengan el mismo identificador, independientemente del tipo.
        //const content_type = 'product';
        const contents = data.items.map(item => ({
            id: item.item_id, // Product ID
            quantity: item.quantity // Product quantity
        }));
        // Format the data for Meta Pixel
        let metaPixelData = {
            content_ids: content_ids, // Array of item IDs
            contents: contents, // Properly formatted contents array
            currency: currency, // Currency code
            value: value, // Total value of items
        };

        if (incudeNumItems) {
            let numberItems = 0;
            contents.forEach(contents => {
                numberItems += contents.quantity;
            });
            if (numberItems > 0) metaPixelData.num_items = numberItems;
        }

        if (data && data.transaction_id && data.transaction_id != null && data.transaction_id > 0) metaPixelData.transaction_id = data.transaction_id;

        return metaPixelData;
    }
    normalizeData(eventName, data) {
        const isDataEmpty = this.isDataEmpty(data);
        if (isDataEmpty) return data;

        if (eventName === EventTypes.VIEW_CONTENT) {
            data = this.contentsGAtoMeta(data, false);
        } else if (eventName === EventTypes.ADD_TO_CART) {
            data = this.contentsGAtoMeta(data, false);
        } else if (eventName === EventTypes.INITIATE_CHECKOUT) {
            data = this.contentsGAtoMeta(data, true);
        } else if (eventName === EventTypes.ADD_PAYMENT_INFO) {
            data = this.contentsGAtoMeta(data, true);
        } else if (eventName === EventTypes.PURCHASE) {
            data = this.contentsGAtoMeta(data, true);
        } else if (eventName === EventTypes.CONTACT) {

        } else if (eventName === EventTypes.SEARCH) {

        } else if (eventName === EventTypes.VIEW_ITEM_LIST) {
            // GA4-only event; no Meta/TikTok normalization needed.
        } else {
            console.error(`NormalizeData.Invalid event name "${eventName}"`);
        }

        return data;
    }
}
*/
;
// ───── checkout.js ─────
/**
 * BlocksCheckoutEventTracker (4.0.49)
 *
 * Replaces the classic-shortcode `CheckoutEventTracker` with a tracker
 * native to the WooCommerce Blocks checkout (React/Redux, no
 * `payment_method_selected` / `updated_checkout` / `checkout_error`
 * jQuery body events). Wires three signal sources in parallel:
 *
 *   1. wp.data.subscribe() over `wc/store/payment`, `wc/store/cart`,
 *      `wc/store/checkout`, `wc/store/validation`, `wc/store/notices`
 *      — fine-grained Redux state diffs.
 *   2. WC Blocks DOM events on document.body (the public extensibility
 *      surface emitted by wc-blocks-checkout-events module).
 *   3. Native DOM events (focus/blur on inputs, click delegation,
 *      MutationObserver for the account-exists prompt).
 *
 * Event funnel emitted via pixelManager.sendEvent:
 *   - begin_checkout       fires from oremPixelsOnLoad on
 *                          `oremInitCheckoutData` (handled by app.js).
 *   - add_shipping_info    (4.0.64) once per page on a GENUINE shipping
 *                          signal: user-initiated rate change, shipping-block
 *                          interaction, or the payment_reached floor. NOT on
 *                          the hydration auto-default.
 *   - add_payment_info     (4.0.64 rework) once per page on a GENUINE payment
 *                          signal: post-baseline method change, payment-block
 *                          interaction, or submit (floor). NOT on the WC Blocks
 *                          hydration auto-default — that made it fire 1:1 with
 *                          begin_checkout since 4.0.50. saved_method_used tag
 *                          for the saved-token path.
 *   - CHECKOUT_PROGRESS:
 *       9   cart→checkout intent (proceed-to-checkout block click)
 *       10  step section visible (IntersectionObserver per block)
 *       11  payment method selected (every change after the first)
 *       12  generic checkout error shown
 *       13  shipping rate revealed/changed (covers auto-default)
 *       14  coupon applied
 *       15  coupon removed
 *       16  coupon failed
 *       17  cart modified during checkout (item/qty diff)
 *       18  coupon applied but only covers PART of the cart (addons Store
 *           API extension, extensions.orem-coupons.partial_discount)
 *       20  submit clicked
 *       21  submit succeeded (order processed)
 *       22  submit failed (payment / validation / server)
 *       30  email-exists prompt shown (addons inline login)
 *       31  "Iniciar sesión" clicked
 *       32  "Continuar como invitado" clicked
 *       33  Express Payment surface click (gateway-agnostic)
 *       96  field-stuck friction signal
 *       99  no shipping rates available
 *   - checkout_field_blur          per-field dwell + had_value + prefilled
 *   - checkout_validation_error    one per (field, message) per page
 *
 * Every dispatched event also pushes a Microsoft Clarity custom event
 * with a normalized name (`orem_chkt_step_NN_<slug>` /
 * `orem_chkt_validation_<field>`) so analysts can filter Clarity
 * session replays by funnel position and watch the exact UX glitch
 * that triggered friction.
 *
 * Idempotency: per-event Set keyed on the value that should not
 * re-fire (rate_id, coupon_code, field_name, etc.). The
 * `__oremPaymentInfoFired` window flag is preserved verbatim so any
 * existing sGTM rule referencing it still works.
 */

// 4.0.72 — contextos donde WC Blocks registra notices de error, en
// `core/notices`. Fuente ÚNICA compartida entre getCheckoutErrorText()
// (texto del error de pago, step 22) y diffNotices() (detección de cupón
// fallido, step 16). Antes cada función tenía su propia lista y divergían:
// getCheckoutErrorText() SÍ incluía 'wc/cart', diffNotices() NO — y el
// formulario de cupón (incluso embebido en el Checkout Block, que reusa el
// mismo sub-componente del Cart Block) registra su error bajo 'wc/cart'
// SIEMPRE, sin importar en qué página esté. Confirmado en producción:
// 0 eventos coupon_failed en 180 días, incluso 2+ semanas después del fix
// parcial de 4.0.66 (que agregó contextos a getCheckoutErrorText() pero
// dejó diffNotices() con solo 'wc/checkout'). Compartir esta lista evita
// que las dos funciones vuelvan a divergir.
const CHECKOUT_NOTICE_CONTEXTS = ['wc/checkout/payments', 'wc/checkout', 'wc/checkout/shipping-methods', 'wc/checkout/billing-address', 'wc/checkout/shipping-address', 'wc/cart'];

class BlocksCheckoutEventTracker {
    constructor() {
        // Hard gate: Blocks checkout DOM + wp.data runtime must both be
        // present. Otherwise this tracker silently no-ops (e.g. classic
        // checkout, non-checkout pages, admin contexts where wp.data
        // isn't loaded).
        if (!document.querySelector('.wp-block-woocommerce-checkout')) return;
        if (!window.wp || !window.wp.data || typeof window.wp.data.subscribe !== 'function') return;
        if (typeof pixelManager === 'undefined') return;

        this.$jq = typeof jQuery !== 'undefined' ? jQuery.noConflict() : undefined;

        // Closure-scoped state (not on window — keeps namespace clean
        // beyond the existing __oremPaymentInfoFired flag).
        this.state = {
            beginCheckoutTs: performance.now(),
            firstInteractionTs: null,
            submitTs: null,
            promptShownTs: null,
            currentStep: 'contact',
            initData: (typeof oremInitCheckoutData !== 'undefined' && oremInitCheckoutData) ? oremInitCheckoutData : null,
            isLoggedIn: 0,
            // Field telemetry maps
            fieldFocusTs: new Map(),           // fieldName → ms timestamp of current focus
            fieldDwellTotal: new Map(),        // fieldName → cumulative ms across all focuses
            fieldInitialValue: new Map(),      // fieldName → value at first focus (for prefilled detection)
            fieldFocusCount: new Map(),        // fieldName → focus cycle count (for focus_thrash)
            fieldErrorRepeat: new Map(),       // (field|msg) → count
        };
        // 4.0.71 — initData key renamed is_logged_in -> logged_in (Utilities.php).
        if (this.state.initData && typeof this.state.initData.logged_in !== 'undefined') {
            this.state.isLoggedIn = this.state.initData.logged_in ? 1 : 0;
        }

        // Idempotency Sets — key construction varies per event family.
        this.fired = {
            steps: new Set(),                  // generic "<step>:<value>" lock
            validationErrors: new Set(),       // "<field>" (dedup by field, 4.0.70)
            fieldStuck: new Set(),             // <field>
        };

        // Last-seen Redux state slices for diffing.
        this.last = {
            paymentMethod: '',
            shippingRateId: '',
            cartItemsHash: '',
            checkoutHasError: false,
            checkoutLastErrorMsg: '',
            validationErrors: {},              // field → message
            noticesHash: '',
            // 4.0.51/53 — submit funnel state for diffCheckoutSubmit
            checkout: { isProc: false, hasErr: false, redirectUrl: '', isComplete: false, succeeded: false },
        };

        this.wireUp();
    }

    /**
     * Emit a tracker event through pixelManager + mirror to Clarity.
     * Centralized so every event automatically gets the Clarity push
     * + the logged_in tag (4.0.71 — was is_logged_in; renamed to match
     * the GA4 custom dimension's registered parameterName).
     */
    emit(eventType, payload, clarityName) {
        const data = Object.assign({ logged_in: this.state.isLoggedIn }, payload || {});
        try {
            pixelManager.sendEvent(eventType, data);
        } catch (e) { /* never break checkout on tracker errors */ }
        if (clarityName && typeof window.clarity === 'function') {
            try { window.clarity('event', clarityName); } catch (e) {}
        }
    }

    /**
     * Helper to emit a CHECKOUT_PROGRESS step with idempotency.
     * stepKey is the dedup token (use '*' to dedup on step code alone).
     */
    emitStep(stepCode, stepKey, payload, clarityNameSuffix) {
        const lock = `${stepCode}:${stepKey}`;
        if (this.fired.steps.has(lock)) return;
        this.fired.steps.add(lock);
        const data = Object.assign({
            checkout_step: String(stepCode),
            plugin: 'orem-add-ons',
        }, payload || {});
        const clarityName = `orem_chkt_step_${stepCode}` + (clarityNameSuffix ? '_' + clarityNameSuffix : '');
        this.emit(EventTypes.CHECKOUT_PROGRESS, data, clarityName);
    }

    /**
     * Force-allow re-fire for a step (e.g. submit retry after fail —
     * step=20 fires again after a step=22 intervened).
     */
    resetStep(stepCode, stepKey) {
        this.fired.steps.delete(`${stepCode}:${stepKey}`);
    }

    /**
     * Time delta helper from begin_checkout (page hydrate).
     */
    msFromBegin() {
        return Math.max(0, Math.round(performance.now() - this.state.beginCheckoutTs));
    }

    /**
     * Wire all listeners. Called once from constructor after gates pass.
     */
    wireUp() {
        // 4.0.51 — wireCartToCheckoutClick moved to a standalone IIFE
        // below because the BlocksCheckoutEventTracker constructor gates
        // on `.wp-block-woocommerce-checkout` existing, which is FALSE
        // on /cart/. So step=9 was never wired before. Now it runs
        // independently on /cart/.
        this.wireStepViewIntersection();
        this.wireFieldFocusBlur();
        this.wirePaymentShippingIntent();
        this.wireAccountExistsPrompt();
        this.wireExpressPaymentClicks();
        this.wirePlaceOrderClarity();
        this.wireBlocksDomEvents();
        this.wireMarketingDataCapture();
        this.wireCheckoutResponseCapture();
        this.wireDataStoreSubscription();
    }

    /* 4.1.18 — what the server answered to the order POST. 269 of the step-22
     * failures in GA4 (Sep 15–Oct 14) arrived as checkout_option='unknown':
     * getCheckoutErrorText() only reads Blocks notices, and a field error on
     * a hidden form (billing with "same as shipping"), a payment_result
     * failure or a 5xx/HTML response (security lockout page) leaves no
     * notice. Blocks posts /wc/store/v1/checkout through the global
     * wp.apiFetch with parse:false, so a middleware sees the raw Response;
     * we read a CLONE (Blocks still reads the original body) and keep only
     * codes, field keys and a message — no address data. */
    wireCheckoutResponseCapture() {
        const af = window.wp && window.wp.apiFetch;
        if (!af || typeof af.use !== 'function') return;
        const isOrderPost = (o) => o && String(o.method || '').toUpperCase() === 'POST'
            && /\/wc\/store(\/v1)?\/checkout(\?|$)/.test(String(o.path || o.url || ''));
        const record = (status, body) => {
            const b = (body && typeof body === 'object') ? body : {};
            const data = (b.data && typeof b.data === 'object') ? b.data : {};
            const pr = (b.payment_result && typeof b.payment_result === 'object') ? b.payment_result : {};
            const details = Array.isArray(pr.payment_details) ? pr.payment_details : [];
            const detailMsg = (details.find(d => d && d.key === 'message') || {}).value || '';
            const strip = (s) => String(s || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 100);
            this.state.lastOrderResponse = {
                http_status: status || 0,
                error_code: String(b.code || ''),
                error_fields: Object.keys(data.params || data.details || {}).join(',').substring(0, 100),
                payment_status: String(pr.payment_status || ''),
                server_message: strip(b.message || detailMsg),
                not_json: body === null,
            };
        };
        const read = (res) => {
            try {
                if (!res || typeof res.clone !== 'function') { record(0, res); return; }
                const status = res.status;
                res.clone().json().then((j) => record(status, j), () => record(status, null));
            } catch (e) {}
        };
        try {
            af.use((options, next) => {
                const p = next(options);
                if (isOrderPost(options) && p && typeof p.then === 'function') {
                    this.state.lastOrderResponse = null;
                    p.then(read, read);
                }
                return p;
            });
        } catch (e) {}
    }

    /* 4.1.18 — compact diagnostics for step 22, built from the server answer
     * above plus the fields Blocks marks invalid at failure time. Goes to
     * `text` (registered GA4 dimension) so 'unknown' failures are queryable. */
    getSubmitFailureDetail() {
        const r = this.state.lastOrderResponse || {};
        let invalid = '';
        try {
            const v = this.selectStore('wc/store/validation');
            const errs = (v && typeof v.getValidationErrors === 'function') ? (v.getValidationErrors() || {}) : {};
            invalid = Object.keys(errs).filter(k => errs[k] && !errs[k].hidden).join(',');
        } catch (e) {}
        const parts = [];
        if (r.http_status) parts.push('http=' + r.http_status);
        if (r.not_json) parts.push('not_json');
        if (r.error_code) parts.push('code=' + r.error_code);
        if (r.error_fields) parts.push('params=' + r.error_fields);
        if (r.payment_status) parts.push('pay=' + r.payment_status);
        if (invalid) parts.push('invalid=' + invalid);
        if (!this.state.lastOrderResponse) parts.push('no_response');
        return {
            text: parts.join('|').substring(0, 100),
            server_message: r.server_message || '',
            error_code: r.error_code || '',
            http_status: r.http_status || 0,
            error_fields: r.error_fields || '',
            payment_status: r.payment_status || '',
            invalid_fields: invalid.substring(0, 100),
        };
    }

    /* ───────────────────── 4.0.55 marketing data capture ─────────────────
     * Capture the GUEST's email + phone + name from wc/store/cart customer
     * data into the mkt_* cookies as they type, so client-side checkout
     * events (add_payment_info, begin_checkout, etc.) include them in
     * orem_user_data → em= → Facebook/TikTok match.
     *
     * Why this is needed: before 4.0.55 the phone/email a guest entered at
     * the Blocks checkout lived only in the React wc/store/cart state. The
     * marketing cookies (mkt_email/mkt_phone) — which getMarketingEmail/
     * getMarketingPhone read, and which feed orem_user_data — were never
     * written. Result: ph=0% / low em to FB+TikTok for the guest majority
     * (confirmed in log 20+21 platform fan-out audit).
     *
     * We write the cookies DIRECTLY (not via oremUserData.setMarketingData)
     * because setMarketingData fires a Brevo /track/identify on every
     * change — we don't want to spam Brevo on each keystroke. Brevo
     * identify still fires on real lead-form submits via forms.js, and on
     * the existing checkout identify flow, unchanged. Idempotent via a
     * last-value cache so we only write when a value actually changes.
     */
    wireMarketingDataCapture() {
        if (typeof setCookieOrem !== 'function') return;
        const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const last = { email: '', phone: '', fn: '', ln: '' };

        const capture = () => {
            try {
                const cart = window.wp.data.select('wc/store/cart');
                if (!cart || typeof cart.getCustomerData !== 'function') return;
                const d = cart.getCustomerData() || {};
                const bill = d.billingAddress || {};
                const ship = d.shippingAddress || {};

                const email = String(bill.email || ship.email || '').trim();
                const phone = String(ship.phone || bill.phone || '').trim();
                const fn = String(ship.first_name || bill.first_name || '').trim();
                const ln = String(ship.last_name || bill.last_name || '').trim();

                // Email — only when it's a valid address and changed.
                if (email && email !== last.email && EMAIL_RE.test(email)) {
                    last.email = email;
                    setCookieOrem('mkt_email', email, 360);
                }
                // Phone — length gate matches setMarketingData (>8) so we
                // don't persist half-typed numbers; getMarketingPhone
                // normalizes (+52 / leading +) on read.
                if (phone && phone !== last.phone && phone.replace(/[^\d]/g, '').length >= 8) {
                    last.phone = phone;
                    setCookieOrem('mkt_phone', phone, 360);
                }
                if (fn && fn !== last.fn) {
                    last.fn = fn;
                    setCookieOrem('mkt_firstName', fn, 360);
                }
                if (ln && ln !== last.ln) {
                    last.ln = ln;
                    setCookieOrem('mkt_lastName', ln, 360);
                }
            } catch (e) { /* never break checkout from a subscriber */ }
        };

        capture(); // initial pass (returning customer with prefilled data)
        try { window.wp.data.subscribe(capture); } catch (e) {}
    }

    /* ───────────────────── §2.2a step=10 step-view IO ────────────────────
     * Emits one event per step block when it scrolls into view. Drives
     * the time_to_first_interaction_ms metric on the FIRST step view
     * (after firstInteractionTs is captured).
     */
    wireStepViewIntersection() {
        const BLOCK_TO_STEP = [
            { selector: '.wp-block-woocommerce-checkout-contact-information-block', step: 'contact',          idx: 1 },
            { selector: '.wp-block-woocommerce-checkout-shipping-address-block',    step: 'shipping_address', idx: 2 },
            { selector: '.wp-block-woocommerce-checkout-billing-address-block',     step: 'billing_address',  idx: 3 },
            { selector: '.wp-block-woocommerce-checkout-shipping-method-block',     step: 'shipping_method',  idx: 4 },
            { selector: '.wp-block-woocommerce-checkout-payment-block',             step: 'payment',          idx: 5 },
            { selector: '.wp-block-woocommerce-checkout-order-review-block',        step: 'order_review',     idx: 6 },
        ];
        if (typeof IntersectionObserver !== 'function') return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const cfg = BLOCK_TO_STEP.find(b => entry.target.matches(b.selector));
                if (!cfg) return;
                this.state.currentStep = cfg.step;
                const payload = {
                    checkout_option: cfg.step,
                    step_name: cfg.step,
                    step_index: cfg.idx,
                    time_from_begin_checkout_ms: this.msFromBegin(),
                };
                if (this.state.firstInteractionTs !== null) {
                    payload.time_to_first_interaction_ms = Math.max(0, Math.round(this.state.firstInteractionTs - this.state.beginCheckoutTs));
                }
                this.emitStep(10, cfg.step, payload, cfg.step);
            });
        }, { threshold: 0.25 });
        BLOCK_TO_STEP.forEach((b) => {
            const node = document.querySelector(b.selector);
            if (node) io.observe(node);
        });
    }

    /* ───────────────────── §2.2b checkout_field_blur + §2.6 prefill ─────
     * Tracks first focus value (for prefilled detection), accumulates
     * dwell across multiple focus periods, emits on blur after ≥250ms
     * of focus. Also feeds the field-stuck (step=96) detector.
     */
    wireFieldFocusBlur() {
        const root = document.querySelector('.wp-block-woocommerce-checkout');
        if (!root) return;

        // 4.0.51 — proper WHATWG autocomplete-token mapping. The old
        // regex `/-(\w+)$/` was capturing trailing fragments (`given-name`
        // → `name`, `postal-code` → `code`, `address-level1` → `level1`,
        // `tel-national` → `national`), producing low-quality cardinality
        // in GA4 reports. This table maps tokens explicitly to a canonical
        // GA4-friendly name shared with the wc/store/validation field
        // identifiers (so `checkout_validation_error` and
        // `checkout_field_blur` use the SAME field_name for the SAME
        // logical field — was bypassing idempotency dedup before).
        const AUTOCOMPLETE_TOKEN_TO_FIELD = {
            'email': 'email',
            'given-name': 'first_name',
            'family-name': 'last_name',
            'organization': 'company',
            'street-address': 'address_1',
            'address-line1': 'address_1',
            'address-line2': 'address_2',
            'address-level1': 'state',
            'address-level2': 'city',
            'country': 'country',
            'country-name': 'country',
            'postal-code': 'postcode',
            'tel': 'phone',
            'tel-national': 'phone',
            'cc-number': 'cc_number',
            'cc-exp': 'cc_exp',
            'cc-exp-month': 'cc_exp',
            'cc-exp-year': 'cc_exp',
            'cc-csc': 'cc_csc',
            'cc-name': 'cc_name',
        };
        const fieldName = (el) => {
            // 1) Try the autocomplete attribute first — WHATWG tokens are
            //    locale-independent and survive WC Blocks copy changes.
            const auto = (el.getAttribute('autocomplete') || '').toLowerCase().trim();
            if (auto) {
                // Autocomplete is space-separated tokens (e.g.,
                // `section-shipping shipping postal-code`). Find the LAST
                // token that matches our table — it's the meaningful one.
                const tokens = auto.split(/\s+/);
                for (let i = tokens.length - 1; i >= 0; i--) {
                    if (AUTOCOMPLETE_TOKEN_TO_FIELD[tokens[i]]) {
                        return AUTOCOMPLETE_TOKEN_TO_FIELD[tokens[i]];
                    }
                }
            }
            // 2) Fallback to name/id/placeholder, normalized: strip the
            //    `shipping_`/`shipping-`/`billing_`/`billing-` prefix that
            //    WC Blocks adds, then collapse separators.
            const raw = (el.name || el.id || el.placeholder || 'unknown').toLowerCase();
            return raw
                .replace(/^(shipping|billing|contact)[-_]/, '')
                .replace(/[-]/g, '_');
        };
        // 4.0.51 — normalize wc/store/validation field identifiers (used
        // for checkout_validation_error) so they collapse to the SAME
        // canonical name as checkout_field_blur. Was a leak before
        // (shipping-state vs shipping_state showed up as two separate
        // fields in GA4 reports despite being the same field).
        this.normalizeValidationFieldName = (key) => {
            return String(key || '')
                .toLowerCase()
                .replace(/^(shipping|billing|contact)[-_]/, '')
                .replace(/[-]/g, '_');
        };

        const onFocus = (ev) => {
            const t = ev.target;
            if (!t || !t.matches || !t.matches('input, textarea, select')) return;
            if (!t.closest('.wp-block-woocommerce-checkout')) return;
            const name = fieldName(t);
            if (this.state.firstInteractionTs === null) {
                this.state.firstInteractionTs = performance.now();
            }
            this.state.fieldFocusTs.set(name, performance.now());
            // First focus → capture initial value for prefilled detection
            if (!this.state.fieldInitialValue.has(name)) {
                this.state.fieldInitialValue.set(name, String(t.value || ''));
            }
            // Track focus cycles for friction detection
            this.state.fieldFocusCount.set(name, (this.state.fieldFocusCount.get(name) || 0) + 1);
        };

        // 4.0.51 — derive step_name from the FIELD's parent block, not
        // from this.state.currentStep. Previously currentStep was set by
        // IntersectionObserver as the user scrolled, so a user typing in
        // the email field (contact block) while having scrolled to the
        // shipping_method block would emit `email | step_name=
        // shipping_method`. Now we look up the field's actual containing
        // block, falling back to currentStep only if the field is in a
        // section outside the standard blocks.
        const STEP_BLOCK_TO_NAME = [
            ['.wp-block-woocommerce-checkout-contact-information-block', 'contact'],
            ['.wp-block-woocommerce-checkout-shipping-address-block', 'shipping_address'],
            ['.wp-block-woocommerce-checkout-billing-address-block', 'billing_address'],
            ['.wp-block-woocommerce-checkout-shipping-method-block', 'shipping_method'],
            ['.wp-block-woocommerce-checkout-payment-block', 'payment'],
            ['.wp-block-woocommerce-checkout-order-review-block', 'order_review'],
        ];
        const stepNameForField = (el) => {
            for (const [sel, name] of STEP_BLOCK_TO_NAME) {
                if (el.closest(sel)) return name;
            }
            return this.state.currentStep || 'unknown';
        };

        const onBlur = (ev) => {
            const t = ev.target;
            if (!t || !t.matches || !t.matches('input, textarea, select')) return;
            if (!t.closest('.wp-block-woocommerce-checkout')) return;
            // 4.0.53 — skip the payment-method radio group. It surfaced as
            // `radio_control_wc_payment_method_options` field_blur noise in
            // log 18 — it's a selection control, not a data-entry field;
            // its selection is already tracked via step=11
            // (payment method selected). Dwell on a radio isn't a friction
            // signal worth a field_blur event.
            if (t.type === 'radio' || t.type === 'checkbox') return;
            const name = fieldName(t);
            const focusTs = this.state.fieldFocusTs.get(name);
            if (typeof focusTs !== 'number') return;
            const dwellThisFocus = Math.max(0, Math.round(performance.now() - focusTs));
            this.state.fieldFocusTs.delete(name);
            if (dwellThisFocus < 250) return;  // Too short, ignore
            const cumulative = (this.state.fieldDwellTotal.get(name) || 0) + dwellThisFocus;
            this.state.fieldDwellTotal.set(name, cumulative);
            const currentValue = String(t.value || '');
            const initial = this.state.fieldInitialValue.get(name) || '';
            const hadValue = currentValue !== '' ? 1 : 0;
            const prefilled = (initial !== '' && currentValue === initial) ? 1 : 0;
            this.emit(EventTypes.CHECKOUT_FIELD_BLUR, {
                field_name: name,
                dwell_ms: dwellThisFocus,
                had_value: hadValue,
                prefilled: prefilled,
                step_name: stepNameForField(t),
            });
            // Field-stuck detector (§2.2a step=96)
            const focusCycles = this.state.fieldFocusCount.get(name) || 0;
            if (!this.fired.fieldStuck.has(name)) {
                if (focusCycles >= 3 && hadValue === 0) {
                    this.fireFieldStuck(name, 'focus_thrash');
                } else if (cumulative >= 30000 && hadValue === 0) {
                    this.fireFieldStuck(name, 'dwell_no_value');
                }
            }
        };

        root.addEventListener('focusin', onFocus, true);
        root.addEventListener('focusout', onBlur, true);
    }

    fireFieldStuck(fieldName, signal) {
        if (this.fired.fieldStuck.has(fieldName)) return;
        this.fired.fieldStuck.add(fieldName);
        this.emitStep(96, fieldName, {
            field_name: fieldName,
            friction_signal: signal,
            step_name: this.state.currentStep,
            checkout_option: signal,
        }, 'field_stuck_' + signal);
    }

    /* ───────────────────── §2.4 account-exists prompt (30/31/32) ─────────
     * Observes the addons-rendered #orem-email-account-hint node and
     * delegates clicks on its login/dismiss buttons. Loose-coupled —
     * the addons code is untouched.
     */
    wireAccountExistsPrompt() {
        const PROMPT_ID = 'orem-email-account-hint';

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((m) => {
                m.addedNodes && m.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) return;
                    const promptNode = (node.id === PROMPT_ID) ? node : (node.querySelector && node.querySelector('#' + PROMPT_ID));
                    if (promptNode) this.onPromptShown(promptNode);
                });
            });
        });
        observer.observe(document.body, { childList: true, subtree: true });

        // Click delegation for the prompt's buttons (single listener).
        document.addEventListener('click', (ev) => {
            const loginBtn = ev.target.closest && ev.target.closest('.orem-email-account-hint__login');
            const dismissBtn = ev.target.closest && ev.target.closest('.orem-email-account-hint__dismiss');
            if (loginBtn) {
                this.onPromptAction('login');
            } else if (dismissBtn) {
                this.onPromptAction('dismiss');
            }
        }, true);
    }

    onPromptShown(promptNode) {
        this.state.promptShownTs = performance.now();
        const emailSpan = promptNode.querySelector('#orem-email-account-hint__email');
        const email = emailSpan ? emailSpan.textContent.trim() : '';
        const dedupKey = email || '*';
        // Different email → different lock so a new prompt fires step=30 again
        if (email) sha256(email).then((hex) => {
            const emailHash = hex.substring(0, 8);
            this.emitStep(30, dedupKey, {
                email_hash: emailHash,
                checkout_option: 'shown',
                time_from_begin_checkout_ms: this.msFromBegin(),
            }, 'account_exists_shown');
        }).catch(() => {
            this.emitStep(30, dedupKey, {
                checkout_option: 'shown',
                time_from_begin_checkout_ms: this.msFromBegin(),
            }, 'account_exists_shown');
        });
    }

    onPromptAction(kind) {
        const dt = this.state.promptShownTs ? Math.max(0, Math.round(performance.now() - this.state.promptShownTs)) : null;
        if (kind === 'login') {
            this.emitStep(31, '*', {
                checkout_option: 'login_clicked',
                time_from_prompt_shown_ms: dt,
            }, 'account_exists_login');
        } else if (kind === 'dismiss') {
            this.emitStep(32, '*', {
                checkout_option: 'guest_clicked',
                time_from_prompt_shown_ms: dt,
            }, 'account_exists_guest');
        }
    }

    /* ───────────────────── §2.5 express payment (step=33) ─────────────────
     * Gateway-agnostic detection: click delegation on the WC Blocks
     * express-payment container blocks. Method derived from
     * data-payment-method-name (preferred) → aria-label → title →
     * 'unknown'. Idempotent per (method, surface).
     */
    wireExpressPaymentClicks() {
        document.addEventListener('click', (ev) => {
            const target = ev.target;
            if (!target || !target.closest) return;
            const cartContainer = target.closest('.wp-block-woocommerce-cart-express-payment-block');
            const checkoutContainer = target.closest('.wp-block-woocommerce-checkout-express-payment-block');
            const container = cartContainer || checkoutContainer;
            if (!container) return;
            const surface = cartContainer ? 'cart' : 'checkout';

            // Derive method (priority: data attr → aria-label → title → unknown)
            let method = '';
            const named = target.closest('[data-payment-method-name]');
            if (named && named.dataset && named.dataset.paymentMethodName) {
                method = named.dataset.paymentMethodName;
            }
            if (!method) {
                const btn = target.closest('button');
                if (btn) {
                    method = btn.getAttribute('aria-label') || btn.getAttribute('title') || '';
                }
            }
            method = (method || 'unknown').toLowerCase().replace(/[^a-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
            const key = method + '|' + surface;
            this.emitStep(33, key, {
                checkout_option: method,
                express_method: method,
                surface: surface,
            }, 'express_' + method);
        }, true);
    }

    /* ───────────────────── §2.10 place order Clarity ──────────────────────
     * Single-purpose click listener (Clarity event only — actual submit
     * tracking lives in wireDataStoreSubscription via the Blocks
     * submit body event).
     */
    wirePlaceOrderClarity() {
        document.addEventListener('click', (ev) => {
            const btn = ev.target.closest && ev.target.closest(
                '.wc-block-components-checkout-place-order-button, button.wc-block-components-button[type="submit"]'
            );
            if (!btn) return;
            if (typeof window.clarity === 'function') {
                try { window.clarity('event', 'chkt_click_comprar'); } catch (e) {}
            }
        }, true);
    }

    /* ───────────────────── §2.1 Blocks body DOM events ────────────────────
     * The wc-blocks-checkout-events module dispatches CustomEvent on
     * document.body for the major lifecycle moments. Cheaper than
     * Redux subscription for these specific signals.
     */
    wireBlocksDomEvents() {
        document.body.addEventListener('experimental__woocommerce_blocks-checkout-submit', () => {
            this.state.submitTs = performance.now();
            // Reset step=20 lock to allow retry after a previous fail
            this.resetStep(20, '*');
            const paymentStore = this.selectStore('wc/store/payment');
            const active = (paymentStore && paymentStore.getActivePaymentMethod) ? paymentStore.getActivePaymentMethod() : '';
            this.emitStep(20, '*', {
                checkout_option: active,
                active_payment_method: active,
                time_from_begin_checkout_ms: this.msFromBegin(),
            }, 'submit_clicked');
            // 4.0.64 — submit is the guaranteed FLOOR for add_payment_info /
            // add_shipping_info (idempotent; no-ops if already fired earlier).
            this.firePaymentInfo(active, 'submit');
        });

        document.body.addEventListener('experimental__woocommerce_blocks-checkout-order-processed', () => {
            const paymentStore = this.selectStore('wc/store/payment');
            const active = (paymentStore && paymentStore.getActivePaymentMethod) ? paymentStore.getActivePaymentMethod() : '';
            this.emitStep(21, '*', {
                checkout_option: active,
                active_payment_method: active,
                time_from_begin_checkout_ms: this.msFromBegin(),
                time_from_submit_ms: this.state.submitTs ? Math.max(0, Math.round(performance.now() - this.state.submitTs)) : null,
            }, 'submit_succeeded');
        });

        document.body.addEventListener('experimental__woocommerce_blocks-checkout-payment-error', (ev) => {
            const msg = (ev.detail && ev.detail.message) ? String(ev.detail.message).substring(0, 100) : '';
            this.emitStep(22, '*', {
                checkout_option: msg,
                error_source: 'payment',
                error_message: msg,
            }, 'submit_failed_payment');
        });

        document.body.addEventListener('experimental__woocommerce_blocks-checkout-show-error-message', (ev) => {
            const msg = (ev.detail && ev.detail.message) ? String(ev.detail.message).substring(0, 100) : '';
            // step=12 (generic error) — separate idempotency per message
            this.emitStep(12, msg, {
                checkout_option: msg,
            }, 'error_shown');
        });

        // 4.0.50 — belt-and-suspenders for the wp.data.subscribe race.
        // WC Blocks emits these CustomEvents on body for every active-
        // payment-method / shipping-rate change. Listening here too
        // means even if our subscribe was registered AFTER the initial
        // Redux dispatch, we still catch the state via the DOM event.
        document.body.addEventListener('experimental__woocommerce_blocks-checkout-set-active-payment-method', () => {
            try { this.diffPaymentMethod(); } catch (e) {}
        });
        document.body.addEventListener('experimental__woocommerce_blocks-checkout-set-selected-shipping-rate', () => {
            try { this.diffShippingRate(); } catch (e) {}
        });
    }

    /* ───────────────────── §2.1 wp.data.subscribe() — fine-grained ────────
     * Subscribes for live diffs, plus an INITIAL synchronous tick (4.0.50
     * fix) so state that WC Blocks already populated BEFORE our subscribe
     * gets captured — without the initial tick, our diff
     * never fires for a stable checkout where the default gateway and
     * shipping rate were set during React hydration BEFORE window.load.
     *
     * Additionally polls 5× over the first 5 s as a safety net for
     * async-late initializations (e.g. shipping rates that depend on
     * geo-resolved address auto-fill after page paint).
     */
    wireDataStoreSubscription() {
        const tick = () => {
            try { this.diffPaymentMethod(); } catch (e) {}
            try { this.diffShippingRate();  } catch (e) {}
            try { this.diffCartItems();     } catch (e) {}
            try { this.diffNoShipping();    } catch (e) {}
            try { this.diffValidationErrors(); } catch (e) {}
            try { this.diffNotices();       } catch (e) {}
            try { this.diffCheckoutSubmit(); } catch (e) {}
        };
        // 1. Synchronous initial tick — captures state already set
        //    during Blocks hydration before our subscribe registers.
        tick();
        // 2. Subscribe for subsequent Redux dispatches.
        try { window.wp.data.subscribe(tick); } catch (e) {}
        // 3. Polling safety net — catches async-late stores (5 × 1 s).
        let polls = 0;
        const pollId = setInterval(() => {
            tick();
            if (++polls >= 5) clearInterval(pollId);
        }, 1000);
    }

    selectStore(key) {
        try { return window.wp.data.select(key); } catch (e) { return null; }
    }

    diffPaymentMethod() {
        const store = this.selectStore('wc/store/payment');
        if (!store || typeof store.getActivePaymentMethod !== 'function') return;
        const current = store.getActivePaymentMethod() || '';
        if (current === this.last.paymentMethod) return;
        // 4.0.64 — the FIRST non-empty active method observed is the WC
        // Blocks hydration AUTO-DEFAULT, not a user choice. `_paymentBaselined`
        // marks that we've absorbed it. This is the root-cause fix: since the
        // 4.0.50 synchronous initial tick, the pre-4.0.64 code fired
        // add_payment_info on this auto-default, making add_payment_info fire
        // 1:1 with begin_checkout (the funnel's payment step was a mirror of
        // begin_checkout — confirmed in GA4 weekly data from ~wk21). Same
        // pattern as diffCheckoutSubmit's `_checkoutBaselined` and
        // diffShippingRate's `was_auto_default`.
        const wasBaseline = !this._paymentBaselined;
        this._paymentBaselined = true;
        this.last.paymentMethod = current;
        if (!current) return;

        // step=11 (diagnostic) fires on EVERY change incl. the auto-default,
        // tagged with was_auto_default so analysts can tell them apart.
        this.emitStep(11, current, {
            checkout_option: current,
            active_payment_method: current,
            was_auto_default: wasBaseline ? 1 : 0,
        }, 'payment_method_' + current);

        // Only a POST-baseline change is a genuine user selection of payment.
        // The floor (payment-block interaction / submit) is covered by
        // wirePaymentShippingIntent + the submit handlers.
        if (!wasBaseline) {
            this.firePaymentInfo(current, 'method_change');
        }
    }

    /* 4.0.64 — single choke-point for the GA4 `add_payment_info` event.
     * Fires at most once per page (window.__oremPaymentInfoFired preserved
     * for sGTM rule compatibility). Ensures add_shipping_info precedes it so
     * the funnel stays monotonic. `trigger` records what fired it
     * (method_change / payment_interaction / submit) for diagnostics. */
    firePaymentInfo(paymentType, trigger) {
        if (window.__oremPaymentInfoFired) return;
        if (!this.state.initData) return; // need items[] for the itemRequired gate
        // Reaching payment implies shipping was accepted — keep funnel order.
        this.fireShippingInfo('payment_reached');
        let savedMethodUsed = 0;
        try {
            const store = this.selectStore('wc/store/payment');
            const method = paymentType || (store && store.getActivePaymentMethod && store.getActivePaymentMethod()) || '';
            const saved = (store && store.getSavedPaymentMethods && store.getSavedPaymentMethods()) || {};
            const token = (store && store.getActiveSavedToken && store.getActiveSavedToken()) || '';
            if (token) {
                savedMethodUsed = 1;
            } else if (saved && typeof saved === 'object') {
                Object.keys(saved).forEach((gw) => {
                    const list = saved[gw];
                    if (Array.isArray(list) && list.some(m => m.method && m.method.gateway === method)) {
                        savedMethodUsed = 1;
                    }
                });
            }
            paymentType = method;
        } catch (e) {}
        this.emit(EventTypes.ADD_PAYMENT_INFO, Object.assign(
            {}, this.state.initData, {
                payment_type: paymentType || '',
                saved_method_used: savedMethodUsed,
                trigger: trigger || '',
            }
        ));
        window.__oremPaymentInfoFired = true;
    }

    /* 4.0.64 — single choke-point for the GA4 `add_shipping_info` event.
     * Fires at most once per page. Carries the begin_checkout items[] (so it
     * passes the itemRequired gate) plus the selected shipping tier/rate when
     * known. `trigger`: rate_change / shipping_interaction / payment_reached. */
    fireShippingInfo(trigger) {
        if (window.__oremShippingInfoFired) return;
        if (!this.state.initData) return;
        window.__oremShippingInfoFired = true;
        const payload = Object.assign({}, this.state.initData, {
            trigger: trigger || '',
        });
        if (this.last.shippingRateId) {
            payload.rate_id = this.last.shippingRateId;
            if (this._lastShippingTier) payload.shipping_tier = this._lastShippingTier;
        }
        this.emit(EventTypes.ADD_SHIPPING_INFO, payload);
    }

    /* 4.0.64 — floor/engagement signals for add_payment_info + add_shipping_info.
     * The auto-default baseline (above) means neither event fires on hydration,
     * so we need genuine-interaction triggers that also work for single-gateway
     * / single-rate carts (where diffPaymentMethod/diffShippingRate never see a
     * post-baseline CHANGE). A click or focus inside the payment/shipping block
     * is such a signal. Stripe card fields live in cross-origin iframes whose
     * focus doesn't bubble here — but the surrounding radio/label clicks do, and
     * the submit handlers guarantee the floor for everyone who places an order. */
    wirePaymentShippingIntent() {
        const PAYMENT_SEL = '.wp-block-woocommerce-checkout-payment-block';
        const SHIPPING_SEL = '.wp-block-woocommerce-checkout-shipping-method-block';
        const onIntent = (ev) => {
            const t = ev.target;
            if (!t || !t.closest) return;
            if (t.closest(PAYMENT_SEL)) {
                const store = this.selectStore('wc/store/payment');
                const active = (store && store.getActivePaymentMethod) ? store.getActivePaymentMethod() : '';
                this.firePaymentInfo(active, 'payment_interaction');
            } else if (t.closest(SHIPPING_SEL)) {
                this.fireShippingInfo('shipping_interaction');
            }
        };
        document.addEventListener('click', onIntent, true);
        document.addEventListener('focusin', onIntent, true);
    }

    diffShippingRate() {
        const cartStore = this.selectStore('wc/store/cart');
        if (!cartStore || typeof cartStore.getShippingRates !== 'function') return;
        const groups = cartStore.getShippingRates() || [];
        let selected = null;
        let availableCount = 0;
        for (let g = 0; g < groups.length; g++) {
            const rates = (groups[g] && groups[g].shipping_rates) || [];
            availableCount += rates.length;
            for (let r = 0; r < rates.length; r++) {
                if (rates[r].selected) { selected = rates[r]; break; }
            }
            if (selected) break;
        }
        if (!selected) return;
        const rateId = selected.rate_id || '';
        if (rateId === this.last.shippingRateId) return;
        const wasAutoDefault = this.last.shippingRateId === '' ? 1 : 0;
        this.last.shippingRateId = rateId;
        // 4.1.20 — Store API prices are integers in the currency's minor unit,
        // and orem's MXN is configured with 0 decimals (`currency_minor_unit: 0`):
        // a hardcoded /100 shipped a $103 rate as rate_cost=1.03 (log 65).
        const rateCost = Number(selected.price || 0) / Math.pow(10, Number(selected.currency_minor_unit || 0));
        const shippingTier = rateCost === 0 ? 'free' : 'paid';
        this._lastShippingTier = shippingTier;
        this.emitStep(13, rateId, {
            checkout_option: rateId,
            rate_id: rateId,
            rate_label: String(selected.name || '').substring(0, 60),
            rate_cost: rateCost,
            shipping_tier: shippingTier,
            was_auto_default: wasAutoDefault,
            available_rates_count: availableCount,
        }, 'shipping_' + shippingTier);

        // 4.0.64 — a user-initiated rate change (NOT the hydration
        // auto-default) is a genuine add_shipping_info. Auto-default/single
        // rate is covered by the shipping-block interaction + payment_reached
        // floor in wirePaymentShippingIntent / firePaymentInfo.
        if (!wasAutoDefault) {
            this.fireShippingInfo('rate_change');
        }
    }

    diffCartItems() {
        const cartStore = this.selectStore('wc/store/cart');
        if (!cartStore || typeof cartStore.getCartData !== 'function') return;
        const items = (cartStore.getCartData().items || []);
        const hash = items.map(i => i.key + ':' + i.quantity).join('|');
        if (this.last.cartItemsHash === '') {
            this.last.cartItemsHash = hash;
            return;
        }
        if (hash === this.last.cartItemsHash) return;
        // Cart changed mid-checkout → step=17. Best-effort change_type
        // detection by comparing item keys + quantities.
        const prev = this.last.cartItemsHash.split('|').filter(Boolean);
        const next = hash.split('|').filter(Boolean);
        let changeType = 'qty_changed';
        if (next.length > prev.length) changeType = 'item_added';
        else if (next.length < prev.length) changeType = 'item_removed';
        this.last.cartItemsHash = hash;
        // Emit per-diff (allow re-fire on each subsequent modification)
        const lockKey = changeType + ':' + hash;
        this.emitStep(17, lockKey, {
            checkout_option: changeType,
            change_type: changeType,
        }, 'cart_modified_' + changeType);
    }

    diffNoShipping() {
        const cartStore = this.selectStore('wc/store/cart');
        if (!cartStore) return;
        const rates = cartStore.getShippingRates ? cartStore.getShippingRates() : [];
        if (!Array.isArray(rates) || rates.length === 0) return;
        const totalRates = rates.reduce((n, g) => n + ((g.shipping_rates || []).length), 0);
        const customer = cartStore.getCustomerData ? cartStore.getCustomerData() : null;
        const ship = (customer && customer.shippingAddress) || {};
        if (totalRates === 0 && (ship.postcode || ship.country)) {
            this.emitStep(99, ship.postcode || '*', {
                checkout_option: 'no_shipping_rates',
                event_label: ship.state || '',
                text: ship.postcode || '',
            }, 'no_shipping');
        }
    }

    diffValidationErrors() {
        const vStore = this.selectStore('wc/store/validation');
        if (!vStore || typeof vStore.getValidationErrors !== 'function') return;
        const errors = vStore.getValidationErrors() || {};
        Object.keys(errors).forEach((rawField) => {
            const err = errors[rawField];
            const msg = (err && err.message) ? String(err.message).substring(0, 100) : '';
            if (!msg) return;
            // 4.1.17 — skip hidden errors. Blocks registers a hidden error for
            // every empty required field at mount (measured live 2026-10-16: 8
            // per checkout view, before the shopper types anything), so every
            // page load sent 8 checkout_validation_error events. A hidden error
            // only becomes a real one when Blocks shows it (blur or a submit
            // attempt); the store changes then, this diff runs again, and the
            // field is not in `fired` yet, so it is still reported once.
            if (err.hidden) return;
            // 4.0.51 — normalize the field key to the same canonical name
            // checkout_field_blur uses. The validation store returns both
            // `shipping-state` and `shipping_state` for the same logical
            // field (different code paths) — without normalization GA4
            // saw them as two separate fields and idempotency leaked.
            const field = (this.normalizeValidationFieldName || ((x) => x))(rawField);
            // 4.0.70 — dedup by FIELD only (was field+'|'+msg). The message can
            // now change AFTER registration: addons 1.1.132 rewrites the gated
            // Estado/Ciudad/Colonia messages to Spanish/CP-redirect, and under a
            // field|msg key that rewrite (English → Spanish) minted a SECOND
            // checkout_validation_error for the same field per page load,
            // inflating the metric. Field-only dedup captures the first message
            // per field and ignores later cosmetic message churn — field_name is
            // the analytic signal; `message` stays a first-occurrence attribute.
            if (this.fired.validationErrors.has(field)) return;
            this.fired.validationErrors.add(field);
            this.emit(EventTypes.CHECKOUT_VALIDATION_ERROR, {
                field_name: field,
                message: msg,
                // step_name is best-effort here — we don't have a DOM
                // element to look up the parent block, so we fall back to
                // currentStep (the user's last-visible step). Validation
                // errors usually surface ON the field the user just
                // touched, so currentStep is typically right.
                step_name: this.state.currentStep,
            });
            try { window.clarity && window.clarity('event', 'orem_chkt_validation_' + field); } catch (e) {}
            // Field-stuck via validation repeat
            const cnt = (this.state.fieldErrorRepeat.get(field) || 0) + 1;
            this.state.fieldErrorRepeat.set(field, cnt);
            if (cnt >= 3 && !this.fired.fieldStuck.has(field)) {
                this.fireFieldStuck(field, 'validation_repeat');
            }
        });
    }

    /* 4.0.51 — submit funnel via wp.data state (NOT body DOM events). The
     * body events `experimental__woocommerce_blocks-checkout-submit` /
     * `-order-processed` / `-payment-error` that the plan §2.1 mentioned
     * don't fire in current WC Blocks versions (confirmed in log 16+17:
     * 3 server-side purchases, 0 step=20/21/22 events). Robust solution:
     * subscribe to `wc/store/checkout` state and detect transitions in
     * the canonical selectors that ALL WC Blocks versions expose:
     *
     *   isCalculating(), isComplete(), hasError(), getOrderId(),
     *   getCustomerPassword() (irrelevant), getRedirectUrl()
     *
     * Transitions of interest:
     *   - step=20 (submit clicked):   isCalculating() flips false→true
     *   - step=21 (submit succeeded): getOrderId() becomes non-zero AND
     *                                 hasError() is false
     *   - step=22 (submit failed):    hasError() flips false→true after
     *                                 a submit (isCalculating was true)
     */
    diffCheckoutSubmit() {
        const ck = this.selectStore('wc/store/checkout');
        if (!ck) return;
        const isProc = (typeof ck.isProcessing === 'function') ? !!ck.isProcessing()
                      : (typeof ck.isCalculating === 'function') ? !!ck.isCalculating()
                      : false;
        const hasErr = (typeof ck.hasError === 'function') ? !!ck.hasError() : false;
        // 4.0.53 — success is detected via getRedirectUrl() / isComplete(),
        // NOT getOrderId(). Confirmed live on prod: WC Blocks + Stripe
        // pre-create the order id during hydration (getOrderId()=321381 on
        // idle load), so there is NO 0→positive transition at real
        // completion — the 4.0.52 baseline guard correctly killed the
        // phantom step=21 but ALSO killed the real one (log 18: 2 purchases,
        // 0 step=21). getRedirectUrl() is empty on idle and gets set ONLY
        // when the order succeeds and the checkout is about to navigate to
        // the thank-you page — a clean empty→non-empty transition that
        // fires exactly once on real success.
        const redirectUrl = (typeof ck.getRedirectUrl === 'function') ? String(ck.getRedirectUrl() || '') : '';
        const isComplete = (typeof ck.isComplete === 'function') ? !!ck.isComplete() : false;

        // 4.0.52 — baseline the FIRST observation without emitting any
        // transition. Seed `last` from the initial state and bail; real
        // transitions are only meaningful AFTER this baseline (orderId was
        // pre-set, redirectUrl empty, etc.).
        if (!this._checkoutBaselined) {
            this._checkoutBaselined = true;
            this.last.checkout = { isProc, hasErr, redirectUrl, isComplete, succeeded: false };
            return;
        }

        const last = this.last.checkout || { isProc: false, hasErr: false, redirectUrl: '', isComplete: false, succeeded: false };

        const payment = this.selectStore('wc/store/payment');
        const activeMethod = (payment && typeof payment.getActivePaymentMethod === 'function')
            ? payment.getActivePaymentMethod() : '';

        // step=20 (submit clicked): processing flag transitions true.
        if (isProc && !last.isProc) {
            this.state.submitTs = performance.now();
            this.resetStep(20, '*');
            // 4.0.66 — resetear también el lock del step 22: cada intento de
            // submit que falle debe registrar SU propio fallo (antes solo se
            // contaba el primero por página).
            this.resetStep(22, '*');
            this.emitStep(20, '*', {
                checkout_option: activeMethod,
                active_payment_method: activeMethod,
                time_from_begin_checkout_ms: this.msFromBegin(),
            }, 'submit_clicked');
            // 4.0.64 — submit floor for add_payment_info / add_shipping_info.
            this.firePaymentInfo(activeMethod, 'submit');
        }
        // step=21 (succeeded): redirectUrl becomes non-empty OR isComplete
        // flips true. Either signals the order went through. Guard with a
        // `succeeded` latch so we emit at most once per page lifetime even
        // if both signals fire on consecutive ticks.
        const justSucceeded = (!last.succeeded) && (
            (redirectUrl !== '' && last.redirectUrl === '') ||
            (isComplete && !last.isComplete)
        );
        let succeeded = last.succeeded;
        if (justSucceeded) {
            succeeded = true;
            this.emitStep(21, '*', {
                checkout_option: activeMethod,
                active_payment_method: activeMethod,
                time_from_begin_checkout_ms: this.msFromBegin(),
                time_from_submit_ms: this.state.submitTs
                    ? Math.max(0, Math.round(performance.now() - this.state.submitTs))
                    : null,
            }, 'submit_succeeded');
        }
        // step=22 (failed): hasError flips true after a submit.
        if (hasErr && !last.hasErr && last.isProc) {
            // 4.0.66 — mensaje real del error. getCheckoutErrors() (4.0.51) ya
            // no existe en WC Blocks actual → los 5 fallos de la semana llegaron
            // con checkout_option='unknown' (confirmado en GA4). El texto vive
            // en core/notices (contextos wc/checkout*). El notice puede
            // registrarse unos ms DESPUÉS del flip de hasError (batch de
            // React): si aún no hay texto, reintenta una vez a 450ms.
            const emitFail = (msg) => {
                // 4.1.18 — when Blocks shows no notice, fall back to what the
                // server said; `text` always carries the diagnostics.
                const d = this.getSubmitFailureDetail();
                const option = msg || d.server_message || (d.error_code ? 'code:' + d.error_code : '')
                    || (d.payment_status ? 'pay:' + d.payment_status : '') || 'unknown';
                this.emitStep(22, '*', {
                    checkout_option: option,
                    error_source: 'submit',
                    error_message: msg || d.server_message,
                    active_payment_method: activeMethod,
                    text: d.text,
                    error_code: d.error_code,
                    http_status: d.http_status,
                    error_fields: d.error_fields,
                    payment_status: d.payment_status,
                    invalid_fields: d.invalid_fields,
                    time_from_submit_ms: this.state.submitTs
                        ? Math.max(0, Math.round(performance.now() - this.state.submitTs))
                        : null,
                }, 'submit_failed');
            };
            const errMsg = this.getCheckoutErrorText();
            if (errMsg) {
                emitFail(errMsg);
            } else {
                setTimeout(() => emitFail(this.getCheckoutErrorText()), 450);
            }
        }

        this.last.checkout = { isProc, hasErr, redirectUrl, isComplete, succeeded };
    }

    /* 4.0.66 — lee el texto real del error visible del checkout.
     * Prioridad: core/notices (WC Blocks actual registra ahí, con contextos
     * wc/checkout*) → wc/store/notices (legacy) → banner en DOM. Devuelve ''
     * si no hay error registrado aún. */
    getCheckoutErrorText() {
        try {
            const core = window.wp.data.select('core/notices');
            if (core && typeof core.getNotices === 'function') {
                for (let i = 0; i < CHECKOUT_NOTICE_CONTEXTS.length; i++) {
                    const errs = (core.getNotices(CHECKOUT_NOTICE_CONTEXTS[i]) || []).filter(n => n.status === 'error' && n.content);
                    if (errs.length) {
                        return String(errs[errs.length - 1].content).replace(/<[^>]*>/g, '').trim().substring(0, 100);
                    }
                }
            }
        } catch (e) {}
        try {
            const legacy = this.selectStore('wc/store/notices');
            if (legacy && typeof legacy.getNotices === 'function') {
                const errs = (legacy.getNotices('wc/checkout') || []).filter(n => (n.status === 'error' || n.type === 'error') && n.content);
                if (errs.length) return String(errs[errs.length - 1].content).replace(/<[^>]*>/g, '').trim().substring(0, 100);
            }
        } catch (e) {}
        try {
            const el = document.querySelector('.wc-block-components-notice-banner.is-error');
            if (el) return el.textContent.trim().substring(0, 100);
        } catch (e) {}
        return '';
    }

    diffNotices() {
        // 4.0.72 — recorre TODOS los contextos de CHECKOUT_NOTICE_CONTEXTS,
        // no solo 'wc/checkout'. El formulario de cupón (cart Y checkout)
        // registra su error bajo 'wc/cart' — por eso coupon_failed llevaba
        // 0 eventos incluso después del fix de 4.0.66 (que solo tocó
        // getCheckoutErrorText(), no esta función). wc/store/notices queda
        // como fallback legacy si core/notices no tiene nada aún.
        let allErrors = [];
        try {
            const core = window.wp.data.select('core/notices');
            if (core && typeof core.getNotices === 'function') {
                for (let i = 0; i < CHECKOUT_NOTICE_CONTEXTS.length; i++) {
                    const errs = (core.getNotices(CHECKOUT_NOTICE_CONTEXTS[i]) || []).filter(n => n.status === 'error');
                    if (errs.length) allErrors = allErrors.concat(errs);
                }
            }
        } catch (e) {}
        if (!allErrors.length) {
            const noticesStore = this.selectStore('wc/store/notices');
            if (noticesStore && typeof noticesStore.getNotices === 'function') {
                try {
                    for (let i = 0; i < CHECKOUT_NOTICE_CONTEXTS.length; i++) {
                        const ctx = noticesStore.getNotices(CHECKOUT_NOTICE_CONTEXTS[i]) || [];
                        const errs = ctx.filter(n => n.status === 'error' || n.type === 'error');
                        if (errs.length) allErrors = allErrors.concat(errs);
                    }
                } catch (e) { return; }
            }
        }
        if (!allErrors.length) return;
        const hash = allErrors.map(n => (n.id || '') + ':' + (n.content || '').substring(0, 40)).join('|');
        if (hash === this.last.noticesHash) return;
        this.last.noticesHash = hash;
        allErrors.forEach((n) => {
            const content = String(n.content || '').substring(0, 100);
            // Coupon failure detection — content typically mentions "cupón" / "coupon" / "código"
            if (/cup[oó]n|coupon|c[oó]digo/i.test(content)) {
                this.emitStep(16, content, {
                    checkout_option: content,
                }, 'coupon_failed');
            }
        });
    }
}

// 4.0.72 — coupon applied/removed/partial via wp.data diff on wc/store/cart.
//
// REEMPLAZA la implementación anterior, que escuchaba
// `wc-blocks_applied_coupon` / `wc-blocks_removed_coupon` como DOM
// CustomEvents en `document`. Confirmado por grep directo del bundle
// distribuido de WC Blocks (wc-cart-checkout-base-frontend.js): esos DOS
// nombres de evento NO EXISTEN — WC Blocks solo despacha
// `wc-blocks_added_to_cart`, `wc-blocks_removed_from_cart`,
// `wc-blocks_render_blocks_frontend` y
// `wc-blocks_dismissed_incompatible_extensions_notices`. El listener estaba
// escuchando un evento fantasma — es la causa real de que los steps 14/15/16
// llevaran 0 eventos en 90 días (no un problema de contextos de notice, que
// era la otra mitad ya arreglada arriba en diffNotices()/CHECKOUT_NOTICE_CONTEXTS).
//
// Standalone (no depende de BlocksCheckoutEventTracker, que se gatea a
// `.wp-block-woocommerce-checkout` y por tanto nunca corre en /cart/ —
// mismo patrón que attachCartToCheckoutIntent más abajo): así corre en
// /cart/ Y /checkout/, que es donde de verdad se escriben los códigos.
//
// Además emite el step 18 (coupon_partial): el servidor (addons,
// class-wc-coupons-customizations.php) expone en
// extensions.orem-coupons.partial_discount cuándo un cupón de cliente
// aplicó a SOLO PARTE del carrito — WooCommerce no dispara ningún error en
// ese caso, así que sin esto era invisible para GA4.
(function attachCouponDiffTracker() {
    if (typeof pixelManager === 'undefined' || typeof EventTypes === 'undefined') return;
    if (!window.wp || !window.wp.data || typeof window.wp.data.subscribe !== 'function') return;

    // Cupones de SISTEMA (auto-aplicados, no tecleados por la clienta) — no
    // cuentan como "aplicado/removido" por acción suya. Duplicado a propósito
    // (no importado) de WC_Orem_Single_Coupon::is_system_coupon_code() /
    // single-coupon-blocks.js — es telemetría en OTRO plugin, y esta lista es
    // sencilla y estable; una dependencia cruzada de plugins para esto no vale
    // la pena. Mantener en minúsculas si se agrega algo.
    var SYSTEM_COUPON_CODES = ['_orem_descuentos_auto'];
    var SYSTEM_COUPON_PREFIXES = ['rewards_'];
    function isSystemCoupon(code) {
        var c = String(code || '').toLowerCase();
        if (SYSTEM_COUPON_CODES.indexOf(c) !== -1) return true;
        for (var i = 0; i < SYSTEM_COUPON_PREFIXES.length; i++) {
            if (c.indexOf(SYSTEM_COUPON_PREFIXES[i]) === 0) return true;
        }
        return false;
    }

    var pageContext = document.querySelector('.wp-block-woocommerce-checkout') ? 'checkout'
        : (document.querySelector('.wp-block-woocommerce-cart') ? 'cart' : 'other');

    var lastCodes = null;   // null = aún no inicializado — evita disparar "aplicado" con la hidratación inicial de la página.
    var lastPartialKey = '';

    function dispatchCouponStep(step, code) {
        try {
            pixelManager.sendEvent(EventTypes.CHECKOUT_PROGRESS, {
                checkout_step: step,
                checkout_option: code,
                coupon_code: code,
                page_context: pageContext,
                plugin: 'orem-add-ons',
            });
            if (typeof window.clarity === 'function') {
                try { window.clarity('event', 'orem_chkt_coupon_' + (step === '14' ? 'applied' : 'removed')); } catch (e) {}
            }
        } catch (e) {}
    }

    function sync() {
        try {
            var store = window.wp.data.select('wc/store/cart');
            if (!store || typeof store.getCartData !== 'function') return;
            var data = store.getCartData();
            if (!data) return;

            var codes = (Array.isArray(data.coupons) ? data.coupons : [])
                .map(function (c) { return c && c.code; })
                .filter(function (c) { return c && !isSystemCoupon(c); });

            if (lastCodes === null) {
                lastCodes = codes; // línea base — no es "aplicado", es el estado con el que llegó.
            } else {
                var added = codes.filter(function (c) { return lastCodes.indexOf(c) === -1; });
                var removed = lastCodes.filter(function (c) { return codes.indexOf(c) === -1; });
                added.forEach(function (code) { dispatchCouponStep('14', code); });
                removed.forEach(function (code) { dispatchCouponStep('15', code); });
                lastCodes = codes;
            }

            // Step 18 — aviso de descuento parcial (Fase 3/4, addons).
            var ext = data.extensions && data.extensions['orem-coupons'];
            var partial = ext && ext.partial_discount;
            var msg = partial && partial.message;
            if (msg) {
                var key = '18:' + (partial.coupon || '') + ':' + (partial.excluded || 0) + ':' + (partial.reason || '');
                if (key !== lastPartialKey) {
                    lastPartialKey = key;
                    try {
                        pixelManager.sendEvent(EventTypes.CHECKOUT_PROGRESS, {
                            checkout_step: '18',
                            checkout_option: 'coupon_partial',
                            coupon_code: partial.coupon || '',
                            partial_reason: partial.reason || '',
                            excluded_items: partial.excluded || 0,
                            page_context: pageContext,
                            plugin: 'orem-add-ons',
                        });
                        if (typeof window.clarity === 'function') {
                            try { window.clarity('event', 'orem_chkt_step_18_coupon_partial'); } catch (e) {}
                        }
                    } catch (e) {}
                }
            } else if (lastPartialKey) {
                lastPartialKey = ''; // el aviso desapareció (quitó el ítem/cupón) — la próxima vez que reaparezca, re-dispara.
            }
        } catch (e) {}
    }

    sync();
    window.wp.data.subscribe(sync);
})();

// 4.0.51 — standalone cart→checkout intent tracker (step=9). Runs
// independently of BlocksCheckoutEventTracker because that class gates
// on the checkout block being present, which is FALSE on /cart/ where
// this click happens. Loose-coupled — only depends on pixelManager and
// EventTypes from the rest of the bundle.

// Step 9's cart_value from the Store API cart totals — the SAME basis as
// begin_checkout's value (Utilities::format_cart_for_initiate_checkout:
// contents total + contents tax): with IVA, AFTER discounts, shipping and
// fees out. Store API amounts are integers in the currency's minor unit
// (MXN here = 0 decimals). 4.1.20 used total_items + total_items_tax, which
// is BEFORE discounts: verified 2026-10-24, a $249 item under the automatic
// 20% coupon sent cart_value 249 while begin_checkout said 199.
function oremCartValueFromTotals(totals) {
    if (!totals || !Number(totals.total_items || 0)) return null; // amounts are strings: '0' is truthy
    const n = (k) => Number(totals[k] || 0);
    const minor = Math.pow(10, n('currency_minor_unit'));
    const value = (n('total_items') + n('total_items_tax') - n('total_discount') - n('total_discount_tax')) / minor;
    return Math.round(Math.max(0, value) * 100) / 100;
}

(function attachCartToCheckoutIntent() {
    if (typeof pixelManager === 'undefined' || typeof EventTypes === 'undefined') return;
    document.addEventListener('click', function (ev) {
        const target = ev.target && ev.target.closest && ev.target.closest(
            '.wp-block-woocommerce-proceed-to-checkout-block button, a.wc-block-cart__submit-button, .wc-block-cart__submit-button, .wp-block-woocommerce-proceed-to-checkout-block a'
        );
        if (!target) return;
        let cart_value = null;
        let cart_items_count = 0;
        let logged_in = 0; // 4.0.71 — was is_logged_in, renamed to match the GA4 dimension
        try {
            const cartStore = window.wp && window.wp.data && window.wp.data.select && window.wp.data.select('wc/store/cart');
            if (cartStore && cartStore.getCartTotals) {
                cart_value = oremCartValueFromTotals(cartStore.getCartTotals());
            }
            if (cartStore && cartStore.getCartData) {
                const items = cartStore.getCartData().items || [];
                cart_items_count = items.reduce(function (n, i) { return n + (i.quantity || 0); }, 0);
            }
        } catch (e) {}
        try {
            if (typeof orem_pixel_vars !== 'undefined' && orem_pixel_vars.isAdmin !== undefined) {
                // Best-effort logged-in signal: orem_pixel_vars carries isAdmin
                // but not a generic is_user_logged_in. Cart Store API customer
                // data presence is a stronger proxy.
                const customer = window.wp && window.wp.data && window.wp.data.select &&
                    window.wp.data.select('wc/store/cart') &&
                    window.wp.data.select('wc/store/cart').getCustomerData &&
                    window.wp.data.select('wc/store/cart').getCustomerData();
                if (customer && customer.billingAddress && customer.billingAddress.email) {
                    // Email known doesn't guarantee logged-in, but is the best
                    // proxy available outside the tracker class scope.
                    logged_in = 0;  // unknown without server context, default 0
                }
            }
        } catch (e) {}
        try {
            pixelManager.sendEvent(EventTypes.CHECKOUT_PROGRESS, {
                checkout_step: '9',
                checkout_option: 'cart_to_checkout',
                cart_value: cart_value,
                cart_items_count: cart_items_count,
                logged_in: logged_in,
                plugin: 'orem-add-ons',
            });
        } catch (e) {}
        if (typeof window.clarity === 'function') {
            try { window.clarity('event', 'orem_chkt_step_9_cart_to_checkout'); } catch (e) {}
        }
    }, true);
})();

// 4.0.61 — address autocomplete tracking. The addons Google-Places
// autocomplete (checkout-address-autocomplete.js) emits two loose-coupled
// CustomEvents on document.body; we turn them into tracked GA4 events +
// Clarity mirrors here, so all tracking stays in core. PII-safe: only the
// corrected FIELD + coarse geo (cp/estado) + the value's SOURCE travel — never
// the street address.
//   orem-address-autocomplete-used       → adoption (does it help conversion?)
//   orem-address-autocomplete-corrected  → quality: customer fixed a field
//                                           Google filled (where MX data fails)
(function attachAddressAutocompleteEvents() {
    if (typeof pixelManager === 'undefined' || typeof EventTypes === 'undefined') return;
    document.body.addEventListener('orem-address-autocomplete-used', function (ev) {
        const d = (ev && ev.detail) || {};
        try {
            pixelManager.sendEvent(EventTypes.ADDRESS_AUTOCOMPLETE_USED, {
                address_type: d.address_type || 'shipping',
                plugin: 'orem-add-ons',
            });
        } catch (e) {}
        if (typeof window.clarity === 'function') {
            try { window.clarity('event', 'orem_chkt_autocomplete_used'); } catch (e) {}
        }
    });
    document.body.addEventListener('orem-address-autocomplete-corrected', function (ev) {
        const d = (ev && ev.detail) || {};
        try {
            pixelManager.sendEvent(EventTypes.ADDRESS_AUTOCOMPLETE_CORRECTED, {
                field_corrected: d.field || 'unknown',
                cp: d.cp || '',
                estado: d.estado || '',
                original_source: d.original_source || '',
                address_type: d.address_type || 'shipping',
                plugin: 'orem-add-ons',
            });
        } catch (e) {}
        if (typeof window.clarity === 'function') {
            try { window.clarity('event', 'orem_chkt_autocomplete_corrected_' + (d.field || 'x')); } catch (e) {}
        }
    });
})();

// 4.1.9 — "Mis direcciones" (addons 1.1.214) address-book signals. The checkout block
// emits loose-coupled CustomEvents on document.body (same contract as the autocomplete
// above); we turn them into GA4-only custom events + Clarity mirrors here, so all
// tracking stays in core. Dispatched with sendCustom, NOT sendEvent: an event with no
// `eventMapping` entry is silently dropped by sendEvent (WEB-72 lesson, see app.js).
// PII-safe: only a coarse count travels — never an address, name or entry id.
//   orem-direcciones-ver      → address_book_view    (card shown; saved_count)
//   orem-direcciones-cambiar  → address_book_change  (opened the saved-address list)
//   orem-direcciones-elegir   → address_book_select  (picked a saved address)
//   orem-direcciones-editar   → address_book_edit    (edit a saved address, native form)
//   orem-direcciones-capturar → address_book_new     ("Agregar una nueva dirección", in the modal)
//   orem-direcciones-eliminar → address_book_delete  (4.1.10: deleted a saved address)
//   orem-direcciones-facturacion → address_book_billing (4.1.10: billing picked from the modal)
//   orem-direcciones-guardar     → address_book_save    (4.1.11: saved/edited an address in the modal)
(function attachAddressBookEvents() {
    if (typeof pixelManager === 'undefined') return;
    const MAP = {
        ver: 'address_book_view',
        cambiar: 'address_book_change',
        elegir: 'address_book_select',
        editar: 'address_book_edit',
        capturar: 'address_book_new',
        eliminar: 'address_book_delete',
        facturacion: 'address_book_billing',
        guardar: 'address_book_save',
    };
    // 4.1.11: besides the event name, the "which one" travels in event_action (and the saved
    // count in event_label for the view) -- the SAME mirror dims menu_click uses -- so every
    // event is reportable from day one without registering new custom dimensions in GA4.
    //   change/edit/new/delete -> event_action = envio | facturacion (which list was open)
    //   save                   -> event_action = nueva | edicion  (+ "_otro_pais" when saved for the other country)
    //   billing                -> event_action = igual_envio | auto_regalo | elegida
    function actionFor(key, d) {
        if (key === 'cambiar' || key === 'editar' || key === 'capturar' || key === 'eliminar') return d.modo ? String(d.modo) : '';
        if (key === 'guardar') return (d.tipo ? String(d.tipo) : 'nueva') + (d.otro ? '_otro_pais' : '');
        if (key === 'facturacion') return d.same ? 'igual_envio' : (d.auto ? 'auto_regalo' : 'elegida');
        return '';
    }
    let viewSent = false; // the block re-renders on every store change; count the view once per page load
    Object.keys(MAP).forEach(function (key) {
        document.body.addEventListener('orem-direcciones-' + key, function (ev) {
            const d = (ev && ev.detail) || {};
            if (key === 'ver') {
                if (viewSent) return;
                viewSent = true;
            }
            const params = { plugin: 'orem-add-ons' };
            if (key === 'ver') {
                params.saved_count = Number(d.n) || 0;
                params.event_label = String(params.saved_count);
            }
            const action = actionFor(key, d);
            if (action) params.event_action = action;
            try { pixelManager.sendCustom(MAP[key], params); } catch (e) {}
            if (typeof window.clarity === 'function') {
                try { window.clarity('event', 'orem_chkt_' + MAP[key]); } catch (e) {}
            }
        });
    });
})();

// Initialize after DOM ready. Same load-already-fired guard pattern
// used elsewhere (WP Rocket Delay JS may release this script post-load).
(function initBlocksCheckoutTracker() {
    const init = function () {
        try { new BlocksCheckoutEventTracker(); } catch (e) { /* never break checkout */ }
    };
    if (document.readyState === 'complete') {
        init();
    } else {
        window.addEventListener('load', init);
    }
})();

;
// ───── forms.js ─────
class OremEventTracker {
    constructor() {
        // Bug fix (2026-05-03): wrapping init in window.addEventListener('load')
        // breaks under WP Rocket Delay JS. The script may load AFTER the load
        // event has already fired (because Delay JS waits for first user
        // interaction), in which case the listener is registered too late and
        // its callback never runs. Result: no submit_success listener, so
        // monitorElementorFormsOptInForms() never sees Elementor form submits
        // and setMarketingData() never fires for newly-identified visitors —
        // breaking the localStorage page_view buffer flush to Brevo.
        //
        // Fix: if document is already 'complete' (load fired), init synchronously.
        // Otherwise register the listener as before.
        const init = () => {
            this.$jq = typeof jQuery !== 'undefined' ? jQuery.noConflict() : undefined;
            this.monitorRuleta();
            this.monitorElementorFormsOptInForms();
            this.monitorSearchSuggestions();
            this.checkSearchResults();
            // 4.0.79 — a debounced search still in flight when the visitor
            // clicks a suggestion would otherwise be lost on navigation.
            // pagehide (not unload) so bfcache isn't disabled.
            window.addEventListener('pagehide', () => this.flushSearch());
            // ACTrackingCode() removed — ActiveCampaign was deprecated when
            // we migrated CRM to Brevo. The diffuser.js / prism.app-us1.com /
            // trackcmp.net loads were dead weight (no active campaigns or
            // popups configured in AC).
            this.initClarity();
        };
        if (document.readyState === 'complete') {
            init();
        } else {
            window.addEventListener('load', init);
        }
    }



    enable = {
        clarity: true,
        //trackLution: true,
    }

    // 4.0.79 — search event hygiene. 800ms is long enough to cover the gap
    // between autocomplete responses while typing and short enough that a
    // user who types and immediately hits enter still gets their event before
    // the page navigates. 30s dedupe collapses the autocomplete event and the
    // results-page event for the same query without suppressing a genuine
    // repeat search later in the session.
    SEARCH_DEBOUNCE_MS = 800;
    SEARCH_DEDUPE_MS = 30000;
    SEARCH_LEDGER_KEY = 'orem_search_seen';


    initClarity () {
        var email = oremUserData.getMarketingEmail();
        clarity('consent', true)
        if (email != null) window.clarity("identify", email);
    }

    //Monitor Commercekit ajax search suggestions
    monitorSearchSuggestions() {
        window.addEventListener("commercekit_search_results", (evt) => {
            // 4.0.79 — CommerceKit emits this event on every autocomplete
            // response, i.e. once per keystroke burst. Log-50: 14 `search`
            // events for what were really 6 searches — "Cal" then
            // "Calendario ", "Age"→"Agend"→"Agenda"→"Agendas". Every prefix
            // was counted as its own search and shipped its own event, which
            // both inflates the event count and buries the real query in
            // GA4's search_terms report under its own prefixes.
            //
            // Debounced instead of throttled: we want the LAST term the user
            // settled on, not the first one they typed.
            this.queueSearch(evt.detail.searchValue, evt.detail.suggestions);
        });
    }

    /**
     * Debounce + prefix-collapse for search terms (4.0.79).
     *
     * Holds a term for SEARCH_DEBOUNCE_MS and only dispatches once typing
     * settles. If a newer term arrives that is a continuation of the pending
     * one (or the pending one is a continuation of it — backspacing), the
     * pending term is replaced rather than emitted, so a single query
     * produces a single event. Unrelated terms flush the pending one first,
     * because that was a genuinely separate search.
     *
     * Also de-dupes the same term inside SEARCH_DEDUPE_MS via sessionStorage,
     * which is what stops the autocomplete event and the `?s=` results-page
     * event (checkSearchResults, a separate pageload) from both counting the
     * one query the user actually ran. The window is short on purpose: the
     * same term searched again later in the session is real behaviour and
     * still gets its own event.
     */
    queueSearch(searchTerm, suggestions) {
        const term = String(searchTerm == null ? '' : searchTerm).trim();
        if (term === '') return;
        if (this.searchWasSeen(term)) return;

        const pending = this._pendingSearch;
        if (pending) {
            const a = pending.term.toLowerCase();
            const b = term.toLowerCase();
            const related = a.indexOf(b) === 0 || b.indexOf(a) === 0;
            clearTimeout(this._searchTimer);
            // A genuinely different query — the previous one was real, emit it.
            if (!related) this.flushSearch();
        }

        this._pendingSearch = { term: term, suggestions: suggestions };
        this._searchTimer = setTimeout(() => this.flushSearch(), this.SEARCH_DEBOUNCE_MS);
    }

    flushSearch() {
        clearTimeout(this._searchTimer);
        const pending = this._pendingSearch;
        this._pendingSearch = null;
        if (!pending) return;
        if (this.searchWasSeen(pending.term)) return;
        this.markSearchSeen(pending.term);
        this.trackSearch(pending.term, pending.suggestions);
    }

    // sessionStorage-backed recent-term ledger. Wrapped because Safari
    // private mode throws on access; a throw must never lose the event, so
    // both helpers fail open (nothing seen / nothing recorded → we dispatch).
    readSearchLedger() {
        try {
            const raw = window.sessionStorage.getItem(this.SEARCH_LEDGER_KEY);
            const obj = raw ? JSON.parse(raw) : {};
            return (obj && typeof obj === 'object') ? obj : {};
        } catch (e) { return {}; }
    }

    searchWasSeen(term) {
        const ledger = this.readSearchLedger();
        const ts = ledger[term.toLowerCase()];
        return !!ts && (Date.now() - ts) < this.SEARCH_DEDUPE_MS;
    }

    markSearchSeen(term) {
        try {
            const ledger = this.readSearchLedger();
            const now = Date.now();
            Object.keys(ledger).forEach((k) => {
                if (now - ledger[k] >= this.SEARCH_DEDUPE_MS) delete ledger[k];
            });
            ledger[term.toLowerCase()] = now;
            const keys = Object.keys(ledger);
            if (keys.length > 20) {
                keys.sort((a, b) => ledger[a] - ledger[b])
                    .slice(0, keys.length - 20)
                    .forEach((k) => delete ledger[k]);
            }
            window.sessionStorage.setItem(this.SEARCH_LEDGER_KEY, JSON.stringify(ledger));
        } catch (e) { /* never break tracking on storage */ }
    }

    checkSearchResults() {
        try {
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            let searchTerm = null;
            let eventUrl = null;
            if (urlParams.has('cgkit_search_word')) {
                searchTerm = urlParams.get('cgkit_search_word');
                eventUrl = document.title;
            } else {
                if (urlParams.has('s')) searchTerm = urlParams.get('s');
                eventUrl = 'Ver todos los resultados';
            }

            // 4.0.79 — a committed search (the results page loaded), so it
            // fires immediately rather than debounced; it still goes through
            // the ledger so it doesn't double-count the autocomplete event
            // the same query already produced on the previous page.
            if (searchTerm) {
                const term = String(searchTerm).trim();
                if (term !== '' && !this.searchWasSeen(term)) {
                    this.markSearchSeen(term);
                    this.trackSearch(term, null);
                }
            }

        } catch (e) {
            console.error(e);
        }
    }

    monitorRuleta() {
        this.$jq(document).on("wof:play", (event, data) => {
            let leadData = new LeadData();

            if (data && data.email && data.email != '') leadData.email = data.email;
            if (document.querySelector("input[data-wof-field='phone']")) leadData.phone = document.querySelector("input[data-wof-field='phone']").value;
            if (document.querySelector("input[data-wof-field='first_name']")) leadData.firstName = document.querySelector("input[data-wof-field='first_name']").value;
            if (document.querySelector("input[data-wof-field='last_name']")) leadData.lastName = document.querySelector("input[data-wof-field='last_name']").value;

            this.trackLead('wheel_submit', leadData);
        });
    }

    monitorElementorFormsOptInForms() {
        // 4.0.76 (WEB-28) — 2nd arg: Elementor Pro triggers this as
        // `trigger("submit_success", response.data)`, so `response` here is
        // `{message, data:{...}}` — the `data` bag that any server-side hook
        // filled via `$ajax_handler->add_response_data()`. Core doesn't read
        // it; it just forwards it to the filter, and addons decides.
        this.$jq(document).on('submit_success', (e, response) => {

            logger.log('monitorElementorFormsOptInForms', 'submit: ' + e.target.name);
            let leadData = new LeadData();
            let email = oremUserData.getMarketingEmail();

            // 4.1.6 — the form that was actually submitted, not every form
            // sharing its name: the home renders TWO "Web-registro-tribu"
            // forms, and a name lookup read the first one's (empty) fields
            // when the second was used — Lead without email, no ledger key,
            // no Brevo re-subscription.
            const form = this.$jq(e.target);
            if (!email) {
                // 4.0.78 — mismo arreglo de mayusculas que abajo: este respaldo
                // tampoco encontraba el campo en las paginas con `form-field-email`.
                email = this.findFormEmail(form);
            }


            leadData.email = email;
            if (form.find('#form-field-PHONE')) leadData.phone = form.find("#form-field-PHONE").val();
            if (form.find("#form-field-FIRSTNAME")) leadData.firstName = form.find("#form-field-FIRSTNAME").val();
            if (form.find("#form-field-LASTNAME")) leadData.lastName = form.find("#form-field-LASTNAME").val();

            // 4.0.76 (WEB-28) — the email TYPED IN THIS FORM, which is NOT
            // necessarily leadData.email: the block above prefers the session
            // email (mkt_* cookies, 360-day TTL) and only falls back to the
            // field. That preference is fine for match quality, but it is the
            // WRONG key for the "already counted" ledger — a logged-in customer
            // signing up with a different address would be judged by her
            // account address, and once any lead fired in a browser the ledger
            // key would never change again, silently suppressing every later
            // form. The ledger must judge the person who just typed. Server
            // side already reads form_fields, so this also keeps both guards
            // talking about the same human.
            // 4.0.78 (WEB-28, 3a pasada) — NO buscar por `#form-field-EMAIL`.
            // El id lo elige quien arma el formulario en Elementor, y en este
            // sitio conviven las dos grafias. Medido en el HTML real
            // (2026-09-05): la home trae 3 `form-field-EMAIL` y 3
            // `form-field-email`, y la landing del Kit CEO -- de las que mas
            // captan -- trae CUATRO en minuscula y NINGUNA en mayuscula. Ahi el
            // selector viejo no encontraba nada, `formEmail` quedaba vacio, y
            // en silencio: (a) el registro nunca deduplicaba ese formulario, o
            // sea seguia contando doble justo donde mas leads entran, y (b) la
            // re-suscripcion a n8n tampoco se mandaba. Lo (b) es anterior a
            // WEB-28; lo (a) lo introdujo WEB-28 al reusar el selector.
            // `type="email"` es la señal estable: Elementor renderiza asi todo
            // campo de correo, con cualquier id. Verificado en los dos sitios.
            const formEmail = this.findFormEmail(form);

            // Llave ESTABLE del formulario para el registro. NO se usa
            // `e.target.name`: el nombre no es unico entre formularios
            // publicados distintos -- medido en la base, "New Form" (el nombre
            // por defecto de Elementor) lo comparten `Contactanos` y `Registro
            // de distribuidores`, y "LandingAgendas2025" lo comparten tres
            // paginas. Con el nombre como llave, mandar Contactanos suprimia el
            // registro de distribuidoras del mismo correo durante un año --
            // exactamente el "5 forms = 5 leads" que el dueño pidio que NO
            // pasara. El `form_id` que Elementor pone como campo oculto SI es
            // unico por formulario, y es el mismo que el servidor ya escribe en
            // su log (`form_id=3048a830`). `formName` se conserva intacto para
            // `lead_source`: esa es la dimension del tablero y no debe moverse.
            const formKey = form.find('input[name="form_id"]').val() || e.target.name;

            this.trackLead(e.target.name, leadData, {
                response: response,
                formEmail: formEmail,
                formKey: formKey,
            });

            // Brevo re-subscription — always use form input, not session email
            if (formEmail) {
                fetch('https://n8n.orem.com.mx/webhook/brevo-resubscribe', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: formEmail, source: 'elementor' })
                }).catch(err => logger.log('brevoResubscribe', 'error: ' + err));
            }
            


        });
    }



    /**
     * Tracks a lead event
     * @param {string} formName - The submitted form name, support Active campaign and Elementor forms.
     * @param {Object} leadData - The lead data to be tracked (e.g., name, email).
     * @param {Object} [meta]   - 4.0.76 (WEB-28): opaque per-source context handed
     *                            to the lead filters. Core never inspects it
     *                            (see applyOremFilter / applyOremValueFilter in
     *                            app.js) — addons reads what it put there.
     */
    trackLead(formName, leadData, meta) {
        logger.log('trackLead', 'formName: ' + formName);

        // 4.0.76 (WEB-28) — the ONE reason not to count this: the same person
        // already submitted THIS SAME form inside the window. Owner's rule
        // (2026-09-05): a person filling five different forms is five leads,
        // because the number measures each form's capture performance.
        //
        // Whether that person was already a Brevo subscriber does NOT suppress
        // anything — it only labels the event (`lead_check`, added by addons in
        // the params filter below), so the dashboard can split fresh capture
        // from re-engagement without losing either.
        //
        // Suppressing skips only the GA4 event; setMarketingData still runs,
        // because identifying the visitor for tracking has nothing to do with
        // whether this submission counts as a lead.
        //
        // Same class of thing as maybeSendPurchase()'s `order_sent_<id>` guard.
        // The filter stays as the extension point (see applyOremFilter in
        // app.js): with nothing registered it just returns the local verdict.
        // Ledger key: the address typed in THIS form when we have it (see the
        // note in monitorElementorFormsOptInForms), else whatever the caller
        // resolved. A form with no email field yields no key and no dedupe —
        // which is right: a contact form must not burn a ledger slot that would
        // suppress a genuine subscription later.
        const ledgerEmail = (meta && meta.formEmail) ? meta.formEmail : (leadData && leadData.email);
        // 4.0.78: llave estable (form_id) cuando el llamador la trae; si no, el
        // nombre, que es lo unico que tienen la ruleta y el camino de AC.
        const ledgerKey = (meta && meta.formKey) ? meta.formKey : formName;
        const alreadyCounted = this.leadAlreadyCounted(ledgerEmail, ledgerKey);
        const suppressed = applyOremFilter('track/lead/should_block', alreadyCounted, {
            formName: formName,
            leadData: leadData,
            meta: meta || {},
        });

        if (suppressed) {
            logger.log('trackLead', 'suppressed (this person already submitted this form): ' + formName);
            oremUserData.setMarketingData(leadData.email, leadData.phone, leadData.firstName, leadData.lastName);
            return;
        }

        this.rememberLeadCounted(ledgerEmail, ledgerKey);

        //GA4
        // 4.0.74 — content_type distingue leads capturados en un post de
        // blog (single-post) de los del resto del sitio (footer, popups),
        // para el tablero SEO ("Captura email (blog)", hoy hardcodeado a
        // null porque nunca se instrumentó).
        // 4.0.76 (WEB-28): `lead_check` (new|existing|unknown) lo añade addons
        // vía el filtro de params. NO suprime nada — es la etiqueta que permite
        // separar captación nueva de re-interacción en el tablero, y medir la
        // tasa de fallo-abierto (`unknown` = no se pudo comprobar contra Brevo,
        // que sin etiqueta se vería idéntico a una captación nueva).
        pixelManager.sendEvent(EventTypes.LEAD, applyOremValueFilter('track/lead/params', {
            'lead_source': formName,
            'content_type': document.body.classList.contains('single-post') ? 'blog' : 'other',
        }, { formName: formName, leadData: leadData, meta: meta || {} }));

        //GAads
        //if (this.enable.gAds && typeof gtag === 'function')
        //gtag('event', 'conversion', { 'send_to': 'AW-691601578/bq3mCJnM6uMZEKqB5MkC' });


        //Clarity
        if (this.enable.clarity && typeof window.clarity == 'function')
            window.clarity("event", "lead_submit")

        //TrackLution
        /*
        if (this.enable.trackLution && typeof tlq === 'function')
            tlq('track', 'Lead', { target: oremSessionVars.tracklutionId });
        */

        oremUserData.setMarketingData(leadData.email, leadData.phone, leadData.firstName, leadData.lastName);


    }

    trackSearch(searchTerm, suggestions) {

        let suggestionsLength = 0;
        if (suggestions) suggestionsLength = suggestions.length;

        //GA4
        // 4.0.79 — trim at the wire too. Log-50 carried "Calendario " with a
        // trailing space, which GA4 reports as a distinct search_term from
        // "Calendario". Callers already trim; this is the belt for any future
        // caller that doesn't.
        pixelManager.sendEvent(EventTypes.SEARCH, {
            'search_term': String(searchTerm == null ? '' : searchTerm).trim(),
        });

        //TrackLution
        /*
        if (this.enable.trackLution && typeof tlq === 'function')
            tlq('track', 'Search', { target: oremSessionVars.tracklutionId });
        */
    }

    trackContact() {

        //GA4
        pixelManager.sendEvent(EventTypes.CONTACT, {});

        //Clarity
        if (this.enable.clarity && typeof window.clarity == 'function')
            window.clarity("event", "wa_clicked");


        //TrackLution
        /*
        if (this.enable.trackLution && typeof tlq === 'function')
            tlq('track', 'Contact', {
                target: oremSessionVars.tracklutionId,
            });
        */
    }

    addToCart(json_product_data) {
        //GA4
        pixelManager.sendEvent(EventTypes.ADD_TO_CART, json_product_data);

        //Clarity
        if (this.enable.clarity && typeof window.clarity == 'function') window.clarity("event", "add_to_cart");

    }

    // 4.0.76 (WEB-28) — local "already counted this person ON THIS FORM" ledger.
    //
    // Keyed by EMAIL **+ FORM**, per the owner's explicit rule (2026-09-05):
    // one person filling five DIFFERENT forms is five leads and that's correct
    // — the count measures each form's capture performance, so deduping by
    // email alone would zero out every form after the first. What must never
    // count twice is the same person re-submitting the SAME form.
    //
    // Window: 1 year (the owner's stated minimum). Long enough that a form
    // re-submitted weeks later still doesn't double-count; short enough that
    // an annual capture surface can legitimately count the same person again
    // next cycle.
    //
    // localStorage, not a cookie: it never rides along on every request, and
    // this site has a documented history of a PHP-set cookie poisoning the
    // Kinsta page cache (see the core CLAUDE.md — it's why _fbp/_ttp are
    // written from JS). Same store the page_view buffer and order_sent_* use,
    // and unlike a cookie it has no expiry of its own — the 1-year window is
    // enforced by the stored timestamp below.
    // 4.0.78 (WEB-28) — el correo tecleado en ESTE formulario, sin depender de
    // como este escrito el id del campo. Ver el comentario largo en
    // monitorElementorFormsOptInForms.
    findFormEmail(form) {
        let v = form.find('input[type="email"]').first().val();
        if (v) return v;
        // Respaldos por id, por si algun formulario usa un campo de texto suelto.
        v = form.find('[id$="form-field-EMAIL"], [id$="form-field-email"]').first().val();
        // 4.1.6 — no page-wide fallback: it returned whatever email input
        // came first on the page (another form's), which is exactly the
        // "form with no email field yields no key" case trackLead documents.
        return v || '';
    }

    leadLedgerKey() { return 'orem_lead_counted'; }
    leadLedgerMaxEntries() { return 200; }

    // Window in ms, or 0 = the whole local guard is OFF. Server-controlled
    // (`orem_pixel_vars.leadDedupeDays`, WP option `orem_lead_dedupe_days`,
    // filter `orem_core/track/lead/dedupe_days`) so this half of the mechanism
    // can be turned off WITHOUT a deploy — the same way the Brevo-side door
    // modes can. Falls back to the 1-year default if the var is missing (an
    // older cached bundle, or the localize failing), never to "off": silently
    // disabling dedupe is the recoverable error, silently suppressing is not.
    leadLedgerWindowMs() {
        let days = 365;
        try {
            const v = (typeof orem_pixel_vars !== 'undefined') ? orem_pixel_vars.leadDedupeDays : undefined;
            if (v !== undefined && v !== null && v !== '' && !isNaN(Number(v))) days = Number(v);
        } catch (e) { /* keep the default */ }
        return days > 0 ? days * 24 * 60 * 60 * 1000 : 0;
    }

    // Non-crypto hash on purpose: this is a localStorage key, not a security
    // boundary, and sha256 here is async — which would force trackLead to
    // become async and reorder the setMarketingData cookie write that the
    // dispatch depends on. djb2 over "<email>|<form>".
    hashEmailForLedger(email, formName) {
        const mail = String(email || '').trim().toLowerCase();
        if (!mail) return '';
        const s = mail + '|' + String(formName || '').trim().toLowerCase();
        let h = 5381;
        for (let i = 0; i < s.length; i++) h = (((h << 5) + h) ^ s.charCodeAt(i)) >>> 0;
        return h.toString(36);
    }

    readLeadLedger() {
        try {
            const raw = window.localStorage.getItem(this.leadLedgerKey());
            const parsed = raw ? JSON.parse(raw) : {};
            return (parsed && typeof parsed === 'object') ? parsed : {};
        } catch (e) {
            return {}; // corrupt/blocked storage must never suppress tracking
        }
    }

    leadAlreadyCounted(email, formName) {
        const windowMs = this.leadLedgerWindowMs();
        if (!windowMs) return false; // guard switched off server-side
        const key = this.hashEmailForLedger(email, formName);
        if (!key) return false; // no email → nothing to dedupe on, behave as before
        try {
            const entry = this.readLeadLedger()[key];
            if (!entry || !entry.t) return false;
            // Past the window, treat it as a fresh capture rather than
            // suppressing forever on a stale local record.
            return (Date.now() - entry.t) < this.leadLedgerWindowMs();
        } catch (e) {
            return false;
        }
    }

    rememberLeadCounted(email, formName) {
        const key = this.hashEmailForLedger(email, formName);
        if (!key) return;
        try {
            const ledger = this.readLeadLedger();
            ledger[key] = { t: Date.now(), f: String(formName || '').slice(0, 40) };
            // Bounded: keep the N most recent so this can't grow forever on a
            // shared/kiosk browser. Roomy on purpose — one person legitimately
            // occupies one slot PER FORM now, and the window is a year.
            const keys = Object.keys(ledger);
            const max = this.leadLedgerMaxEntries();
            if (keys.length > max) {
                keys.sort((a, b) => (ledger[b].t || 0) - (ledger[a].t || 0))
                    .slice(max)
                    .forEach(k => delete ledger[k]);
            }
            window.localStorage.setItem(this.leadLedgerKey(), JSON.stringify(ledger));
        } catch (e) { /* never break tracking on storage */ }
    }

    maybeSendPurchase(orderId, json_purchase_data) {
        const key = 'order_sent_' + orderId;
        const orderSent = localStorage.getItem(key);

        if (orderSent != "true") {
            localStorage.setItem(key, true);
            //send purchase event via gtag pixel, the server event will be triggered by the server
            pixelManager.sendEvent(EventTypes.PURCHASE, json_purchase_data, orderId);
        }

    }



}

class LeadData {
    constructor() {
        this.email = '';
        this.phone = '';
        this.firstName = '';
        this.lastName = '';
    }
}


// Example Usage
const tracker = new OremEventTracker();
